/* eslint-disable max-lines */
import Session from "supertokens-auth-react/recipe/session";
// Auth types
import type { ChartGranularity } from "@/pages/sku/constants";
import {
  VerifyFreeUserResponseSchema,
  type VerifyFreeUserResponse,
  type ResetPasswordRequest,
  type RegisterInviteRequest,
  type RegisterSSORequest,
  type LoginRequest,
  type ForgotPasswordRequest,
} from "@/pages/authentication/types";
// SKU Cost types
import {
  CostBreakdownResponse as SkuCostBreakdownResponse,
  CostBreakdownResponseSchema as SkuCostBreakdownResponseSchema,
  SkuBreakdownParams,
} from "@/pages/sku/types";
// Clusters types (Databricks)
import {
  ClustersParams,
  ClustersResponse,
  ClustersResponseSchema,
  ClusterDetail,
  ClusterDetailSchema,
  ClusterDetailParams,
  ClusterTimeseriesParams,
  ClusterUtilizationResponse,
  ClusterUtilizationResponseSchema,
  ClusterCostResponse,
  ClusterCostResponseSchema,
  ClustersCostTrendsParams,
  ClustersCostTrendsResponse,
  ClustersCostTrendsResponseSchema,
} from "@/pages/clusters/types";
// Jobs types (Databricks)
import {
  JobRunTrendsResponse,
  JobRunTrendsResponseSchema,
  JobTasksInventoryResponse,
  JobTasksInventoryResponseSchema,
  JobRunSparkUiResponse,
  JobRunSparkUiResponseSchema,
  JobDetailResponse,
  JobDetailResponseSchema,
  DbxAutoRecommendationsResponse,
  DbxAutoRecommendationsResponseSchema,
  ToggleAutoTuneResponse,
  ToggleAutoTuneResponseSchema,
} from "@/pages/jobs/types";
// Jobs Auto Tune backoff/notification config — a job-specific backoff
// policy shape (latency threshold, not the cluster/warehouse cold-start
// rate), reusing only the shared notification config.
import {
  JobAutoTuneConfigResponse,
  JobAutoTuneConfigResponseSchema,
  JobAutoTuneConfigRequest,
} from "@/pages/jobs/auto-tune/configTypes";
// Cluster Auto Tune types (Databricks all-purpose clusters)
import {
  AutoTuneStatusResponse,
  AutoTuneStatusResponseSchema,
  AutoTuneHistoryResponse,
  AutoTuneHistoryResponseSchema,
  AutoTuneSavingsTrendsResponse,
  AutoTuneSavingsTrendsResponseSchema,
  AutoTuneSavingsTrendPoint,
  ClusterAutoTuneRecommendationsResponse,
  ClusterAutoTuneRecommendationsResponseSchema,
  ClusterAutoTuneToggleResponse,
  ClusterAutoTuneToggleResponseSchema,
  ClusterAutoTuneConfigResponse,
  ClusterAutoTuneConfigResponseSchema,
  ClusterAutoTuneConfigRequest,
  ClusterAutoTuneToggleEventsResponse,
  ClusterAutoTuneToggleEventsResponseSchema,
} from "@/pages/clusters/auto-tune/types";
// Workspaces Cost types
import {
  CostTotalResponse,
  CostTotalResponseSchema,
  CostBreakdownResponse as WorkspacesCostBreakdownResponse,
  CostBreakdownResponseSchema as WorkspacesCostBreakdownResponseSchema,
  ProductTrendsResponse,
  ProductTrendsResponseSchema,
  WorkspacesResponse,
  WorkspacesResponseSchema,
  WorkspaceEfficiencyResponse,
  WorkspaceEfficiencyResponseSchema,
  WorkspacesCostParams,
  WorkspacesTrendsParams,
  WorkspacesBreakdownParams,
} from "@/pages/workspaces/types";
// Databricks page types
import {
  type QuerySqlLineageResponse,
  QuerySqlLineageResponseSchema,
  type ServicePrincipalConfig,
  ServicePrincipalConfigSchema,
} from "@/pages/databricks/types";
// Databricks Summary page types
import {
  type SummarySavingsOverallResponse,
  SummarySavingsOverallResponseSchema,
  type SummarySavingsOverallEstimateResponse,
  SummarySavingsOverallEstimateResponseSchema,
  type SummarySavingsTimeseriesResponse,
  SummarySavingsTimeseriesResponseSchema,
  type SummarySavingsAssistedResponse,
  SummarySavingsAssistedResponseSchema,
  type SummaryCostParams,
  type SummarySavingsParams,
} from "@/pages/databricks/summary/types";
// Subscription types - imported at top to avoid mid-file imports
import type {
  SubscriptionListResponse,
  SubscriptionV2Summary,
  SubscriptionHistoryResponse,
  BackendSubscriptionCreateRequest,
  SubscriptionResponse,
  SubscriptionTemplatesListResponse,
  SubscriptionDeleteResponse,
  MetricFilterValuesResponse,
  MetricsCatalogResponse,
  NotificationTestRequest,
  NotificationTestResponse,
} from "@/pages/subscriptions/services/types";
import type { RuleVisibility } from "@/pages/subscriptions/constants";
import {
  SubscriptionListResponseSchema,
  SubscriptionV2SummarySchema,
  SubscriptionHistoryResponseSchema,
  SubscriptionResponseSchema,
  SubscriptionTemplatesListResponseSchema,
  MetricsCatalogResponseSchema,
  SubscriptionDeleteResponseSchema,
  MetricFilterValuesResponseSchema,
  NotificationTestResponseSchema,
  type PlaygroundReportCreateRequest,
  type PlaygroundReportUpdateRequest,
  PlaygroundReportTestResponseSchema,
  type PlaygroundReportTestResponse,
  type PlaygroundReportPreviewRequest,
  PlaygroundReportRunSchema,
  type PlaygroundReportRun,
  PlaygroundReportRunStatusSchema,
  type PlaygroundReportRunStatus,
  type PlaygroundReportSendPreviewRequest,
} from "@/pages/subscriptions/services/types";
// Databricks Connection types
import {
  type DatabricksConnectionCreateRequest,
  type DatabricksConnectionResponse,
  DatabricksConnectionResponseSchema,
  type DatabricksConnectionTestRequest,
  type DatabricksConnectionTestResponse,
  DatabricksConnectionTestResponseSchema,
  type DatabricksWorkspacesResponse,
  DatabricksWorkspacesResponseSchema,
  type DatabricksSyncHistoryParams,
  type DatabricksSyncHistoryResponse,
  DatabricksSyncHistoryResponseSchema,
} from "@/pages/datastores/databricksTypes";
import type { SubscriptionFiltersResponse } from "@/pages/subscriptions/components/list/types";
import {
  SubscriptionFiltersResponseSchema,
  type SubscriptionListFilters,
  type SubscriptionSummaryFilters,
} from "@/pages/subscriptions/components/list/types";
import type {
  SubscriptionPreviewParams,
  SubscriptionPreviewResponse,
  MetricFilterOptionsResponse,
} from "@/pages/subscriptions/components/wizard/types";
// AI Services types
import {
  aiServicesSummaryResponseSchema,
  aiServiceGraphResponseSchema,
  aiServiceUsageResponseSchema,
  aiServiceBreakdownResponseSchema,
  aiServiceFilterValuesResponseSchema,
  type AIServicesSummaryResponse,
  type AIServicesSummaryParams,
  type AIServiceGraphResponse,
  type AIServiceGraphParams,
  type AIServiceUsageResponse,
  type AIServiceUsageParams,
  type AIServiceBreakdownResponse,
  type AIServiceBreakdownParams,
  type AIServiceFilterValuesResponse,
  type AIServiceFilterValuesParams,
} from "@/pages/query/ai-services/types";
import {
  conversationsResponseSchema,
  traceResponseSchema,
  traceQueryResponseSchema,
  type ConversationsResponse,
  type ConversationsParams,
  type TraceResponse,
  type ConversationTraceParams,
  type TraceQueryResponse,
  type TraceQueryParams,
} from "@/pages/query/ai-conversations/types";
import {
  cacheOverviewResponseSchema,
  type CacheOverviewParams,
  type CacheOverviewResponse,
} from "@/pages/query/ai-caching/types";
import {
  SubscriptionPreviewResponseSchema,
  MetricFilterOptionsResponseSchema,
} from "@/pages/subscriptions/components/wizard/types";

import { AggregatedAiServicesEstimateGraphResponseSchema } from "@/pages/summary/context/schemas";
import {
  AggregatedComputeGraphResponse,
  AggregatedComputeGraphResponseSchema,
  AggregatedServerlessGraphResponse,
  AggregatedServerlessGraphResponseSchema,
  AggregatedStorageGraphResponse,
  AggregatedStorageGraphResponseSchema,
  AggregatedCloudServicesGraphResponse,
  AggregatedCloudServicesGraphResponseSchema,
  AggregatedAiServicesGraphResponse,
  AggregatedAiServicesGraphResponseSchema,
  AggregatedAiServicesEstimateGraphResponse,
  AssistedSavingsResponse,
  AssistedSavingsResponseSchema,
  AutoTuneSavingsSummaryResponse,
  AutoTuneSavingsSummaryResponseSchema,
  CostAnalysisSubscriptionsListResponse,
  CostAnalysisUnsubscribeResponse,
  costAnalysisSubscriptionsListResponseSchema,
  costAnalysisUnsubscribeResponseSchema,
  GetSummaryOpportunitiesDataResponse,
  GetSummaryOpportunitiesDataResponseSchema,
  OpportunitiesSummary,
  OverallCostSummaryResponse,
  OverallCostSummaryResponseSchema,
  OverallSavingsEstimateResponse,
  OverallSavingsEstimateResponseSchema,
  OverallSavingsResponse,
  OverallSavingsResponseSchema,
  SummaryCostGraphRequest,
  TotalCosts,
  TotalCostsSchema,
} from "../pages/summary/context/types";
import {
  GetExportLineageItem,
  GetExportLineageItemSchema,
  GetExportLineagePageSchema,
} from "../pages/exportLineage/types";
import {
  FilterResponse,
  FilterResponseSchema,
  GetOpportunitiesDataResponse,
  GetOpportunitiesDataResponseSchema,
  GetImplementedOpportunitiesDataResponse,
  GetImplementedOpportunitiesDataResponseSchema,
  GetOpportunitiesRedirectionRequest,
  GetOpportunitiesRedirectResponse,
  GetOpportunitiesRedirectResponseSchema,
  GetOpportunitiesResourceNameFilterRequest,
  GetOpportunitiesResourceRequest,
  GetOpportunitiesResourceResponse,
  GetOpportunitiesResourceResponseSchema,
  GetOpportunitiesSummaryRequest,
  GetOpportunitiesSummaryResponse,
  GetOpportunitiesSummaryResponseSchema,
  GetOpportunitiesUserFilterRequest,
  GetOpportunitiesWarehouseFilterRequest,
  GetOpportunityUpdatesRequest,
  GetOpportunityUpdatesResponse,
  GetOpportunityUpdatesResponseSchema,
  GetQuerySQLLineageResponse,
  GetQuerySQLLineageResponseSchema,
  GetScheduledEventUpdatesRequest,
  GetScheduledEventUpdatesResponse,
  GetScheduledEventUpdatesResponseSchema,
  GetGroupedOpportunitiesDataResponseSchema,
  GroupedOpportunitiesFilters,
  OpportunitiesFilters,
  Page,
  PriorityOpportunityResponse,
  PriorityOpportunityResponseSchema,
  UpdateOpportunitiesDataRequest,
  GetGroupedOpportunitiesDataResponse,
  OpportunityDetailResponse,
  OpportunityDetailResponseSchema,
  OpportunityComment,
  CommentSchema,
  LabelSuggestionResponse,
  LabelSuggestionResponseSchema,
  SetLabelsRequest,
  AddCommentRequest,
  UpdateCommentRequest,
} from "../pages/explorer/types";
import {
  dbtJobRunItem,
  DbtJobRunItem,
  DbtJobListingItem,
  dbtJobListingItemSchema,
  DbtJobListingSummary,
  dbtJobListingSummarySchema,
  GetDbtJobListingParams,
  GetDbtJobListingSummaryParams,
  DbtJobFilterItem,
  dbtJobFilterItemSchema,
  DbtTagFilterItem,
  dbtTagFilterItemSchema,
  DbtModelFilterItem,
  dbtModelFilterItemSchema,
  DbtModel,
  dbtModelRunSummary,
  DbtModelRunSummary,
  dbtModelSchema,
  DbtProjectAndIntegration,
  dbtProjectAndIntegrationSchema,
  DbtHealthCheckResponse,
  dbtHealthCheckResponseSchema,
  DbtTasksSummary,
  dbtTasksSummarySchema,
  GetDbtJobRunsListParams,
  GetDbtJobRunsListGraphDataParams,
  GetDbtModelRunSummaryParams,
  GetDbtTasksSummaryParams,
  GetDbtModelsListParams,
  GetSnowflakeJobGraphData,
  getSnowflakeJobGraphDataSchema,
  GetSnowflakeJobItem,
  getSnowflakeJobItemSchema,
  GetSnowflakeJobRunItem,
  getSnowflakeJobRunItemSchema,
  GetSnowflakeJobRunsGraphData,
  getSnowflakeJobRunsGraphDataSchema,
  GetSnowflakeJobSummary,
  getSnowflakeJobSummarySchema,
  GetSnowflakeJobTaskItem,
  getSnowflakeJobTaskItemSchema,
  QueryAllCSVFilters,
  QueryDetailsResponse,
  QueryDetailsResponseSchema,
  QueryGroupsFilters,
  QueryJobExecutionsFilters,
  QueryJobsFilters,
  QueryJobsResponse,
  QueryJobsResponseSchema,
  QueryTextIndicesResponse,
  QueryTextIndicesResponseSchema,
  QueryTextResponse,
  QueryTextResponseSchema,
  RunCompareDiff,
  runCompareDiffSchema,
  SnowflakeJobDefinitionResponse,
  snowflakeJobDefinitionResponseSchema,
  SnowflakeJobFilters,
  snowflakeJobFiltersSchema,
  SnowflakeJobsFilterPaginatedParams,
  SnowflakeJobsFilterStringPage,
  SnowflakeJobsFilterLabelValuePage,
  snowflakeJobsFilterStringPageSchema,
  snowflakeJobsFilterLabelValuePageSchema,
  WorkloadAttributeValues,
  workloadAttributeValuesSchema,
  WorkloadAttributes,
  workloadAttributesSchema,
  SnowflakeJobSettings,
  snowflakeJobSettingsSchema,
  StreamlitNotebookChildQueriesListResponse,
  StreamlitNotebookJobListResponse,
  CommonJobListSchema,
  StreamlitNotebookJobRunsListResponse,
  StreamlitNotebookJobRunsSummaryDataResponse,
  CommonRunsSummarySchema,
  StreamlitNotebookJobsGraphDataResponse,
  StreamlitNotebookJobsGraphDataResponseSchema,
  StreamlitNotebookChildQueriesSchema,
  StreamlitNotebookRunsListSchema,
  QueryFiltersSchema,
  QueryFilters,
  GetQueryFiltersParams,
  FilterParams,
  QueryGroup,
  QueryGroupSchema,
  QueryItemSchema,
  UpdateQueryJobNameRequest,
  UpdateQueryJobNameResponse,
  UpdateQueryJobNameResponseSchema,
  UpdateQueryGroupNameRequest,
  UpdateQueryGroupNameResponse,
  UpdateQueryGroupNameResponseSchema,
  GetSnowflakeJobsParams,
  GetSnowflakeJobRunsParams,
  GetSnowflakeJobRunsGraphDataParams,
  GetSnowflakeJobRunTasksParams,
  GetSnowflakeJobFiltersParams,
  GetSnowflakeJobDefinitionParams,
  GetQueryJobFiltersParams,
  GetQueryJobDataParams,
  GetQueryGroupDataParams,
  QueryJobFiltersResponse,
  QueryJobFiltersResponseSchema,
  QueryGraphDataResponse,
  QueryGraphDataResponseSchema,
  QueryJobExecutionsResponse,
  QueryJobExecutionsResponseSchema,
  QueryJobDefinitionsResponse,
  QueryJobDefinitionsResponseSchema,
  QueryItem,
  CommonGraphSummarySchema,
  CommonGraphSummaryResponse,
  GetSnowflakeJobGraphDataRequestParams,
  flattenCustomTagFilters,
} from "../pages/query/types";
import {
  StartingNodeDBTParams,
  GetSQLLineageParams,
  PostColumnsDBTParams,
  PostConnectedColumnsDBTParams,
  TableSchemaResponseSchema,
  TableSchemaResponse,
  ColumnLineageResponseSchema,
  ColumnLineageResponse,
  ConnectedTablesResponseSchema,
  ConnectedTablesResponse,
  TableNodeResponseSchema,
  TableNodeResponse,
  SQLLineageResponseSchema,
  SQLLineageResponse,
  IntegrationEnvironmentsResponse,
  IntegrationEnvironmentsResponseSchema,
} from "@/pages/dbtModels/types";

import { API_URL } from "@/config";
import { APIClient, axios, validateResponse } from "./api_helper";
import {
  OpportunityFinding,
  StudioOpportunityFieldError,
  studioOpportunityErrorDetailSchema,
  SubmitStudioOpportunityResponse,
  submitStudioOpportunityErrorSchema,
  submitStudioOpportunityResponseSchema,
  DiscardStudioOpportunityResponse,
  discardStudioOpportunityResponseSchema,
  ValidateStudioOpportunityResponse,
  validateStudioOpportunityResponseSchema,
} from "@/pages/playground/opportunities/types";
import {
  GetOpportunitiesDynamicFilterResponse,
  GetOpportunitiesDynamicFilterResponseSchema,
  GetTableLastRefreshedResponse,
  GetTableLastRefreshedResponseSchema,
  GetTenantInfoResponse,
  pageSchema,
  StandardResponse,
  StandardResponseSchema,
  SessionEmailResponse,
  SessionEmailResponseSchema,
  StandardResponseWithWarning,
  StandardResponseWithWarningSchema,
  TableRkFilterResponse,
  TableRkFilterResponseSchema,
  tenantInfoSchema,
  UrlResponse,
  urlResponse,
  UserLoginOutResponse,
  userLoginOutSchema,
  CompanyInfoResponse,
  CompanyInfoResponseSchema,
  CompanyListResponse,
  CompanyListResponseSchema,
  CompanyCreateResponse,
  CompanyCreateResponseSchema,
  DemoSessionResponse,
  demoSessionResponseSchema,
  EmailInstanceListResponseSchema,
  type EmailInstanceListResponse,
} from "./types";
import {
  CompanyRegisterResponseSchema,
  type CompanyRegisterRequest,
  type CompanyRegisterResponse,
} from "@/pages/settings/types";
import { downloadBlob } from "./utils";
import {
  FilterType,
  PresetIdResponseSchema,
  SavedFilter,
  savedFilterSchema,
} from "@/components/tags/types";
import { z } from "zod";
import {
  GetAllUserGroupsResponseSchema,
  UploadUserGroupResponse,
  UploadUserGroupResponseSchema,
  UploadUserResponse,
  UploadUserResponseSchema,
  UserGroupWithMembersAndRulesZodSchema,
} from "@/pages/settings/components/userGroups/types";
import {
  CreateTeamResponseSchema,
  type CreateTeamResponse,
} from "@/pages/settings/components/userGroups/createTeamResponse";
import {
  DbtModelsResponse,
  DbtModelsResponseSchema,
  DbtModelFiltersResponse,
  DbtModelFiltersResponseSchema,
  DbtModelByRkRequest,
  DbtModelByRkResponse,
  DbtModelByRkResponseSchema,
  GetDbtModelsParams,
} from "@/types/dbtModels";
import {
  PlaygroundAllSessionResponse,
  playgroundAllSessionResponseSchema,
  PlaygroundSessionDetailsResponse,
  playgroundSessionDetailsResponseSchema,
  ShareLinkResponse,
  shareLinkResponseSchema,
  ShareLinkListResponse,
  shareLinkListResponseSchema,
  SharedSessionResponse,
  sharedSessionResponseSchema,
  ShareLinkCreateRequest,
  ShareToSlackRequest,
  ShareToSlackResponse,
  shareToSlackResponseSchema,
  SessionLifecycleStatus,
  SessionLifecycleListFilter,
  SessionRenameResponse,
  sessionRenameResponseSchema,
  SessionStarResponse,
  sessionStarResponseSchema,
  SessionStatusUpdateResponse,
  sessionStatusUpdateResponseSchema,
} from "@/pages/playground/types";
import {
  DbtDocsFiltersSchema,
  DbtDocsFilters,
  GetDbtDocsShareListParams,
  DbtDocsShareListResponse,
  DbtDocsShareListResponseSchema,
  DbtDocsShareUrlResponse,
  DbtDocsShareUrlResponseSchema,
  CurrentUserResponse,
  StandardApiResponseSchema as DbtDocsStandardApiResponseSchema,
  StandardApiResponse as DbtDocsStandardApiResponse,
} from "@/pages/dbtDocs/types";
import {
  GetAllExperimentsParams,
  GetAllExperimentsResponse,
  GetAllExperimentsResponseSchema,
  ExperimentDetail,
  ExperimentDetailSchema,
  ExperimentSummaryResponse,
  ExperimentSummaryResponseSchema,
  CreateExperimentRequest,
  UpdateExperimentStatusRequest,
  SchemaMetadataResponse,
  SchemaMetadataResponseSchema,
  StandardApiResponseSchema as ExperimentStandardApiResponseSchema,
} from "@/pages/experimentation/types";
import {
  GetMaskDataResponse,
  GetMaskDataResponseSchema,
  SetMaskDataRequest,
  SetMaskDataResponse,
  SetMaskDataResponseSchema,
} from "@/pages/security/types";
import {
  DBTConfigListResponse,
  DBTConfigListResponseSchema,
  DBTConfigFiltersResponse,
  DBTConfigFiltersResponseSchema,
  DBTConfigDetail,
  DBTConfigDetailSchema,
  DBTConfigRequest,
  DBTConfigMutationResponse,
  DBTConfigMutationResponseSchema,
  DBTConfigChecksResponse,
  DBTConfigChecksResponseSchema,
  DBTConfigChecksFiltersResponse,
  DBTConfigChecksFiltersResponseSchema,
} from "@/types/dbtconfig";
import {
  IntegrationListResponse,
  IntegrationListResponseSchema,
  IntegrationDetails,
  IntegrationDetailsSchema,
  PostIntegrationRequest,
  PostIntegrationResponse,
  PostIntegrationResponseSchema,
} from "@/types/integration";
import {
  User,
  UserSchema,
  UserListResponseSchema,
  UserListResponse,
  InviteUserResponse,
  InviteUserResponseSchema,
  UpdateUserResponse,
  UpdateUserResponseSchema,
  UpdateUserRoleResponse,
  UpdateUserRoleResponseSchema,
  DeleteUserResponse,
  DeleteUserResponseSchema,
  AssetSchema,
  Asset,
  SSOUserInfoResponse,
  SSOUserInfoResponseSchema,
  BaseUserSchema,
} from "@/types";

import {
  CostBreakdown,
  CostBreakdownSchema,
  TableDataFilters,
  TableQueriesResponse,
  TableQueriesResponseSchema,
  TableQueryFilters,
  TableQueryFiltersResponse,
  TableQueryFiltersResponseSchema,
  TableDetailResponse,
  TableDetailResponseSchema,
  TableDescriptionResponse,
  TableDescriptionResponseSchema,
  UpdateTablePurposeRequest,
  TableInsightsResponse,
  TableInsightsResponseSchema,
  GetTableIdRequest,
  GetTableIdResponse,
  GetTableIdResponseSchema,
  TableStorageOverTimeResponse,
  TableStorageOverTimeResponseSchema,
  GetColumnAccessHistoryParams,
  ColumnAccessHistoryResponse,
  ColumnAccessHistoryResponseSchema,
  ColumnUserAccessHistoryFiltersResponse,
  ColumnUserAccessHistoryFiltersResponseSchema,
  GetColumnUserAccessHistoryRequest,
  ColumnUserAccessHistoryResponse,
  ColumnUserAccessHistoryResponseSchema,
  DatasetListResponse,
  DatasetListResponseSchema,
  TableFiltersResponseSchema,
} from "@/pages/datasets/types";
import {
  GetScheduleResponseSchema,
  CombinedScheduleResponse,
  GetCombinedScheduleResponseSchema,
  ScheduleApprovalRequest,
  ScheduleCreateRequest,
  ScheduleRejectionRequest,
  ScheduleResponse,
  ScheduleResponseSchema,
  ScheduleSeriesCreateRequest,
  ScheduleSeriesUpdateRequest,
  ScheduleSeriesPreviewResponse,
  ScheduleSeriesPreviewResponseSchema,
  ScheduleSeriesResponse,
  ScheduleSeriesResponseSchema,
  ScheduleSeriesSummary,
  ScheduleSeriesSummarySchema,
  ScheduleSeriesTemplate,
  ScheduleSeriesTemplateListSchema,
  ScheduleUpdateRequest,
  WarehouseCostSummarySchema,
  WarehouseCostSummaryResponse,
  WarehouseSummaryCostGraphSchema,
  WarehouseSummaryCostGraphResponse,
  WarehouseListResponse,
  WarehouseListResponseSchema,
  WarehouseFiltersResponse,
  WarehouseFiltersResponseSchema,
  GetWarehouseSummaryCostGraphParams,
  GetAllWarehousesParams,
  GetWarehouseFiltersParams,
  GetWarehouseParams,
  WarehouseDetailsSchema,
  WarehouseDetails,
  WarehouseAutoSavingsResponse,
  WarehouseAutoSavingsResponseSchema,
  GetWarehouseAutoSavingsParams,
  WarehouseAutoTuneConfiguration,
  WarehouseAutoTuneConfigurationSchema,
  SetWarehouseAutoTuneConfigurationRequest,
  WarehouseAutoTuneToggleResponse,
  WarehouseAutoTuneToggleResponseSchema,
  WarehouseDecisionDataResponse,
  WarehouseDecisionDataResponseSchema,
  GetWarehouseDecisionDataParams,
  WarehouseQueryExecutionTimesResponse,
  WarehouseQueryExecutionTimesResponseSchema,
  GetWarehouseQueryExecutionTimesParams,
  WarehouseAuditEventsResponse,
  WarehouseAuditEventsResponseSchema,
  GetWarehouseAuditEventsParams,
  GetWarehouseCostParams,
  WarehouseCostResponse,
  WarehouseCostResponseSchema,
  WarehouseClusterUtilizationResponse,
  WarehouseClusterUtilizationResponseSchema,
  WarehouseLastEventTimeResponse,
  WarehouseLastEventTimeResponseSchema,
  WarehouseMetadataResponse,
  WarehouseMetadataResponseSchema,
  WarehouseInsightsRequest,
  WarehouseInsightsResponse,
  WarehouseInsightsResponseSchema,
  ToggleWarehouseSchedulesResponse,
  ToggleWarehouseSchedulesResponseSchema,
  WarehouseAutoSavingsConfigurationResponse,
  WarehouseAutoSavingsConfigurationResponseSchema,
  GetWarehouseSchedulerEventsParams,
} from "@/pages/warehouse/types";
import {
  RolesTableFilterParams,
  UsersTableFilterParams,
  GovernanceSummary,
  GovernanceSummarySchema,
  GovernanceFilters,
  GovernanceFiltersSchema,
  GovernanceUsersResponse,
  GovernanceUsersResponseSchema,
  GovernanceRolesResponse,
  GovernanceRolesResponseSchema,
  GovernanceUserDetails,
  GovernanceUserDetailsSchema,
  GovernanceRoleDetails,
  GovernanceRoleDetailsSchema,
  GovernanceCostGraph,
  GovernanceCostGraphSchema,
  GovernanceFilterParams,
} from "@/pages/governance/types";
import {
  AccessRuleCoveragePreviewResponseSchema,
  type AccessRuleCoveragePreviewResponse,
  type OwnershipCostPreviewResponse,
  OwnershipCostPreviewResponseSchema,
  type OwnershipConflictsResponse,
  OwnershipConflictsResponseSchema,
  type ConflictingTeamsResponse,
  ConflictingTeamsResponseSchema,
  type TeamConflictsResponse,
  TeamConflictsResponseSchema,
  type TeamConflictDetailsResponse,
  TeamConflictDetailsResponseSchema,
  type OwnershipRequestPayload,
  type OwnershipRequestsData,
} from "@/pages/settings/components/userGroups/types";
import {
  ApplyRecommendedEventsRequestSchema,
  BulkApprovalResponseSchema,
} from "@/pages/warehouse/components/scheduler/types";
import { NotificationsResponseSchema } from "@/pages/settings/components/notifications/constants";
import { SSOProvider } from "./constants";
import {
  AddTagToNodeRequest,
  AssociateTagResponseSchema,
  GetTagsParams,
  Tag,
  TagSchema,
  TagsResponseSchema,
  TagTypeSchema,
} from "@/pages/settings/tags/types";
import {
  type DatastoreResponseInferred,
  DatastoreResponseSchema,
  type DatastoreTypeResponse,
  DatastoreTypeResponseSchema,
  type TestConnectionResponse,
  TestConnectionResponseSchema,
  type SyncDatastoreResponse,
  SyncDatastoreResponseSchema,
  type ToggleSyncScheduleResponse,
  ToggleSyncScheduleResponseSchema,
  type AddDatastoreRequest,
  type AddDatabasesRequest,
  type AddDatastoreResponse,
  AddDatastoreResponseSchema,
  type TestDatastoreConnectionDuringCreationRequest,
  type TestDatastoreConnectionDuringCreationResponse,
  TestDatastoreConnectionDuringCreationResponseSchema,
  AvailableDatabasesResponseSchema,
  type AvailableDatabasesResponse,
  DatastoreDetailResponseSchema,
  type DatastoreDetailResponse,
  GetSyncHistoryResponseSchema,
  type GetSyncHistoryResponse,
  type GetSyncHistoryParams,
  // dbt Cloud types
  type DbtCloudConnectionCreateRequest,
  type DbtCloudConnectionListResponse,
  DbtCloudConnectionListResponseSchema,
  type DbtCloudConnectionResponse,
  DbtCloudConnectionResponseSchema,
  type DbtCloudDiscoveryResponse,
  DbtCloudDiscoveryResponseSchema,
  type DbtCloudEnvironmentResponse,
  DbtCloudEnvironmentResponseSchema,
  type DbtCloudEnvironmentUpdateRequest,
  type DbtCloudProjectResponse,
  DbtCloudProjectResponseSchema,
  type DbtCloudProjectUpdateRequest,
  type DbtCloudScheduleResponse,
  DbtCloudScheduleResponseSchema,
  type DbtCloudScheduleUpdateRequest,
  type DbtCloudConnectionUpdateRequest,
  type DbtCloudSyncHistoryResponse,
  DbtCloudSyncHistoryResponseSchema,
  type DbtCloudSyncResponse,
  DbtCloudSyncResponseSchema,
  type DbtCloudValidateTokenRequest,
  type DbtCloudValidationResponse,
  DbtCloudValidationResponseSchema,
} from "@/pages/datastores/types";
import {
  TableauProject,
  TableauProjectSchema,
} from "@/pages/datastores/components/DatastoreAdd/DatastoreConnections/types";
import {
  type ConnectionEditForm,
  ConnectionEditFormSchema,
  type EditCapabilitiesResponse,
  EditCapabilitiesResponseSchema,
} from "@/pages/datastores/editCapability";
import {
  Alert,
  AlertSchema,
  AlertEventsResponse,
  AlertEventsResponseSchema,
  AlertRulesResponse,
  AlertRulesResponseSchema,
  SlackChannelsResponse,
  SlackChannelsResponseSchema,
  GetAlertEventsParams,
  GetAlertRulesParams,
} from "@/pages/alerts/types";
import { SnowflakeStageMetadata } from "@altimateai/ui-components/lineage";
import {
  DbtInfoResponse,
  DbtInfoResponseSchema,
  snowflakeStageMetadataSchema,
} from "@/components/lineage/types";
import {
  CommonIntegrationInstance,
  CommonIntegrationInstanceSchema,
  Connection,
  ConnectionSchema,
  DatamateIntegrationSchema,
  GetDatamateWithStatsResponse,
  GetDatamateWithStatsResponseSchema,
  Teammate,
  TeammateSchema,
  TeammateCreateResponse,
  TeammateCreateResponseSchema,
  TeammateUpdateResponse,
  TeammateUpdateResponseSchema,
  Usage,
  UsageSchema,
  AssistAggregationsResponse,
  AssistAggregationsResponseSchema,
  GuardrailAggregationsResponse,
  GuardrailAggregationsResponseSchema,
  ConnectionTestStatus,
  ConnectionTestResultSchema,
} from "@/pages/mcp/mcpSlice";
import {
  KnowledgeCatalogItem,
  KnowledgeCatalogItemSchema,
  KnowledgeDocumentUploadResponseSchema,
  KnowledgeDocumentUploadResponse,
  KnowledgeDocumentUploadToExistingResponseSchema,
  KnowledgeDocumentUploadToExistingResponse,
  KnowledgeBaseDetailResponseSchema,
  KnowledgeBaseDetailResponse,
  KnowledgeDocumentHistoryItemSchema,
  KnowledgeDocumentHistoryItem,
  DocumentChunksResponseSchema,
  DocumentChunksResponse,
} from "@/pages/knowledgeCatalog/types";
import {
  teamSpendFilters,
  TeamSpendFilters,
  teamSpendGraphAndSummary,
  TeamSpendGraphAndSummary,
  TeamSpendQueryParams,
  TeamSpendTotalSpends,
  teamSpendTotalSpendsSchema,
} from "@/types/teamSpend";
import {
  LineageResponse,
  lineageResponseSchema,
  GetColumnsRequest,
  GetColumnsResponse,
  getColumnsResponseSchema,
  TerminalNodeRequest,
  TerminalNodeResponse,
  terminalNodeResponseSchema,
  ConnectedColumnsRequest,
  ConnectedColumnsResponse,
  connectedColumnsResponseSchema,
  UpstreamDownstreamTablesResponse,
  upstreamDownstreamTablesResponseSchema,
} from "@/components/lineage/types";
import {
  BiReportFilters,
  BiReportFiltersSchema,
  BiUserReportsFilters,
  BiUserReportsFiltersSchema,
  DashboardAccessActivityResponse,
  DashboardAccessActivityResponseSchema,
  DashboardDownloadFiltersSchema,
  DashboardRefreshActivityResponse,
  DashboardRefreshActivityResponseSchema,
  BiReport,
  BiReportSchema,
  BiReportsPaginatedResponse,
  BiReportsPaginatedResponseSchema,
  BiDashboardSummary,
  BiDashboardSummarySchema,
  BiDashboardsQueryParams,
  BiUserReport,
  BiUserReportSchema,
  BiUserSummary,
  BiUserSummarySchema,
  GetUserReportsParams,
} from "@/pages/biReports/types";
import {
  Memory,
  MemoryResponse,
  MemoryResponseSchema,
  MemorySchema,
} from "@/pages/teammate/dataMates/memory/types";
import {
  AccountsCostQueryParams,
  accountsListSchema,
  AccountsList,
  totalCostSchema,
  TotalCost,
  chartDataListSchema,
  ChartDataList,
  tableDataListSchema,
  TableDataList,
} from "@/types/accounts-cost";
import {
  MetabaseDashboard,
  MetabaseDashboardSchema,
  SpacesGuestTokenResponse,
  SpacesGuestTokenResponseSchema,
} from "@/pages/spaces/types";
import {
  SpacesResponse,
  SpacesResponseSchema,
  SpacesRequest,
  SyncDashboardsResponse,
  SyncDashboardsResponseSchema,
} from "@/pages/spaces/types";

import {
  computePoolDailyCostItemSchema,
  ComputePoolEntry,
  computePoolEntrySchema,
  computePoolMetadataItemSchema,
  computePoolOverviewSchema,
  computePoolsGraphDataSchema,
  computePoolsSummarySchema,
  computePoolUtilizationSchema,
} from "@/pages/computePools/types";

import { contractItemSchema, contractsSummarySchema } from "@/pages/contracts/types";
import {
  SlackWebhookResponseSchema,
  type SlackWebhookResponse,
  type SlackWebhookRequest,
} from "@/pages/settings/types";
import {
  MyUsageTrendsResponse,
  myUsageTrendsResponseSchema,
  OrgUsageTrendsResponse,
  orgUsageTrendsResponseSchema,
  Transaction,
  TransactionGroupedByUser,
  transactionGroupedByUserSchema,
  AggregatedTask,
  aggregatedTaskSchema,
  transactionSchema,
  PlansResponse,
  PlansResponseSchema,
  CheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  CreateCheckoutSessionResponseSchema,
  PostCheckoutSessionRequest,
  DowngradePlanRequest,
  DowngradePlanFeedbackRequest,
  DowngradePlanFeedbackResponse,
  DowngradePlanFeedbackResponseSchema,
  CreditUsageResponse,
  CreditUsageResponseSchema,
  AllPlansResponse,
  AllPlansResponseSchema,
  WalletResponse,
  WalletResponseSchema,
  WalletDepositResponse,
  WalletDepositResponseSchema,
  TokenUsageResponse,
  TokenUsageResponseSchema,
} from "@/pages/credits/types";

import {
  CreateOrUpdatePromptRequest,
  PromptLibraryTagsResponse,
  TagsResponseSchema as PromptLibraryTagsResponseSchema,
  PromptResponse,
  PromptResponseSchema,
  StarterQuestionsResponse,
  StarterQuestionsResponseSchema,
  EnhancedPromptResponse,
  EnhancedPromptResponseSchema,
} from "@/pages/playground/promptLibrary/types";
import {
  TablesResponse,
  TablesResponseSchema,
  TableFiltersResponse,
  TagsResponse,
  TagsResponseSchema as DatasetTagsResponseSchema,
  RecentTablesResponse,
  RecentTablesResponseSchema,
  TableRkResponse,
  TableRkResponseSchema,
  UpdateColumnDescriptionRequest,
  GetTablesParams,
} from "@/pages/datasets/types";
import { UserView } from "@/modules/playground";
import { GetAIFeedbackResponse, GetAIFeedbackResponseSchema } from "@/components/feedback/types";
import { OtherGraphResponseSchema } from "@/pages/summary/context/schemas";
import { IntegrationType } from "@/helpers/integrationType";
import {
  SSOTeamSyncConfigResponseSchema,
  SSOGroupMappingListSchema,
  SSOGroupMappingResponseSchema,
  SSOPreviewResponseSchema,
  SSOAuditEventListResponseSchema,
  SSOExternalGroupListResponseSchema,
  SSOBulkUploadResponseSchema,
  SSOBulkMappingUploadResponseSchema,
  SSOTargetsResponseSchema,
  type SSOTeamSyncConfigUpdatePayload,
  type SSOGroupMappingUpdatePayload,
} from "@/pages/settings/sso-group-sync/types";

import {
  CreateSkillPayload,
  ImportCatalogSkillsPayload,
  SkillCatalogResponseSchema,
  DatamateSkillsResponseSchema,
  DEFAULT_SKILL_PAGE_SIZE,
  SkillListParams,
  SkillListResponseSchema,
  SkillResponseSchema,
  SkillsCreatedResponseSchema,
  SkillWorkspacesResponseSchema,
  UpdateSkillPayload,
} from "@/pages/workspaceApp/skillTypes";
import {
  ConnectionShares,
  ConnectionSharesSchema,
  DatamateShares,
  DatamateSharesPayload,
  DatamateSharesSchema,
} from "@/pages/workspaceApp/shareTypes";
import {
  RuleActionPayload,
  RuleDetail,
  RuleDetailSchema,
  RuleEvent,
  RuleEventsResponseSchema,
  RuleListParams,
  RuleListResponse,
  RuleListResponseSchema,
  RuleOwnerAction,
  RuleReviewPayload,
  RulesSummary,
  RulesSummarySchema,
  RuleSettingsPayload,
} from "@/pages/workspaceApp/rules/rulesSchemas";
import { PrivacyLevel } from "@/types";
export const api = new APIClient();

// Summary v2 - Cost endpoints
export const getOverallCost = (params: SummaryCostGraphRequest) =>
  api.get<OverallCostSummaryResponse>(
    "/summary/v2/cost/overall",
    params,
    0,
    OverallCostSummaryResponseSchema
  );

export const getOverallCostEstimate = (params: SummaryCostGraphRequest) =>
  api.get<OverallCostSummaryResponse>(
    "/summary/v2/cost/overall/estimate",
    params,
    0,
    OverallCostSummaryResponseSchema
  );

export const getComputeCost = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedComputeGraphResponse>(
    "/summary/v2/cost/compute",
    params,
    0,
    AggregatedComputeGraphResponseSchema
  );

export const getComputeCostEstimate = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedComputeGraphResponse>(
    "/summary/v2/cost/compute/estimate",
    params,
    0,
    AggregatedComputeGraphResponseSchema
  );

export const getStorageCost = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedStorageGraphResponse>(
    "/summary/v2/cost/storage",
    params,
    0,
    AggregatedStorageGraphResponseSchema
  );

export const getStorageCostEstimate = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedStorageGraphResponse>(
    "/summary/v2/cost/storage/estimate",
    params,
    0,
    AggregatedStorageGraphResponseSchema
  );

export const getServerlessCost = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedServerlessGraphResponse>(
    "/summary/v2/cost/serverless",
    params,
    0,
    AggregatedServerlessGraphResponseSchema
  );

export const getServerlessCostEstimate = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedServerlessGraphResponse>(
    "/summary/v2/cost/serverless/estimate",
    params,
    0,
    AggregatedServerlessGraphResponseSchema
  );

export const getTotalCost = (params: SummaryCostGraphRequest) =>
  api.get<TotalCosts>("/summary/v2/cost/total", params, 0, TotalCostsSchema);

export const getTotalCostEstimate = (params: SummaryCostGraphRequest) =>
  api.get<TotalCosts>("/summary/v2/cost/total/estimate", params, 0, TotalCostsSchema);

export const getOtherCost = (params: SummaryCostGraphRequest) =>
  api.get<z.infer<typeof OtherGraphResponseSchema>>(
    "/summary/v2/cost/other",
    params,
    0,
    OtherGraphResponseSchema
  );

export const getOtherCostEstimate = (params: SummaryCostGraphRequest) =>
  api.get<z.infer<typeof OtherGraphResponseSchema>>(
    "/summary/v2/cost/other/estimate",
    params,
    0,
    OtherGraphResponseSchema
  );

export const getCloudServicesCost = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedCloudServicesGraphResponse>(
    "/summary/v2/cost/cloud_services",
    params,
    0,
    AggregatedCloudServicesGraphResponseSchema
  );

export const getCloudServicesCostEstimate = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedCloudServicesGraphResponse>(
    "/summary/v2/cost/cloud_services/estimate",
    params,
    0,
    AggregatedCloudServicesGraphResponseSchema
  );

export const getAiServicesCost = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedAiServicesGraphResponse>(
    "/summary/v2/cost/ai_services",
    params,
    0,
    AggregatedAiServicesGraphResponseSchema
  );

export const getAiServicesCostEstimate = (params: SummaryCostGraphRequest) =>
  api.get<AggregatedAiServicesEstimateGraphResponse>(
    "/summary/v2/cost/ai_services/estimate",
    params,
    0,
    AggregatedAiServicesEstimateGraphResponseSchema
  );

// Wrapper for backward compatibility - maps costType to specific function with Zod validation
type CostBreakdownResponse =
  | OverallCostSummaryResponse
  | AggregatedComputeGraphResponse
  | AggregatedStorageGraphResponse
  | AggregatedServerlessGraphResponse
  | AggregatedCloudServicesGraphResponse
  | AggregatedAiServicesGraphResponse
  | AggregatedAiServicesEstimateGraphResponse
  | TotalCosts;

export const getCostBreakdown = <R extends CostBreakdownResponse>(
  path: string,
  params: SummaryCostGraphRequest
): Promise<R | undefined> => {
  switch (path) {
    case "overall":
      return getOverallCost(params) as Promise<R | undefined>;
    case "overall/estimate":
      return getOverallCostEstimate(params) as Promise<R | undefined>;
    case "compute":
      return getComputeCost(params) as Promise<R | undefined>;
    case "compute/estimate":
      return getComputeCostEstimate(params) as Promise<R | undefined>;
    case "storage":
      return getStorageCost(params) as Promise<R | undefined>;
    case "storage/estimate":
      return getStorageCostEstimate(params) as Promise<R | undefined>;
    case "serverless":
      return getServerlessCost(params) as Promise<R | undefined>;
    case "serverless/estimate":
      return getServerlessCostEstimate(params) as Promise<R | undefined>;
    case "cloud_services":
      return getCloudServicesCost(params) as Promise<R | undefined>;
    case "cloud_services/estimate":
      return getCloudServicesCostEstimate(params) as Promise<R | undefined>;
    case "ai_services":
      return getAiServicesCost(params) as Promise<R | undefined>;
    case "ai_services/estimate":
      return getAiServicesCostEstimate(params) as Promise<R | undefined>;
    case "total":
      return getTotalCost(params) as Promise<R | undefined>;
    case "total/estimate":
      return getTotalCostEstimate(params) as Promise<R | undefined>;
    case "other":
      return getOtherCost(params) as Promise<R | undefined>;
    case "other/estimate":
      return getOtherCostEstimate(params) as Promise<R | undefined>;
    default:
      console.warn(`Unknown cost path: ${path}`);
      return getOverallCost(params) as Promise<R | undefined>;
  }
};

// Summary v2 - Savings endpoints
export const getAutonomousSavings = (params: SummaryCostGraphRequest) =>
  api.get<AutoTuneSavingsSummaryResponse>(
    "/summary/v2/savings/autonomous",
    params,
    0,
    AutoTuneSavingsSummaryResponseSchema
  );

export const getAutonomousSavingsEstimate = (params: SummaryCostGraphRequest) =>
  api.get<AutoTuneSavingsSummaryResponse>(
    "/summary/v2/savings/autonomous/estimate",
    params,
    0,
    AutoTuneSavingsSummaryResponseSchema
  );

export const getAssistedSavings = (params: SummaryCostGraphRequest) =>
  api.get<AssistedSavingsResponse>(
    "/summary/v2/savings/assisted",
    params,
    0,
    AssistedSavingsResponseSchema
  );

export const getAssistedSavingsEstimate = (params: SummaryCostGraphRequest) =>
  api.get<AssistedSavingsResponse>(
    "/summary/v2/savings/assisted/estimate",
    params,
    0,
    AssistedSavingsResponseSchema
  );

export const getOverallSavings = (params: SummaryCostGraphRequest) =>
  api.get<OverallSavingsResponse>(
    "/summary/v2/savings/overall",
    params,
    0,
    OverallSavingsResponseSchema
  );

export const getOverallSavingsEstimate = (params: SummaryCostGraphRequest) =>
  api.get<OverallSavingsEstimateResponse>(
    "/summary/v2/savings/overall/estimate",
    params,
    0,
    OverallSavingsEstimateResponseSchema
  );

// Wrapper for backward compatibility - maps savingsType to specific function
export const getSavingsBreakdown = (
  path: string,
  params: SummaryCostGraphRequest
): Promise<
  | AutoTuneSavingsSummaryResponse
  | OverallSavingsResponse
  | OverallSavingsEstimateResponse
  | AssistedSavingsResponse
  | undefined
> => {
  switch (path) {
    case "autonomous":
      return getAutonomousSavings(params);
    case "autonomous/estimate":
      return getAutonomousSavingsEstimate(params);
    case "assisted":
      return getAssistedSavings(params);
    case "assisted/estimate":
      return getAssistedSavingsEstimate(params);
    case "overall":
      return getOverallSavings(params);
    case "overall/estimate":
      return getOverallSavingsEstimate(params);
    default:
      console.warn(`Unknown savings path: ${path}`);
      return getAutonomousSavings(params);
  }
};

export const getSummaryPageOpportunities = (params: OpportunitiesSummary) =>
  api.get<GetSummaryOpportunitiesDataResponse[]>(
    "/summary/v2/opportunities",
    params,
    0,
    z.array(GetSummaryOpportunitiesDataResponseSchema)
  );

export const getOpportunities = (data: GroupedOpportunitiesFilters) =>
  api.get<Page<GetOpportunitiesDataResponse>>(
    "/opportunities/",
    data,
    0,
    pageSchema(GetOpportunitiesDataResponseSchema)
  );

// NOTE: backend route is `/opportunities/implemented` (NO trailing slash).
// `/opportunities/` (the regular list) keeps its trailing slash to match
// its existing FastAPI route — don't "normalize" these two consistently
// or one of them will start hitting the wrong handler.
export const getImplementedOpportunities = (data: OpportunitiesFilters) =>
  api.get<Page<GetImplementedOpportunitiesDataResponse>>(
    "/opportunities/implemented",
    data,
    0,
    pageSchema(GetImplementedOpportunitiesDataResponseSchema)
  );

export const downloadImplementedOpportunitiesCSV = (params: OpportunitiesFilters) =>
  downloadCSV(`/opportunities/implemented`, params);

export const downloadOpportunitiesCSV = (params: OpportunitiesFilters) =>
  downloadCSV(`/opportunities/`, params);

export const getGroupedOpportunities = (data: GroupedOpportunitiesFilters) =>
  api.get<GetGroupedOpportunitiesDataResponse[]>(
    "/opportunities/grouped",
    data,
    0,
    z.array(GetGroupedOpportunitiesDataResponseSchema)
  );

export const getPriorityOpportunities = (data: GroupedOpportunitiesFilters) =>
  api.get<PriorityOpportunityResponse[]>(
    "/opportunities/priority",
    data,
    0,
    z.array(PriorityOpportunityResponseSchema)
  );

export const getOpportunityDetail = (params: { opportunity_id: string; instance_id: number }) =>
  api.get<OpportunityDetailResponse>(
    "/opportunities/detail",
    params,
    0,
    OpportunityDetailResponseSchema
  );

export const getOpportunityComments = (opportunity_id: string, instance_id: number) =>
  api.get<OpportunityComment[]>(
    "/opportunities/comments",
    { opportunity_id, instance_id },
    0,
    z.array(CommentSchema)
  );

export const postOpportunityComment = (data: AddCommentRequest) =>
  api.post<OpportunityComment>("/opportunities/comments", data, CommentSchema);

export const putOpportunityComment = (comment_id: string, data: UpdateCommentRequest) =>
  api.put<OpportunityComment, UpdateCommentRequest>(
    `/opportunities/comments/${comment_id}`,
    data,
    CommentSchema
  );

export const deleteOpportunityComment = (comment_id: string) =>
  api.delete<StandardResponse>(`/opportunities/comments/${comment_id}`, {}, StandardResponseSchema);

export const putOpportunityLabels = (data: SetLabelsRequest) =>
  api.put<string[], SetLabelsRequest>("/opportunities/labels", data, z.array(z.string()));

export const getLabelSuggestions = () =>
  api.get<LabelSuggestionResponse>(
    "/opportunities/labels/suggestions",
    {},
    0,
    LabelSuggestionResponseSchema
  );

export const downloadGroupedOpportunitiesCSV = (params: GroupedOpportunitiesFilters) =>
  downloadCSV(`/opportunities/grouped/`, params);

export const updateOpportunities = (data: UpdateOpportunitiesDataRequest) =>
  api.post<StandardResponse>("/opportunities/", data, StandardResponseSchema);

export const getOpportunitiesSummary = (data: GetOpportunitiesSummaryRequest) =>
  api.get<GetOpportunitiesSummaryResponse[]>(
    "/opportunities/summary",
    data,
    0,
    z.array(GetOpportunitiesSummaryResponseSchema)
  );

export const getOpportunitiesResourceNameFilters = (
  data: GetOpportunitiesResourceNameFilterRequest
) =>
  api.get<GetOpportunitiesDynamicFilterResponse>(
    "/opportunities/resource_name_filter",
    data,
    10000,
    GetOpportunitiesDynamicFilterResponseSchema
  );

export const getOpportunitiesUserFilters = (data: GetOpportunitiesUserFilterRequest) =>
  api.get<GetOpportunitiesDynamicFilterResponse>(
    "/opportunities/filters/user",
    data,
    10000,
    GetOpportunitiesDynamicFilterResponseSchema
  );

export const getOpportunitiesWarehouseFilters = (data: GetOpportunitiesWarehouseFilterRequest) =>
  api.get<GetOpportunitiesDynamicFilterResponse>(
    "/opportunities/filters/warehouse",
    data,
    10000,
    GetOpportunitiesDynamicFilterResponseSchema
  );

export const getOpportunityNameFilters = (params: { workspace_id?: string[] } = {}) =>
  api.get<FilterResponse[]>(
    "/opportunities/filters/opportunity_name",
    params,
    0,
    z.array(FilterResponseSchema)
  );

export const getOpportunitiesResource = (data: GetOpportunitiesResourceRequest) =>
  api.get<GetOpportunitiesResourceResponse>(
    "/opportunities/resource",
    data,
    0,
    GetOpportunitiesResourceResponseSchema
  );

export const getOpportunitiesRedirection = (data: GetOpportunitiesRedirectionRequest) =>
  api.get<GetOpportunitiesRedirectResponse>(
    "/opportunities/redirection",
    data,
    0,
    GetOpportunitiesRedirectResponseSchema
  );

export const getOpportunityUpdates = (data: GetOpportunityUpdatesRequest) =>
  api.get<GetOpportunityUpdatesResponse[]>(
    "/opportunities/updates",
    data,
    0,
    z.array(GetOpportunityUpdatesResponseSchema)
  );

export const getQueryById = (id: string, params: Record<string, string>) =>
  api.get<QueryDetailsResponse>(`/query/v2/items/${id}`, params, 0, QueryDetailsResponseSchema);

// Get all queries with pagination and filtering
export const getAllQueries = (params: Partial<FilterParams>) =>
  api.get<Page<QueryItem>>("/query/v2/", params, 0, pageSchema(QueryItemSchema));

// Get last fetched time for queries
export const getLastFetchedTime = () =>
  api.get<{ time_string: string }>(
    "/query/last_fetched_time",
    {},
    0,
    z.object({ time_string: z.string() })
  );

// Download all queries as CSV file
export const getAllQueriesCSVFile = (params: Partial<FilterParams>) =>
  api
    // @ts-expect-error responseType 'blob' not in api client definition
    .get("/query/v2/download", params, undefined, undefined, {
      responseType: "blob",
      headers: {
        Accept: "text/csv",
      },
    })
    .then(blob => {
      return downloadBlob(blob as Blob);
    });

// Get query group details by hash
export const getQueryGroupDetail = (
  hash: string,
  params?: { start_date?: string; end_date?: string }
) => api.get(`/query/v2/groups/${hash}`, params || {}, 0, QueryGroupSchema);

// Update query group name
export const updateQueryGroupName = (hash: string, data: UpdateQueryGroupNameRequest) =>
  api.update<UpdateQueryGroupNameResponse>(
    `/query/groups/${hash}`,
    data,
    UpdateQueryGroupNameResponseSchema
  );

export const getQuerySqlLineageById = (id: string, params: Record<string, string>) =>
  api.get<GetQuerySQLLineageResponse>(
    `/query/v2/sql_lineage/${id}`,
    params,
    60000,
    GetQuerySQLLineageResponseSchema
  );

export const getQueryGroupSqlLineageById = (id: string, params: Record<string, string>) =>
  api.get<GetQuerySQLLineageResponse>(
    `/query/v2/sql_lineage_group/${id}`,
    params,
    60000,
    GetQuerySQLLineageResponseSchema
  );

export const getQueryTextByHash = (hash: string) =>
  api.get<QueryTextResponse>(
    `/query/v2/query_group/query_text/${hash}`,
    {},
    0,
    QueryTextResponseSchema
  );

export const getQueryTextAndIndicesByHash = (hash: string, data: { spans: string[] }) =>
  api.post<QueryTextIndicesResponse>(
    `/query/v2/indices/${hash}`,
    data,
    QueryTextIndicesResponseSchema
  );

export const getTenantInfo = () =>
  api.get<GetTenantInfoResponse>(`/auth/tenant-info`, {}, 10000, tenantInfoSchema);

export const getSSOUserInfo = () =>
  api.get<SSOUserInfoResponse>("/auth/supertokens_user_info", {}, 0, SSOUserInfoResponseSchema);

/** Verify Dippr's SSO identity with the live session, leaving SaaS auth defaults intact. */
export const getDipprSSOUserInfo = async (): Promise<SSOUserInfoResponse> => {
  const accessToken = await Session.getAccessToken();
  if (!accessToken) throw new Error("Dippr sign-in requires an active SSO session");
  const profile = await api.get<SSOUserInfoResponse>(
    "/auth/dippr/sso-user-info",
    {},
    0,
    SSOUserInfoResponseSchema,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  if (!profile) throw new Error("Unable to verify the SSO session");
  return profile;
};

export const postRegisterSSO = (data: RegisterSSORequest) =>
  api.post<User>("/auth/register-sso", data, UserSchema);
export const updateTenantInfo = (data: Partial<GetTenantInfoResponse>) =>
  api.update<StandardResponse>(`/auth/tenant-info`, data, StandardResponseSchema);

// Register invite API - registers user from invite token
export const postRegisterInvite = (data: RegisterInviteRequest) =>
  api.post<User>("/auth/register-invite", data, UserSchema);

// Login API - authenticates user and returns user data with auth token
export const postLogin = (data: LoginRequest) => api.post<User>("/auth/login", data, UserSchema);
// Staff sign-in on the root domain with a Google Identity Services ID token.
export const postGoogleLogin = (credential: string) =>
  api.post<User>("/auth/google-login", { credential }, UserSchema);

// Social login exchange - swaps a short-lived social login_token for the app
// user (auth_token), establishing a session on the instance subdomain.
export const socialExchange = (token: string) =>
  api.post<User>("/auth/social/exchange", { token }, UserSchema);

// Connect-token - an already-authenticated user mints a short-lived, one-time
// login_token that a first-party client (CLI/extension) exchanges via
// /auth/social/exchange. This keeps the raw auth_token out of any loopback URL.
// Uses a direct fetch with the just-issued auth_token so it does not depend on
// the shared axios default headers being set yet.
// A stalled mint fails instead of leaving the caller waiting with no feedback.
const CONNECT_TOKEN_TIMEOUT_MS = 15_000;

/** A login-token endpoint answered with an error status. */
export class LoginTokenRequestError extends Error {
  constructor(
    path: string,
    readonly status: number
  ) {
    super(`${path} failed (${status})`);
  }
}

const postForLoginToken = async (
  path: string,
  authToken: string,
  tenant: string
): Promise<string> => {
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
      "x-tenant": tenant,
    },
    signal: AbortSignal.timeout(CONNECT_TOKEN_TIMEOUT_MS),
  });
  if (!res.ok) throw new LoginTokenRequestError(path, res.status);
  const data = (await res.json()) as { login_token?: string };
  if (!data.login_token) throw new Error(`${path} did not return a login_token`);
  return data.login_token;
};

// The CLI/editor login token: /auth/social/exchange turns it into a session.
export const getConnectLoginToken = (authToken: string, tenant: string): Promise<string> =>
  postForLoginToken("/auth/social/connect-token", authToken, tenant);

// The Dippr handoff ticket for /connect: single use, 120 s, and
// redeemable only by the Altimate IdP for who the user is. It can never be
// exchanged for a SaaS session.
export const getDipprHandoffToken = (authToken: string, tenant: string): Promise<string> =>
  postForLoginToken("/auth/dippr/handoff-token", authToken, tenant);

// Verify free user API - verifies a free user token
export const verifyFreeUser = (data: { token: string }) =>
  api.post<VerifyFreeUserResponse>("/companies/verify-user", data, VerifyFreeUserResponseSchema);

// User onboard API - marks user as onboarded
export const postUserOnboard = () =>
  api.post<StandardResponse>("/users/onboard", {}, StandardResponseSchema);

// Session start API - records that the signed-in user opened the app; the
// backend answers 204 with no body, so the call bypasses response-schema parsing.
export const postSessionStart = () => axios.post("/users/me/session-start", {});

// Reset password API - resets user password with token
export const postResetPassword = (data: ResetPasswordRequest) =>
  api.post<StandardResponse>("/auth/reset-password", data, StandardResponseSchema);

// Get email instance list API - returns list of companies/instances associated with an email
export const getEmailInstanceList = (email: string) =>
  api.get<EmailInstanceListResponse>(
    `/companies/get-all-companies/${email}`,
    {},
    0,
    EmailInstanceListResponseSchema
  );

// Check company availability API - checks if company name is available for registration
export const companyAvailable = (company: string) =>
  api.get<StandardResponse>(`/companies/available/${company}`, {}, 0, StandardResponseSchema);

// Company register API - registers a new company/instance
export const companyRegister = (data: CompanyRegisterRequest) =>
  api.post<CompanyRegisterResponse>("/companies/register", data, CompanyRegisterResponseSchema);

// Datastore APIs
// Get available databases API - retrieves list of available databases for a datastore
export const getAvailableDatabases = (datastoreId: string) =>
  api.get<AvailableDatabasesResponse>(
    `/data_stores/${datastoreId}/available_databases`,
    {},
    0,
    AvailableDatabasesResponseSchema
  );

// Get datastore detail API - retrieves details for a specific datastore
export const getDatastoreDetail = (datastoreId: string) =>
  api.get<DatastoreDetailResponse>(
    `/data_stores/${datastoreId}`,
    {},
    0,
    DatastoreDetailResponseSchema
  );

// Notification API
// Post Slack webhook API - creates/updates a Slack webhook notification channel
export const postSlackWebhook = (data: SlackWebhookRequest) =>
  api.post<SlackWebhookResponse>("/notification", data, SlackWebhookResponseSchema);

// Sync history API - retrieves sync history for a datastore
export const getSyncHistory = (datastoreId: string, params: GetSyncHistoryParams) =>
  api.get<GetSyncHistoryResponse>(
    `/events/sync_history/${datastoreId}`,
    params,
    0,
    GetSyncHistoryResponseSchema
  );

// Lineage API
// Altimate to Amundsen V2 API - retrieves terminal/starting node for lineage visualization
export const altimateToAmundsenV2 = (params: TerminalNodeRequest) =>
  api.get<Asset>("/lineage/v2/terminal", params, 0, AssetSchema);

export const postSendDatamateRequest = (requestType: string) =>
  api.post<StandardResponse>(
    "/notification/send_datamate_request",
    { request_type: requestType },
    StandardResponseSchema
  );

export const getExportLineage = (params: { page: number; size: number }) =>
  api.get<Page<GetExportLineageItem>>(
    `/dbt/v4/export-lineage`,
    params,
    0,
    GetExportLineagePageSchema
  );

export const getExportLineageItem = (id: string) =>
  api.get<GetExportLineageItem>(`/dbt/v4/export-lineage/${id}`, {}, 0, GetExportLineageItemSchema);

export const deleteExportLineageItem = (id: string) =>
  api.delete<StandardResponse>(`/dbt/v4/export-lineage/${id}`, {}, StandardResponseSchema);

export const getDownloadUrl = (s3_path: string) =>
  api.post<UrlResponse>("/dbt/v1/download_signed_url", { s3_path }, urlResponse);

export const getSavedFilters = (data: { applicableFor: string; type: FilterType }) => {
  return api.get<{ presets: SavedFilter[] }>(
    `/preset/`,
    { applicable_for: data.applicableFor, type: data.type },
    0,
    z.object({
      presets: z.array(savedFilterSchema.extend({ id: z.unknown() })),
    })
  );
};

export const createFilter = (data: Omit<SavedFilter, "id">) => {
  return api.post<{ preset_id: number }>(`/preset`, data, PresetIdResponseSchema);
};

export const updateFilter = (data: SavedFilter) => {
  return api.update<{ preset_id: number }>(`/preset/${data.id}`, data, PresetIdResponseSchema);
};

export const deleteFilter = (id: string) => {
  return api.delete<{ preset_id: number }>(`/preset/${id}`, {}, PresetIdResponseSchema);
};

export const getLastTableRefreshedTime = (table_rk: string) =>
  api.get<GetTableLastRefreshedResponse>(
    `/tables/last_refreshed/?table_rk=${table_rk}`,
    {},
    10000,
    GetTableLastRefreshedResponseSchema
  );

export const regenerateToken = () =>
  api.post<UserLoginOutResponse>("/auth/regenerate_token", undefined, userLoginOutSchema);

export const downloadAutoTuneHistoryCSV = (
  instance_id: string,
  warehouse_rk: string,
  params: { period: string; event_types?: string[] }
) =>
  downloadCSV(
    `/warehouse/audit-events/csv/instance/${instance_id}/warehouse_rk/${warehouse_rk}`,
    params
  );

// Mirrors getSqlWarehouseAutoTuneHistory's params minus paging: the export
// covers every row the current filters match, not just the visible page.
// `event_types` carries the same comma-separated filter-group keys.
export const downloadSqlWarehouseAutoTuneHistoryCSV = (
  warehouseId: string,
  params: {
    instance_id: number;
    workspace_id: string;
    event_types?: string;
    period?: number;
  }
) => downloadCSV(`/api/v1/databricks/warehouses/${warehouseId}/auto-tune/history/csv`, params);

const downloadCSV = (endpoint: string, params: Record<string, unknown>) =>
  api
    // @ts-expect-error responseType 'blob' not in api client definition
    .get(endpoint, params, undefined, undefined, {
      responseType: "blob",
      headers: {
        Accept: "text/csv",
      },
    })
    .then(blob => {
      downloadBlob(blob as Blob);
      return true;
    });

const downloadCSVPost = (
  endpoint: string,
  data: Record<string, unknown>,
  params?: Record<string, string>
) => {
  const queryString = params ? new URLSearchParams(params).toString() : "";
  const urlWithParams = queryString ? `${endpoint}?${queryString}` : endpoint;
  return (
    api
      // @ts-expect-error responseType 'blob' not in api client definition
      .post(urlWithParams, data, undefined, {
        responseType: "blob",
        headers: {
          Accept: "text/csv",
        },
      })
      .then(blob => {
        downloadBlob(blob as Blob);
        return true;
      })
  );
};

export const downloadAltimateUsersCSV = (params: {
  company: string;
  withServiceUser: boolean;
  allUsers: boolean;
  type: string;
}) => downloadCSV(`/users/download`, params);

export const downloadBiUserReportsCSV = (
  params: Omit<GetUserReportsParams, "page" | "page_size">
) => downloadCSV(`/business_intelligence/users/download`, params);

export const downloadAllTableDataCSV = (params: TableDataFilters) =>
  downloadCSV(`/tables/tabledata`, params);

export const downloadWarehouseCSV = (params: GetAllWarehousesParams) =>
  downloadCSV(`/warehouse`, params as Record<string, unknown>);

export const downloadAllQueryCSV = (params: QueryAllCSVFilters) =>
  downloadCSV(`/query/v2/`, params);

export const downloadAllQueryGroupsCSV = (params: QueryGroupsFilters) =>
  downloadCSV(`/query/v2/groups/`, params);

export const downloadAllQueryJobsCSV = (params: QueryJobsFilters) =>
  downloadCSV(`/query/jobs/`, params);

export const downloadAllQueryJobExecutionsCSV = (
  job_id: string,
  params: QueryJobExecutionsFilters
) => downloadCSV(`/query/jobs/executions/${job_id}`, params);

// Governance APIs
export const getGovernanceSummary = () =>
  api.get<GovernanceSummary>("/governance/summary", {}, 0, GovernanceSummarySchema);

export const getGovernanceFilters = (params: GovernanceFilterParams) =>
  api.get<GovernanceFilters>("/governance/filters", params, 0, GovernanceFiltersSchema);

export const getGovernanceUsers = (params: UsersTableFilterParams) =>
  api.get<GovernanceUsersResponse>("/governance/users", params, 0, GovernanceUsersResponseSchema);

export const getGovernanceRoles = (params: RolesTableFilterParams) =>
  api.get<GovernanceRolesResponse>("/governance/roles", params, 0, GovernanceRolesResponseSchema);

export const getGovernanceUserDetails = (user_name: string) =>
  api.get<GovernanceUserDetails>(
    `/governance/users/details/${user_name}`,
    {},
    0,
    GovernanceUserDetailsSchema
  );

export const getGovernanceRoleDetails = (role_name: string) =>
  api.get<GovernanceRoleDetails>(
    `/governance/roles/details/${role_name}`,
    {},
    0,
    GovernanceRoleDetailsSchema
  );

export const getGovernanceUsersCostGraph = (params: { period: number; user_name: string }) =>
  api.get<GovernanceCostGraph>("/governance/users/cost", params, 0, GovernanceCostGraphSchema);

export const getGovernanceRolesCostGraph = (params: { period: number; role_name: string }) =>
  api.get<GovernanceCostGraph>("/governance/roles/cost", params, 0, GovernanceCostGraphSchema);

export const downloadUsersCSV = (params: UsersTableFilterParams) =>
  downloadCSV(`/governance/users`, params);

export const downloadRolesCSV = (params: RolesTableFilterParams) =>
  downloadCSV(`/governance/roles`, params);

export const downloadExecutionHistoryCSV = (all_time: string) =>
  downloadCSV(`/events/execution_history/`, {
    all_time,
  });

export const downloadDetailedExecutionHistoryCSV = (all_time: string) =>
  downloadCSV(`/events/credit_details/`, {
    all_time,
  });

export const downloadDashboardsCSV = (params: z.infer<typeof DashboardDownloadFiltersSchema>) =>
  downloadCSV(`/business_intelligence/dashboards/download_csv`, params);

export const getUserGroups = ({
  search = "",
  page = 1,
  size = 50,
}: {
  search?: string;
  page?: number;
  size?: number;
}) =>
  api.get<z.infer<typeof GetAllUserGroupsResponseSchema>>(
    "/groups/",
    { search, page, size },
    0,
    GetAllUserGroupsResponseSchema
  );

export const getUserGroupDetails = (id: string) =>
  api.get<z.infer<typeof UserGroupWithMembersAndRulesZodSchema>>(
    `/groups/${id}`,
    {},
    0,
    UserGroupWithMembersAndRulesZodSchema
  );

export const uploadUserGroups = (data: FormData) =>
  api.post<UploadUserGroupResponse>(
    "/groups/group_rule_file",
    data,
    UploadUserGroupResponseSchema,
    { headers: { "Content-Type": "multipart/form-data" } }
  );

export const uploadUsersToGroups = (data: FormData) =>
  api.post<UploadUserResponse>("/groups/user_group_file", data, UploadUserResponseSchema, {
    headers: { "Content-Type": "multipart/form-data" },
  });

export const deleteRuleInUserGroup = (ruleId: string, groupId: string) => {
  return api.delete<StandardResponse>(
    `/groups/${groupId}/rules/${ruleId}`,
    {},
    StandardResponseSchema
  );
};

export const deleteUserInUserGroup = (userId: string, groupId: string) => {
  return api.delete<StandardResponseWithWarning>(
    `/groups/${groupId}/users/${userId}`,
    {},
    StandardResponseWithWarningSchema
  );
};

export const deleteUserGroup = (groupId: string) => {
  return api.delete<StandardResponse>(`/groups/${groupId}`, {}, StandardResponseSchema);
};

export const getWarehouseSchedulerEvents = (params: GetWarehouseSchedulerEventsParams) =>
  // Trailing slash matters: the backend route lives at the router root, so the
  // slash-less path costs a 307 redirect on every call.
  api.get<ScheduleResponse[]>("/warehouse/schedules/", { ...params }, 0, GetScheduleResponseSchema);

export const getWarehouseDynamicSchedulerEvents = (params: GetWarehouseSchedulerEventsParams) =>
  api.get<CombinedScheduleResponse[]>(
    "/warehouse/schedules/dynamic",
    { ...params },
    0,
    GetCombinedScheduleResponseSchema
  );

export const createWarehouseSchedulerEvent = (data: ScheduleCreateRequest) =>
  api.post<ScheduleResponse>(`/warehouse/schedules/`, data, ScheduleResponseSchema);

export const createWarehouseSchedulerEventForDynamicResizing = (data: ScheduleCreateRequest) =>
  api.post<ScheduleResponse>(`/warehouse/schedules/dynamic/create`, data, ScheduleResponseSchema);

export const createWarehouseScheduleSeries = (data: ScheduleSeriesCreateRequest) =>
  api.post<ScheduleSeriesResponse>(
    `/warehouse/schedules/series`,
    data,
    ScheduleSeriesResponseSchema
  );

// The warehouse's series with their CURRENT templates — the live cadence
// truth, as opposed to each occurrence's materialization-time
// `recurrence_pattern` snapshot, which goes stale after an anchored edit.
export const listWarehouseScheduleSeries = (params: {
  warehouse_name: string;
  instance_id: string;
}) =>
  api.get<ScheduleSeriesTemplate[]>(
    `/warehouse/schedules/series`,
    { ...params },
    0,
    ScheduleSeriesTemplateListSchema
  );

// Dry-run a series spec against existing schedules — returns which occurrences
// would conflict (and be skipped), creating nothing.
export const previewWarehouseScheduleSeries = (data: ScheduleSeriesCreateRequest) =>
  api.post<ScheduleSeriesPreviewResponse>(
    `/warehouse/schedules/series/preview`,
    data,
    ScheduleSeriesPreviewResponseSchema
  );

// Edit a series and regenerate its future occurrences ("all future events").
export const updateWarehouseScheduleSeries = (
  seriesId: number,
  data: ScheduleSeriesUpdateRequest
) =>
  api.update<ScheduleSeriesResponse>(
    `/warehouse/schedules/series/${seriesId}`,
    data,
    ScheduleSeriesResponseSchema
  );

// Cancel a series — soft-rejects its future occurrences ("all events" on delete).
export const cancelWarehouseScheduleSeries = (seriesId: number) =>
  api.post<ScheduleSeriesSummary>(
    `/warehouse/schedules/series/${seriesId}/cancel`,
    {},
    ScheduleSeriesSummarySchema
  );

export const editWarehouseSchedulerEvent = (scheduleId: string, data: ScheduleUpdateRequest) =>
  api.post<ScheduleResponse>(`/warehouse/schedules/${scheduleId}`, data, ScheduleResponseSchema);

export const approveScheduleEvent = (scheduleId: string, data: ScheduleApprovalRequest) =>
  api.post<ScheduleResponse>(
    `/warehouse/schedules/${scheduleId}/approve`,
    data,
    ScheduleResponseSchema
  );

export const rejectScheduleEvent = (scheduleId: string, data: ScheduleRejectionRequest) =>
  api.post<ScheduleResponse>(
    `/warehouse/schedules/${scheduleId}/reject`,
    data,
    ScheduleResponseSchema
  );

export const getScheduledEventUpdates = (data: GetScheduledEventUpdatesRequest) =>
  api.get<GetScheduledEventUpdatesResponse[]>(
    `/warehouse/schedules/${data.schedule_id}/event_history`,
    data,
    0,
    z.array(GetScheduledEventUpdatesResponseSchema)
  );

export const getSnowflakeJobs = (params: GetSnowflakeJobsParams) =>
  api.get<Page<GetSnowflakeJobItem>>(
    "/query/v2/snowflake_jobs",
    flattenCustomTagFilters(params),
    0,
    pageSchema(getSnowflakeJobItemSchema)
  );

export const getSnowflakeJobRuns = (id: string, params: GetSnowflakeJobRunsParams) =>
  api.get<Page<GetSnowflakeJobRunItem>>(
    `/query/v2/snowflake_jobs/runs/${id}`,
    params,
    0,
    pageSchema(getSnowflakeJobRunItemSchema)
  );

export const getSnowflakeJobRunsGraphData = (
  id: string,
  params: GetSnowflakeJobRunsGraphDataParams
) =>
  api.get<GetSnowflakeJobRunsGraphData[]>(
    `/query/v2/snowflake_jobs/runs_graph/${id}`,
    params,
    0,
    z.array(getSnowflakeJobRunsGraphDataSchema)
  );

export const getDbtJobRunsList = (id: string, params: GetDbtJobRunsListParams) =>
  api.get<Page<DbtJobRunItem>>(
    `/query/v2/snowflake_jobs/run_list/${id}`,
    params,
    0,
    pageSchema(dbtJobRunItem)
  );

export const getDbtJobRunsListGraphData = (id: string, params: GetDbtJobRunsListGraphDataParams) =>
  api.get<DbtJobRunItem[]>(
    `/query/v2/snowflake_jobs/run_list_graph/${id}`,
    params,
    0,
    z.array(dbtJobRunItem)
  );

export const getDbtModelRunSummary = (workflowId: string, params: GetDbtModelRunSummaryParams) =>
  api.get<DbtModelRunSummary>(
    `/query/v2/snowflake_jobs/tasks_summary/${workflowId}`,
    params,
    0,
    dbtModelRunSummary
  );

export const getDbtTasksSummary = (workflowId: string, params: GetDbtTasksSummaryParams) =>
  api.get<DbtTasksSummary>(
    `/query/v2/dbt-jobs/tasks/summary/${workflowId}`,
    params,
    0,
    dbtTasksSummarySchema
  );

export const getDbtModelsList = (id: string, params: GetDbtModelsListParams) =>
  api.get<Page<DbtModel>>(
    `/query/v2/snowflake_jobs/tasks/${id}`,
    params,
    0,
    pageSchema(dbtModelSchema)
  );

export const getDbtProjectAndIntegrationDetails = (id: string) =>
  api.get<DbtProjectAndIntegration>(
    `/query/v2/project_details/${id}`,
    {},
    0,
    dbtProjectAndIntegrationSchema
  );

export const getDbtHealthChecks = (params: {
  integration_id?: number;
  environment_id?: number;
  name?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
}) =>
  api.get<DbtHealthCheckResponse>(`/dbt/health-checks`, params, 0, dbtHealthCheckResponseSchema);

export const getSnowflakeJobRunTasks = (
  workflow_id: string,
  params: GetSnowflakeJobRunTasksParams
) =>
  api.get<Page<GetSnowflakeJobTaskItem>>(
    `/query/v2/snowflake_jobs/tasks_by_run/${workflow_id}`,
    params,
    0,
    pageSchema(getSnowflakeJobTaskItemSchema)
  );

export const getSnowflakeJobGraphData = (params: GetSnowflakeJobGraphDataRequestParams) =>
  api.get<GetSnowflakeJobGraphData[]>(
    `/query/v2/snowflake_jobs/graph_data`,
    flattenCustomTagFilters(params),
    0,
    z.array(getSnowflakeJobGraphDataSchema)
  );

export const getSnowflakeJobGraphSummary = (params: GetSnowflakeJobGraphDataRequestParams) =>
  api.get<CommonGraphSummaryResponse>(
    `/query/v2/snowflake_jobs/graph_summary`,
    flattenCustomTagFilters(params),
    0,
    CommonGraphSummarySchema
  );

export const getSnowflakeJobsSummary = (
  id: string,
  params: { start_date: string; end_date?: string; workflow_type?: string }
) =>
  api.get<GetSnowflakeJobSummary>(
    `/query/v2/snowflake_jobs/summary/${id}`,
    params,
    0,
    getSnowflakeJobSummarySchema
  );

export const getDbtJobListing = (id: string, params: GetDbtJobListingParams) =>
  api.get<Page<DbtJobListingItem>>(
    `/query/v2/snowflake_jobs/dbt_jobs/${id}`,
    params,
    0,
    pageSchema(dbtJobListingItemSchema)
  );

export const getDbtJobListingSummary = (id: string, params: GetDbtJobListingSummaryParams) =>
  api.get<DbtJobListingSummary>(
    `/query/v2/snowflake_jobs/dbt_jobs_summary/${id}`,
    params,
    0,
    dbtJobListingSummarySchema
  );

export const getDbtJobFilters = (
  id: string,
  params: { page: number; size: number; prefix?: string }
) =>
  api.get<Page<DbtJobFilterItem>>(
    `/query/v2/snowflake_jobs/dbt_filters/jobs/${id}`,
    params,
    0,
    pageSchema(dbtJobFilterItemSchema)
  );

// Older deployments omit `pages` (ALTIMATE-FRONTEND-16K); `getDbtTagFilters`
// derives it so the tag dropdown can still page.
export const dbtTagFilterPageSchema = pageSchema(dbtTagFilterItemSchema).extend({
  pages: z.number().nullish(),
});

export const getDbtTagFilters = (
  id: string,
  params: { page: number; size: number; prefix?: string }
) =>
  api
    .get<Omit<Page<DbtTagFilterItem>, "pages"> & { pages?: number | null }>(
      `/query/v2/snowflake_jobs/dbt_filters/tags/${id}`,
      params,
      0,
      dbtTagFilterPageSchema
    )
    // Derive `pages` from the requested page size (the response `size` can be
    // null or 0) so tags beyond the first page stay reachable.
    .then(res =>
      res
        ? {
            ...res,
            pages: res.pages ?? Math.max(1, Math.ceil(res.total / Math.max(1, params.size))),
          }
        : res
    );

export const getDbtModelFilters = (
  id: string,
  params: { page: number; size: number; prefix?: string }
) =>
  api.get<Page<DbtModelFilterItem>>(
    `/query/v2/snowflake_jobs/dbt_filters/models/${id}`,
    params,
    0,
    pageSchema(dbtModelFilterItemSchema)
  );

export const getSnowflakeJobSettings = () =>
  api.get<SnowflakeJobSettings>(
    "/query/v2/snowflake_jobs/settings",
    {},
    0,
    snowflakeJobSettingsSchema
  );

export const postSnowflakeJobSettings = (data: SnowflakeJobSettings) =>
  api.post<SnowflakeJobSettings>(
    "/query/v2/snowflake_jobs/settings",
    data,
    snowflakeJobSettingsSchema
  );

export const getWorkloadAttributes = () =>
  api.get<WorkloadAttributes>("/workload/attributes", {}, 0, workloadAttributesSchema);

export const getWorkloadAttributeValues = (params: {
  attribute_key: string;
  start_date: string;
  end_date: string;
  workflow_type?: string;
  page?: number;
  size?: number;
  search?: string;
}) => {
  const { attribute_key, ...queryParams } = params;
  return api.get<WorkloadAttributeValues>(
    `/workload/attributes/${encodeURIComponent(attribute_key)}/values`,
    queryParams,
    0,
    workloadAttributeValuesSchema
  );
};

export const getSnowflakeJobFilters = (params: GetSnowflakeJobFiltersParams) =>
  api.get<SnowflakeJobFilters>(
    "/query/v2/snowflake_jobs/filters",
    params,
    0,
    snowflakeJobFiltersSchema
  );

// Per-facet paginated filter endpoints. The bulk endpoint above stays for the
// bounded facets; these handle the unbounded ones (workflow_ids hit 3.5M
// observed, CUSTOM apps hit 17k observed). Backend rejects the wrong
// workflow_type combination with a 400.
//
// - /workflow_ids: CUSTOM, DBT (TABLEAU → 400)
// - /workflow_ids_with_labels: TABLEAU only (others → 400)
// - /teams, /apps: CUSTOM only (TABLEAU/DBT read these from bulk)
// - /custom_tags/{tag_key}: tag_key must appear in bulk.custom_tag_keys

export const getSnowflakeJobsFilterWorkflowIds = (params: SnowflakeJobsFilterPaginatedParams) =>
  api.get<SnowflakeJobsFilterStringPage>(
    "/query/v2/snowflake_jobs/filters/workflow_ids",
    params,
    0,
    snowflakeJobsFilterStringPageSchema
  );

export const getSnowflakeJobsFilterWorkflowIdsWithLabels = (
  params: SnowflakeJobsFilterPaginatedParams
) =>
  api.get<SnowflakeJobsFilterLabelValuePage>(
    "/query/v2/snowflake_jobs/filters/workflow_ids_with_labels",
    params,
    0,
    snowflakeJobsFilterLabelValuePageSchema
  );

export const getSnowflakeJobsFilterTeams = (params: SnowflakeJobsFilterPaginatedParams) =>
  api.get<SnowflakeJobsFilterStringPage>(
    "/query/v2/snowflake_jobs/filters/teams",
    params,
    0,
    snowflakeJobsFilterStringPageSchema
  );

export const getSnowflakeJobsFilterApps = (params: SnowflakeJobsFilterPaginatedParams) =>
  api.get<SnowflakeJobsFilterStringPage>(
    "/query/v2/snowflake_jobs/filters/apps",
    params,
    0,
    snowflakeJobsFilterStringPageSchema
  );

export const getSnowflakeJobsFilterCustomTagValues = (
  tagKey: string,
  params: SnowflakeJobsFilterPaginatedParams
) =>
  api.get<SnowflakeJobsFilterStringPage>(
    `/query/v2/snowflake_jobs/filters/custom_tags/${encodeURIComponent(tagKey)}`,
    params,
    0,
    snowflakeJobsFilterStringPageSchema
  );

export const compareSnowflakeJobRuns = (data: {
  workflow_id: string;
  run1_id: string;
  run2_id: string;
}) => api.post<RunCompareDiff[]>("/query/v2/snowflake_jobs/compare", data, runCompareDiffSchema);

export const getSnowflakeJobDefinition = (id: string, params: GetSnowflakeJobDefinitionParams) =>
  api.get<SnowflakeJobDefinitionResponse>(
    `/query/v2/snowflake_jobs/definition/${id}`,
    params,
    0,
    snowflakeJobDefinitionResponseSchema
  );

export const createAccessRule = (
  groupId: string,
  data: {
    rules: { entity_type: string; entity: string; type: string }[];
  }
) => {
  return api.post<StandardResponse>(
    `/groups/${groupId}/rules`,
    { rules: data.rules },
    StandardResponseSchema
  );
};

export const previewAccessRuleCoverage = (data: {
  rules: { entity_type: string; entity: string; type: string }[];
}) => {
  return api.post<AccessRuleCoveragePreviewResponse>(
    "/groups/access-rules/entity-assignment-coverage",
    data,
    AccessRuleCoveragePreviewResponseSchema
  );
};

export const createOwnership = (groupId: string, data: OwnershipRequestPayload) => {
  return api.post<StandardResponse>(`/groups/${groupId}/ownership`, data, StandardResponseSchema);
};

export const previewOwnershipCostAttribution = (
  data: OwnershipRequestsData,
  params: {
    start_date: string;
    end_date: string;
  }
) => {
  return api.postWithParams<OwnershipCostPreviewResponse>(
    "/groups/ownership-rules/cost-preview",
    params,
    data,
    OwnershipCostPreviewResponseSchema
  );
};

export const getOwnershipConflicts = (
  data: OwnershipRequestsData,
  params: {
    start_date: string;
    end_date: string;
  }
) => {
  return api.postWithParams<OwnershipConflictsResponse>(
    "/groups/ownership-rules/conflicts",
    params,
    data,
    OwnershipConflictsResponseSchema
  );
};

export const getConflictingTeams = (
  data: OwnershipRequestsData,
  params: {
    start_date: string;
    end_date: string;
  }
) => {
  return api.postWithParams<ConflictingTeamsResponse>(
    "/groups/ownership-rules/conflicting-team",
    params,
    data,
    ConflictingTeamsResponseSchema
  );
};

export const downloadConflictingTeamsCSV = (
  data: OwnershipRequestsData,
  params: {
    start_date: string;
    end_date: string;
  }
) => {
  return downloadCSVPost("/groups/ownership-rules/conflicting-team/download", data, params);
};

export const deleteOwnershipInUserGroup = (ownershipId: string, groupId: string) => {
  return api.delete<StandardResponse>(
    `/groups/${groupId}/ownership/${ownershipId}`,
    {},
    StandardResponseSchema
  );
};

export const getTeamConflicts = (teamId: string) => {
  return api.get<TeamConflictsResponse>(
    `/groups/ownership-rules/conflicts/${teamId}`,
    {},
    0,
    TeamConflictsResponseSchema
  );
};

export const getTeamConflictDetails = (
  teamId: string,
  params?: {
    page?: number;
    size?: number;
    sort_key?: string;
    sort_order?: string;
    filter_team_name?: string;
    filter_warehouse_name?: string;
    filter_database?: string;
    filter_schema?: string;
  }
) => {
  return api.get<TeamConflictDetailsResponse>(
    `/groups/ownership-rules/conflicts/${teamId}/details`,
    params ?? {},
    0,
    TeamConflictDetailsResponseSchema
  );
};

export const downloadTeamConflictDetailsCSV = (
  teamId: string,
  params?: {
    sort_key?: string;
    sort_order?: string;
    filter_team_name?: string;
    filter_warehouse_name?: string;
    filter_database?: string;
    filter_schema?: string;
  }
) => {
  return downloadCSV(`/groups/ownership-rules/conflicts/${teamId}/details/download`, params ?? {});
};

export const createTeam = (data: { name: string; description?: string }) => {
  return api.post<CreateTeamResponse>("/groups/group_create", data, CreateTeamResponseSchema);
};

export const addMemberToGroup = (groupId: string, data: { email: string }) => {
  return api.post<StandardResponse>(`/groups/${groupId}/members`, data, StandardResponseSchema);
};

// Bulk approval reports schedules skipped due to conflicts (e.g. inside an
// approved Blocked window) so the UI can say why.
export const bulkApproveSchedules = (data: z.infer<typeof ApplyRecommendedEventsRequestSchema>) =>
  api.post<z.infer<typeof BulkApprovalResponseSchema>>(
    "/warehouse/schedules/bulk-approve",
    data,
    BulkApprovalResponseSchema
  );
export const getSlackWebhook = () =>
  api.get<z.infer<typeof NotificationsResponseSchema>>(
    // The backend route is declared as ``/notification/``; the slashless
    // form answers 307 and the browser re-issues the request — one wasted
    // round trip per load, so call the real path directly.
    "/notification/",
    {},
    0,
    NotificationsResponseSchema
  );

export const getTags = (params: GetTagsParams) =>
  api.get<z.infer<typeof TagsResponseSchema>>(`/custom/tags`, params, 0, TagsResponseSchema);

export const createTag = (data: { rk: string; description: string; display_name: string }) =>
  api.post<Tag>("/custom/tags", { ...data, tag_type: TagTypeSchema.Enum.custom }, TagSchema);

export const deleteTag = (id: string) =>
  api.delete<StandardResponse>(`/custom/tags/${id}`, {}, StandardResponseSchema);

export const updateTag = (
  data: { rk: string } & Omit<Tag, "usage" | "created_by" | "tag_type">
) => {
  return api.update<Tag>(`/custom/tags/${data.rk}`, data, TagSchema);
};

export const deleteDBTConfig = (configId: string) =>
  api.delete<StandardResponse>(`/dbtconfig/${configId}`, {}, StandardResponseSchema);

export const getAllDBTConfigs = (params: {
  page: number;
  size: number;
  sort_key: string;
  sort_order: string;
  name?: string[];
  created_by?: (string | number)[];
}) => api.get<DBTConfigListResponse>("/dbtconfig/", params, 0, DBTConfigListResponseSchema);

export const getDBTConfigFilters = () =>
  api.get<DBTConfigFiltersResponse>(
    "/dbtconfig/filters/list",
    {},
    0,
    DBTConfigFiltersResponseSchema
  );

export const getDBTConfigById = (id: string | number) =>
  api.get<DBTConfigDetail>(`/dbtconfig/${id}`, {}, 0, DBTConfigDetailSchema);

export const addDBTConfig = (data: DBTConfigRequest) =>
  api.post<DBTConfigMutationResponse>("/dbtconfig/", data, DBTConfigMutationResponseSchema);

export const updateDBTConfig = (id: string | number, data: DBTConfigRequest) =>
  api.update<DBTConfigMutationResponse>(`/dbtconfig/${id}`, data, DBTConfigMutationResponseSchema);

export const getDBTConfigChecks = (params: { check_type?: string[]; files_required?: string[] }) =>
  api.get<DBTConfigChecksResponse>(
    "/dbtconfig/checks/list",
    params,
    0,
    DBTConfigChecksResponseSchema
  );

export const getDBTConfigChecksFilters = () =>
  api.get<DBTConfigChecksFiltersResponse>(
    "/dbtconfig/checks/filters",
    {},
    0,
    DBTConfigChecksFiltersResponseSchema
  );

export const addTagToNode = (data: AddTagToNodeRequest) =>
  api.post<z.infer<typeof AssociateTagResponseSchema>>(
    `/custom/tags/associate`,
    data,
    AssociateTagResponseSchema
  );

export const removeTagFromNode = (tagId: string, entity: AddTagToNodeRequest["entity"]) =>
  api.delete<z.infer<typeof AssociateTagResponseSchema>>(
    `/custom/tags/associate/${tagId}`,
    { data: entity },
    AssociateTagResponseSchema
  );

// Datastores API
export const getDatastores = () =>
  api.get<DatastoreResponseInferred>(`/data_stores/`, {}, 0, DatastoreResponseSchema);

export const addDatastore = (data: AddDatastoreRequest | AddDatabasesRequest) =>
  api.post<AddDatastoreResponse>("/data_stores/form/", data, AddDatastoreResponseSchema);

export const testDatastoreConnectionDuringCreation = (
  data: Partial<TestDatastoreConnectionDuringCreationRequest>
) =>
  api.post<TestDatastoreConnectionDuringCreationResponse>(
    "/data_stores/form/test_connection/",
    data,
    TestDatastoreConnectionDuringCreationResponseSchema
  );

export const getDatastoreTypes = () =>
  api.get<DatastoreTypeResponse>("/data_stores/types/", {}, 0, DatastoreTypeResponseSchema);

export const testDatastoreConnection = (id: number) =>
  api.get<TestConnectionResponse>(
    `/data_stores/${id}/validate`,
    {},
    0,
    TestConnectionResponseSchema
  );

export const syncDatastore = (datastoreId: number) =>
  api.post<SyncDatastoreResponse>(`/sync/${datastoreId}`, {}, SyncDatastoreResponseSchema);

export const toggleDatastoreSyncSchedule = (data_store_id: number) =>
  api.post<ToggleSyncScheduleResponse>(
    `/scheduler/sync/${data_store_id}`,
    {},
    ToggleSyncScheduleResponseSchema
  );

// A union of two `unknown` schemas accepts any response body unchanged; the API client has no
// "skip validation" overload.
const ANY_RESPONSE_SCHEMA = z.union([z.unknown(), z.unknown()]);

// `api.get` returns the raw body when the params are empty, so the response is parsed here.
// Neither the body nor the zod error is logged: the details can carry connection data.
export const getConnectionEditCapabilities = async (): Promise<EditCapabilitiesResponse> => {
  const result = await api.get<unknown>(
    "/connection_edit/capabilities",
    {},
    0,
    ANY_RESPONSE_SCHEMA
  );
  const parsed = EditCapabilitiesResponseSchema.safeParse(result);
  if (!parsed.success) throw new Error("Connection edit capabilities could not be read.");
  return parsed.data;
};

export const getConnectionEditForm = async (
  connectionType: string,
  connectionId: number | string
): Promise<ConnectionEditForm> => {
  const result = await api.get<unknown>(
    `/connection_edit/${encodeURIComponent(connectionType)}/${encodeURIComponent(String(connectionId))}`,
    {},
    0,
    ANY_RESPONSE_SCHEMA
  );
  const parsed = ConnectionEditFormSchema.safeParse(result);
  if (!parsed.success) throw new Error("The connection details could not be read.");
  return parsed.data;
};

// The path comes from the backend's edit form. Only a plain absolute path made of letters, digits,
// underscores, dashes and slashes is accepted: no host, query, fragment, dots or escapes, so a
// malformed response cannot send the authenticated request elsewhere.
const EDIT_PATH_PATTERN = /^\/[A-Za-z0-9_\-/]+$/;

const isAllowedEditPath = (path: string): boolean =>
  EDIT_PATH_PATTERN.test(path) && !path.includes("//");

export const saveConnectionEdit = async (
  method: "PATCH" | "PUT",
  path: string,
  body: Record<string, unknown>
) => {
  if (!isAllowedEditPath(path)) throw new Error("Invalid edit path");
  const result =
    method === "PUT"
      ? await api.put<unknown>(path, body, ANY_RESPONSE_SCHEMA)
      : await api.update<unknown>(path, body, ANY_RESPONSE_SCHEMA);
  // The API client returns undefined without sending when its secure-connection check fails.
  if (result === undefined) throw new Error("The connection could not be saved.");
  return result;
};

// Alert APIs
export const getAlertEvents = (data: GetAlertEventsParams) =>
  api.get<AlertEventsResponse>("/events/alerts/", data, 0, AlertEventsResponseSchema);

export const getAlertRules = (data: GetAlertRulesParams) =>
  api.get<AlertRulesResponse>("/alerts/rules/", data, 0, AlertRulesResponseSchema);

export const getAllSlackChannels = () =>
  api.get<SlackChannelsResponse>(
    "/notification/slack_channels",
    {},
    0,
    SlackChannelsResponseSchema
  );

export const getAlert = (alertId: string) =>
  api.get<Alert>(`/alerts/rules/${alertId}`, {}, 0, AlertSchema);

export const deleteAlert = (alertId: string) =>
  api.delete<StandardResponse>(`/alerts/alerts/${alertId}`, {}, StandardResponseSchema);

export const getSnowflakeStage = (data: { table_rk: string }) =>
  api.get<SnowflakeStageMetadata>(
    `/lineage/snowflake_stage`,
    data,
    0,
    snowflakeStageMetadataSchema
  );

export const getFinalLineageColumns = (params: {
  column_rk: string;
  upstream: boolean;
  downstream: boolean;
  direct: boolean;
  indirect: boolean;
}) => downloadCSV(`lineage/export_final_columns`, params);

export const getFinalLineageTables = (params: {
  table_rk: string;
  upstream: boolean;
  downstream: boolean;
}) => downloadCSV(`lineage/export_final_tables`, params);

// Catalog-wide distinct Snowflake object-tag values for the datasets /
// governance / compute-pools "Snowflake Tags" filter dropdown. `entity` selects
// the object domain whose tags populate the dropdown (defaults to datasets).
// The backend returns each option already formatted (label === value === the
// "name" or "name:value" token the snowflaketags filter param is matched
// against), so no client-side reshaping is needed.
export const getSnowflakeTagFilters = (params: {
  prefix?: string;
  page?: number;
  size?: number;
  entity?: "dataset" | "user" | "role" | "compute_pool";
}) =>
  api.get<TableRkFilterResponse>(
    `/tables/tabledata/filters/snowflaketags`,
    params,
    10000,
    TableRkFilterResponseSchema
  );

export const getSchemaFilters = (params: { prefix?: string; page?: number; size?: number }) =>
  api
    .get<TableRkFilterResponse>(
      `/tables/tabledata/filters/rks/schema`,
      params,
      10000,
      TableRkFilterResponseSchema
    )
    .then(response => {
      // Format the response to extract just the schema from the path
      if (response && response.items) {
        response.items = response.items.map(item => {
          const path = item.value;
          // Extract schema from snowflake://{db}.{schema}/{table}
          let schema = path;
          if (path.includes(".")) {
            const parts = path.split(".");
            if (parts.length > 1) {
              const schemaWithTable = parts[1];
              schema = schemaWithTable.split("/")[0];
            }
          }
          return {
            ...item,
            label: schema,
          };
        });
      }
      return response;
    });

export const getDatabaseFilters = (params: { prefix?: string; page?: number; size?: number }) =>
  api
    .get<TableRkFilterResponse>(
      `/tables/tabledata/filters/rks/db`,
      params,
      10000,
      TableRkFilterResponseSchema
    )
    .then(response => {
      // Format the response to extract just the database from the path
      if (response && response.items) {
        response.items = response.items.map(item => {
          const path = item.value;
          // Extract database from snowflake://{db}.{schema}/{table}
          let db = path;
          if (path.includes("://")) {
            const parts = path.split("://");
            if (parts.length > 1 && parts[1].includes(".")) {
              db = parts[1].split(".")[0];
            }
          }
          return {
            ...item,
            label: db,
          };
        });
      }
      return response;
    });

export const getTableQueries = (tableId: string, params: TableQueryFilters) => {
  return api.get<TableQueriesResponse>(
    `/tables/queries/${tableId}`,
    params,
    0,
    TableQueriesResponseSchema
  );
};

export const getTableQueriesFilters = (
  tableId: string,
  params: { startDate: string; endDate: string }
) => {
  return api.get<TableQueryFiltersResponse>(
    `/tables/queries/filters/${tableId}`,
    params,
    0,
    TableQueryFiltersResponseSchema
  );
};

export const createDatamate = (data: Teammate) =>
  api.post<TeammateCreateResponse>("/datamates", data, TeammateCreateResponseSchema);

export const getDatamates = () =>
  api.get<{ datamates: Teammate[] }>(
    "/datamates/",
    {},
    0,
    z.object({ datamates: TeammateSchema.array() })
  );

export const getDatamatesWithStats = (params?: { start_date: string; end_date: string }) => {
  return api.get<{ datamates: Teammate[]; usage: Usage }>(
    "/datamates/summary",
    params,
    0,
    z.object({ datamates: TeammateSchema.array(), usage: UsageSchema })
  );
};

export const getDatamateByIdWithStats = (
  id: string | undefined,
  params: {
    start_date: string;
    end_date: string;
  }
) => {
  if (!id) return;
  return api.get<GetDatamateWithStatsResponse>(
    `/datamates/${id}/summary`,
    params,
    0,
    GetDatamateWithStatsResponseSchema
  );
};

// edit datamate
export const editDatamate = (id: string, data: Teammate) =>
  api.update<TeammateUpdateResponse>(`/datamates/${id}`, data, TeammateUpdateResponseSchema);

// Who a workspace is shared with. Owner only — a 403 for anyone else, so
// callers must gate on ownership before asking.
export const getDatamateShares = (id: string) =>
  api.get<DatamateShares>(`/datamates/${id}/shares`, {}, 0, DatamateSharesSchema);

// Sets the sharing mode AND the whole grant set in one call. Deliberately not
// two requests: setting `restricted` without grants would leave a workspace
// that claims to be shared and is visible to nobody but its owner, and the API
// rejects that combination.
export const setDatamateShares = (
  id: string,
  privacy: PrivacyLevel,
  userIds: number[],
  teamIds: number[]
) =>
  api.put<DatamateShares, { privacy: PrivacyLevel; user_ids: number[]; team_ids: number[] }>(
    `/datamates/${id}/shares`,
    { privacy, user_ids: userIds, team_ids: teamIds },
    DatamateSharesSchema
  );

// delete datamate
export const deleteDatamate = (id: string) =>
  api.delete<StandardResponse>(`/datamates/${id}`, {}, StandardResponseSchema);

// get datamate integrations
export const getDatamateIntegrations = () =>
  api.get<z.infer<typeof DatamateIntegrationSchema>[]>(
    `/datamate_integrations/`,
    {},
    0,
    z.array(DatamateIntegrationSchema)
  );

// crud /connections
export const createConnection = (data: Connection) =>
  api.post<Connection>("/connections", data, ConnectionSchema);

// The caller's own connections plus those shared with them (shared ones carry
// no secrets and `is_owner: false`).
export const getConnections = () =>
  api.get<{ connections: Connection[] }>(
    "/connections/",
    { include_shared: true },
    0,
    z.object({ connections: ConnectionSchema.array() })
  );

// Who a connection is shared with. Owner only — a 404 for anyone else.
export const getConnectionShares = (id: string) =>
  api.get<ConnectionShares>(`/connections/${id}/shares`, {}, 0, ConnectionSharesSchema);

// Sets the sharing mode and the whole grant set in one call, as for a workspace.
export const setConnectionShares = (id: string, payload: DatamateSharesPayload) =>
  api.put<ConnectionShares, Required<DatamateSharesPayload>>(
    `/connections/${id}/shares`,
    {
      privacy: payload.privacy,
      user_ids: payload.user_ids ?? [],
      team_ids: payload.team_ids ?? [],
    },
    ConnectionSharesSchema
  );

export const deleteConnection = (id: string) =>
  api.delete<StandardResponse>(`/connections/${id}`, {}, StandardResponseSchema);

export const updateConnection = (id: string, data: Connection) =>
  api.update<Connection>(`/connections/${id}`, data, ConnectionSchema);

export const testConnection = (data: Partial<Connection>) =>
  api.post<{ success: boolean; message?: string | null; status?: ConnectionTestStatus }>(
    "/connections/test",
    data,
    z.object({
      success: z.boolean(),
      message: z.string().nullish(),
      status: z.nativeEnum(ConnectionTestStatus).optional().catch(undefined),
    })
  );

// Live-tests a saved connection and returns the result; nothing is stored.
// Allowed for its owner and anyone it is shared with.
const SavedConnectionTestSchema = ConnectionTestResultSchema.extend({
  connection_id: z.coerce.string(),
});

export const testSavedConnection = (id: string) =>
  api.post<z.infer<typeof SavedConnectionTestSchema>>(
    `/connections/${id}/test`,
    {},
    SavedConnectionTestSchema
  );

export const getCustomIntegrationsPaginated = (params: { page: number; size: number }) =>
  api.get<Page<CommonIntegrationInstance>>(
    `/datamate_integrations/custom`,
    params,
    0,
    pageSchema(CommonIntegrationInstanceSchema)
  );

export const getAssistAggregations = (
  aggregateBy: string,
  startDate: Date,
  endDate: Date,
  datamateId: string | undefined
) => {
  if (!datamateId) return [];
  return api.get<AssistAggregationsResponse>(
    `/datamates/audit/${datamateId}/aggregations`,
    {
      aggregation_type: "assist",
      aggregateBy,
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
    },
    0,
    AssistAggregationsResponseSchema
  );
};

export const getGuardrailAggregations = (
  aggregateBy: string,
  startDate: Date,
  endDate: Date,
  datamateId: string | undefined
) => {
  if (!datamateId) {
    return [];
  }
  return api.get<GuardrailAggregationsResponse>(
    `/datamates/audit/${datamateId}/aggregations`,
    {
      aggregation_type: "guard_rail",
      aggregateBy,
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
    },
    0,
    GuardrailAggregationsResponseSchema
  );
};

export const createNewCustomIntegration = (instance: Partial<CommonIntegrationInstance>) =>
  api.post<CommonIntegrationInstance>(
    `/datamate_integrations/custom`,
    instance,
    CommonIntegrationInstanceSchema
  );

// TODO: figure out why PUT doesn't work
export const updateCustomIntegration = (id: string, instance: Partial<CommonIntegrationInstance>) =>
  api.post<CommonIntegrationInstance>(
    `/datamate_integrations/custom/${id}`,
    instance,
    CommonIntegrationInstanceSchema
  );

export const deleteCustomIntegration = (id: string) =>
  api.delete<StandardResponse>(`/datamate_integrations/custom/${id}`, {}, StandardResponseSchema);

export const createJiraTicket = (
  opportunityId: string,
  queryId: string | null = null,
  createJiraTicket?: boolean,
  action?: string
) => {
  return api.post<{
    url: string;
    issue_key: string;
    content?: string;
  }>(
    `/opportunities/jira`,
    {
      opportunity_id: opportunityId,
      query_id: queryId,
      create_jira_ticket: createJiraTicket,
      action,
    },
    z.object({
      url: z.string().url(),
      issue_key: z.string(),
      content: z.string().optional(),
    })
  );
};

// Knowledge Catalog
export const createKnowledgeSource = (formData: Partial<KnowledgeCatalogItem>) =>
  api.post<KnowledgeCatalogItem>("/knowledge_bases", formData, KnowledgeCatalogItemSchema);

export const getKnowledgeBaseTemplates = () =>
  api.get<KnowledgeCatalogItem[]>(
    "/knowledge_bases/templates",
    {},
    0,
    KnowledgeCatalogItemSchema.array()
  );

export const getKnowledgeBaseSources = () =>
  api.get<KnowledgeCatalogItem[]>("/knowledge_bases/", {}, 0, KnowledgeCatalogItemSchema.array());

export const getKnowledgeSourceById = (id: string) =>
  api.get<KnowledgeCatalogItem>(`/knowledge_bases/${id}`, {}, 0, KnowledgeCatalogItemSchema);

export const forkKnowledgeSource = (template_name: string, new_name: string) =>
  api.post<KnowledgeCatalogItem>(
    `/knowledge_bases/fork-template`,
    { template_name: template_name, new_name: new_name },
    KnowledgeCatalogItemSchema
  );

/**
 * Fork a knowledge base template with V2 processing (S3 upload + Celery task)
 * Used when enableKnowledgeEngine feature flag is enabled
 * @param template_name - Name of the template to fork
 * @param new_name - Name for the new knowledge base
 * @returns Response with KB and document details, processing status
 */
export const forkKnowledgeSourceV2 = (template_name: string, new_name: string) =>
  api.post<KnowledgeDocumentUploadResponse>(
    `/knowledge_bases/v2/fork-template`,
    { template_name: template_name, new_name: new_name },
    KnowledgeDocumentUploadResponseSchema
  );

export const deleteKnowledgeSource = (id: number) =>
  api.delete<StandardResponse>(`/knowledge_bases/${id}`, {}, StandardResponseSchema);

export const getDbtModelsFilters = () =>
  api.get<DbtModelFiltersResponse>(`/dbt/models/filters`, {}, 0, DbtModelFiltersResponseSchema, {
    // setting group id as undefined explicitly to get all available integrations to show in add access rule dialog
    headers: { "x-user-group-id": undefined },
  });

export const getDbtModels = (params: GetDbtModelsParams) =>
  api.get<DbtModelsResponse>(`/dbt/models/`, params, 0, DbtModelsResponseSchema);

export const getDbtModelByRk = (data: DbtModelByRkRequest) =>
  api.post<DbtModelByRkResponse>(`/dbt/models/items`, data, DbtModelByRkResponseSchema);

// DBT Model Schema and Lineage APIs
export const postColumnsDBT = (data: PostColumnsDBTParams) =>
  api.get<TableSchemaResponse>(`/dbt/models/schema`, data, 0, TableSchemaResponseSchema);

export const postConnectedColumnsDBT = (data: PostConnectedColumnsDBTParams) =>
  api.post<ColumnLineageResponse>(`/dbt/models/connected`, data, ColumnLineageResponseSchema);

export const upstreamTablesDBT = (fqn: string) =>
  api.post<ConnectedTablesResponse>(`/dbt/models/upstream`, { fqn }, ConnectedTablesResponseSchema);

export const downstreamTablesDBT = (fqn: string) =>
  api.post<ConnectedTablesResponse>(
    `/dbt/models/downstream`,
    { fqn },
    ConnectedTablesResponseSchema
  );

export const startingNodeDBT = (params: StartingNodeDBTParams) =>
  api.get<TableNodeResponse>(`/dbt/models/terminal`, params, 0, TableNodeResponseSchema);

export const getSQLLineage = (params: GetSQLLineageParams) =>
  api.post<SQLLineageResponse>(`/dbt/models/sql_lineage`, params, SQLLineageResponseSchema);

// Integration APIs
export const getIntegrations = () =>
  api.get<IntegrationListResponse>(
    `/dbt/v1/project_integrations`,
    {},
    0,
    IntegrationListResponseSchema
  );

export const getIntegrationByID = (integrationId: string | number, integrationEnv: string) =>
  api.get<IntegrationDetails>(
    `/dbt/v1/project_integrations/${integrationId}/${integrationEnv}`,
    {},
    0,
    IntegrationDetailsSchema
  );

export const postIntegration = (data: PostIntegrationRequest) =>
  api.post<PostIntegrationResponse>(
    `/dbt/v1/project_integration`,
    data,
    PostIntegrationResponseSchema
  );

export const deleteDBTIntegration = (id: string | number) =>
  api.delete<StandardResponse>(`/dbt/v1/project_integration/${id}`, {}, StandardResponseSchema);

export const getKnowledgeSourceByPublicId = (id: string) =>
  api.get<KnowledgeCatalogItem>(`/knowledge_bases/public/${id}`, {}, 0, KnowledgeCatalogItemSchema);

export const updateKnowledgeSource = (id: string, formData: Partial<KnowledgeCatalogItem>) =>
  api.update<KnowledgeCatalogItem>(`/knowledge_bases/${id}`, formData, KnowledgeCatalogItemSchema);

const KnowledgeBasePrivateLinkSettingSchema = z.object({
  config: z.object({
    enabled: z.boolean(),
    port: z.number().optional(),
  }),
});

export const getKnowledgeBasePrivateLinkSetting = () =>
  api.get<z.infer<typeof KnowledgeBasePrivateLinkSettingSchema>>(
    "/knowledge_bases/private-link-setting",
    {},
    0,
    KnowledgeBasePrivateLinkSettingSchema
  );

// Knowledge Base V2 - Document Upload & Versioning APIs

/**
 * Upload a document and create a new knowledge base (V2)
 * Supports both file upload and markdown content
 * @param formData - FormData containing name, description, privacy, chunking_strategy, icon, and either file or markdown_content
 * @returns Response with KB and document details, processing status
 */
export const uploadKnowledgeBaseWithDocument = (formData: FormData) =>
  api.post<KnowledgeDocumentUploadResponse>(
    "/knowledge_bases/v2/upload",
    formData,
    KnowledgeDocumentUploadResponseSchema,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

/**
 * Upload a new document version to an existing knowledge base (V2)
 * Supports both file upload and markdown content
 * @param kbId - Knowledge base ID
 * @param formData - FormData containing chunking_strategy and either file or markdown_content
 * @returns Response with document details and processing status
 */
export const uploadDocumentToKnowledgeBase = (kbId: number, formData: FormData) =>
  api.post<KnowledgeDocumentUploadToExistingResponse>(
    `/knowledge_bases/v2/${kbId}/upload`,
    formData,
    KnowledgeDocumentUploadToExistingResponseSchema,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

/**
 * Get knowledge base details with current document processing status (V2)
 * Returns KB info along with active document metadata and user-friendly status messages
 * @param kbId - Knowledge base ID
 * @returns Detailed KB response with document status
 */
export const getKnowledgeBaseWithDocumentStatus = (kbId: number) =>
  api.get<KnowledgeBaseDetailResponse>(
    `/knowledge_bases/v2/${kbId}`,
    {},
    0,
    KnowledgeBaseDetailResponseSchema
  );

/**
 * Get all document versions/history for a knowledge base (V2)
 * Returns list of all documents ordered by version descending (newest first)
 * @param kbId - Knowledge base ID
 * @returns Array of document history items
 */
export const getKnowledgeBaseDocumentHistory = (kbId: number) =>
  api.get<KnowledgeDocumentHistoryItem[]>(
    `/knowledge_bases/v2/${kbId}/documents`,
    {},
    0,
    z.array(KnowledgeDocumentHistoryItemSchema)
  );

/**
 * Get document chunks for a specific knowledge base document (V2)
 * Returns all chunks for the specified document in order
 * @param kbId - Knowledge base ID
 * @param docId - Document ID
 * @returns Document chunks response with metadata and chunks array
 */
export const getKnowledgeBaseDocumentChunks = (kbId: number, docId: number) =>
  api.get<DocumentChunksResponse>(
    `/knowledge_bases/v2/${kbId}/documents/${docId}/chunks`,
    {},
    0,
    DocumentChunksResponseSchema
  );

/**
 * Retry processing for a failed document (V2)
 * Resets the document status and queues it for re-processing
 * @param kbId - Knowledge base ID
 * @param docId - Document ID
 * @returns Response with document details and new processing status
 */
export const retryKnowledgeDocumentProcessing = (kbId: number, docId: number) =>
  api.post<KnowledgeDocumentUploadResponse>(
    `/knowledge_bases/v2/${kbId}/documents/${docId}/retry`,
    {},
    KnowledgeDocumentUploadResponseSchema
  );

/**
 * Submit feedback for a knowledge base chunk/learning
 * @param kbId - Knowledge base ID
 * @param chunkId - Chunk ID
 * @param feedback - 'helpful' or 'not_helpful'
 * @param comment - Optional comment
 */
export const submitKnowledgeFeedback = (
  kbId: number,
  chunkId: number,
  feedback: "helpful" | "not_helpful",
  comment?: string
) =>
  api.post<StandardResponse>(
    `/knowledge_bases/v2/${kbId}/feedback`,
    { chunk_id: chunkId, feedback, comment },
    StandardResponseSchema
  );

// Global config query tags schema
const GlobalConfigQueryTagsResponseSchema = z.array(z.string());

export const getGlobalConfigQueryTags = () =>
  api.get<z.infer<typeof GlobalConfigQueryTagsResponseSchema>>(
    "/global_config/",
    {},
    0,
    GlobalConfigQueryTagsResponseSchema
  );

export const getBiReportFilters = () =>
  api.get<BiReportFilters>("business_intelligence/filters", {}, 0, BiReportFiltersSchema);

// BI Dashboards (Reports) API Functions
export const getBiDashboardsPaginated = (params?: BiDashboardsQueryParams) =>
  api.get<BiReportsPaginatedResponse>(
    "business_intelligence/dashboards",
    params || {},
    0,
    BiReportsPaginatedResponseSchema
  );

export const getBiDashboardsSummary = (params?: {
  start_date?: string;
  end_date?: string;
  site_name?: string[];
  instance_name?: string[];
}) =>
  api.get<BiDashboardSummary>(
    "business_intelligence/summary",
    params || {},
    0,
    BiDashboardSummarySchema
  );

export const getBiDashboardById = (dashboardId: string) => {
  const decodedId = decodeURIComponent(dashboardId);
  return api.get<BiReport>(
    "business_intelligence/dashboards/get_by_id",
    { dashboard_id: decodedId },
    0,
    BiReportSchema
  );
};

// User Reports API Functions
export const getUserReports = (params?: GetUserReportsParams) =>
  api.get<{ items: BiUserReport[]; total: number; pages: number; page: number; size: number }>(
    "business_intelligence/users",
    params || {},
    0,
    z.object({
      items: z.array(BiUserReportSchema),
      total: z.number(),
      pages: z.number(),
      page: z.number(),
      size: z.number(),
    })
  );

export const getUserSummary = () =>
  api.get<BiUserSummary[]>(
    "business_intelligence/users/summary",
    {},
    0,
    z.array(BiUserSummarySchema)
  );

export const getUserReportsFilters = () =>
  api.get<BiUserReportsFilters>(
    "business_intelligence/users/filters",
    {},
    0,
    BiUserReportsFiltersSchema
  );

export const getUserByRk = (userRk: string) => {
  return api.get<BiUserReport>(
    "business_intelligence/users/get_by_rk",
    { user_rk: userRk },
    0,
    BiUserReportSchema
  );
};

// BI Reports Dashboard Activity API Functions
export const getDashboardAccessActivity = (
  dashboardId: string,
  params: { page?: number; page_size?: number; sort_by?: string; sort?: string } = {}
) =>
  api.get<DashboardAccessActivityResponse>(
    `/business_intelligence/dashboards/access_activity`,
    {
      dashboard_id: dashboardId,
      ...params,
    },
    0,
    DashboardAccessActivityResponseSchema
  );

export const getDashboardRefreshActivity = (
  dashboardId: string,
  params: { page?: number; page_size?: number; sort_by?: string; sort?: string } = {}
) =>
  api.get<DashboardRefreshActivityResponse>(
    `/business_intelligence/dashboards/refresh_activity`,
    {
      dashboard_id: dashboardId,
      ...params,
    },
    0,
    DashboardRefreshActivityResponseSchema
  );

export const getTableauProjects = (credentials: {
  url: string;
  site_name?: string;
  // PAT becomes optional when ``datastore_id`` is provided — backend then
  // falls back to credentials stored on that data store. Used by the
  // "Add database" modal on the data store detail page.
  pat_name?: string;
  pat_secret?: string;
  datastore_id?: number;
  // Sibling site name (in the same instance as ``datastore_id``) whose PAT
  // should be reused. Sent alongside ``datastore_id`` (page context) so the
  // backend resolves the sibling via instance_id + site_name.
  source_site_name?: string;
}) =>
  api.post<TableauProject[]>(
    `/business_intelligence/tableau/projects`,
    credentials,
    z.array(TableauProjectSchema)
  );

export const getTeamSpendGraphAndSummaryData = (params: TeamSpendQueryParams) => {
  return api.get<TeamSpendGraphAndSummary>(
    "/teams/cost/graph",
    params,
    0,
    teamSpendGraphAndSummary
  );
};

export const getTeamSpendTotalSpendsData = (params: TeamSpendQueryParams) => {
  return api.get<TeamSpendTotalSpends>(
    "/teams/cost/period-comparison",
    params,
    0,
    teamSpendTotalSpendsSchema
  );
};

export const getTeamSpendFilters = (params: TeamSpendQueryParams) => {
  return api.get<TeamSpendFilters>("/teams/filters", params, 0, teamSpendFilters);
};

// Memory Management APIs
export const listMemories = (
  params: {
    page?: number;
    page_size?: number;
    datamate_id?: string[];
    /**
     * Opt in to memory written by a client the API hides by default.
     * `altimate-code` blocks are excluded from list/search unless named here,
     * so they do not leak into Datamate sessions as conversational context.
     */
    include_sources?: string[];
  } = {}
) => api.get<Memory[]>("/datamates/memory/list", params, 0, z.array(MemorySchema));

export const searchMemories = (query: string, params: { datamate_id?: string[] } = {}) =>
  api.get<Memory[]>("/datamates/memory/search", { query, ...params }, 0, z.array(MemorySchema));

export const updateMemory = (
  id: string,
  data: { memory: string; metadata: Record<string, unknown> }
) => api.update<MemoryResponse>(`/datamates/memory/${id}`, data, MemoryResponseSchema);

export const deleteMemory = (id: string) =>
  api.delete<MemoryResponse>(`/datamates/memory/${id}`, {}, MemoryResponseSchema);

export const bulkDeleteMemories = (ids: string[]) =>
  api.delete<MemoryResponse>("/datamates/memory/batch", { data: ids }, MemoryResponseSchema);

export const getAnnualizedCostBreakdown = (params: Record<string, string | string[] | number>) => {
  return api.get<CostBreakdown>(
    `/tables/tabledata/costs-breakdown`,
    params,
    0,
    CostBreakdownSchema
  );
};

// Accounts Cost APIs
export const getAccountsList = () => {
  return api.get<AccountsList>("/account_costs/accounts", {}, 0, accountsListSchema);
};

export const getAccountsTotalCost = (params: AccountsCostQueryParams) => {
  return api.get<TotalCost>("/account_costs/total", params, 0, totalCostSchema);
};

export const getAccountsChartData = (params: AccountsCostQueryParams) => {
  return api.get<ChartDataList>("/account_costs/chart", params, 0, chartDataListSchema);
};

export const getAccountsTableData = (params: AccountsCostQueryParams) => {
  return api.get<TableDataList>("/account_costs/table", params, 0, tableDataListSchema);
};

// User Management APIs
export const getUserList = (params: {
  company: string;
  with_service_user?: boolean;
  allUsers?: boolean;
  page?: number;
  pageSize?: number;
  search_term?: string;
  include_deleted?: boolean;
  sortColumn?: string;
  sortOrder?: string;
}) =>
  api.get<UserListResponse>(
    "/users/",
    {
      company: params.company,
      with_service_user: params.with_service_user ?? false,
      all_users: params.allUsers ?? false,
      page: params.page ?? 0,
      size: params.pageSize ?? 20000,
      search_term: params.search_term ?? "",
      include_deleted: params.include_deleted ?? true,
      sortColumn: params.sortColumn ?? "created_at",
      sortOrder: params.sortOrder ?? "desc",
    },
    10000,
    UserListResponseSchema
  );

export const addNewUser = (params: { email: string; company: string; role: string }) =>
  api.post<InviteUserResponse>("/users/invite", params, InviteUserResponseSchema);

export const updateUser = (params: {
  _id: string;
  is_active?: boolean;
  is_verified?: boolean;
  attributes?: { deactivated?: boolean };
}) => api.update<UpdateUserResponse>(`/users/${params._id}`, params, UpdateUserResponseSchema);

export const updateUserRole = (params: { user_uuid: string; role_title: string }) =>
  api.post<UpdateUserRoleResponse>("/users/role_edit/", params, UpdateUserRoleResponseSchema);

export const resolveRoleConflict = (params: {
  user_uuid: string;
  action: "accept_sso" | "dismiss";
}) => api.post<StandardResponse>("/users/resolve_role_conflict", params, StandardResponseSchema);

export const deleteUser = (params: { uuid: string; company: string }) =>
  api.delete<DeleteUserResponse>(
    `/users/${params.uuid}?company=${params.company}`,
    {},
    DeleteUserResponseSchema
  );

// Improve Prompt API
export const getImprovedPrompt = (userPrompt: string, agentName: string) => {
  return api.post<EnhancedPromptResponse>(
    "/studio/prompt/enhance",
    {
      user_prompt: userPrompt,
      agent_name: agentName,
    },
    EnhancedPromptResponseSchema
  );
};

// Prompt Library APIs
export const getPrompts = (params: {
  type?: string[];
  teams?: string[];
  owners?: string[];
  tags?: string[];
  sort?: "created_at" | "last_used_at" | "updated_at";
  search?: string;
  page?: number;
  pageSize?: number;
  integration_type?: IntegrationType;
}) => {
  return api.get<Page<PromptResponse>>("/prompts", params, 0, pageSchema(PromptResponseSchema));
};

export const createPrompt = (data: CreateOrUpdatePromptRequest) => {
  return api.post<PromptResponse, CreateOrUpdatePromptRequest>(
    "/prompts",
    data,
    PromptResponseSchema
  );
};

export const updatePrompt = (id: string, data: CreateOrUpdatePromptRequest) => {
  return api.post<PromptResponse, CreateOrUpdatePromptRequest>(
    `/prompts/${id}`,
    data,
    PromptResponseSchema
  );
};

export const deletePrompt = (id: string) => {
  return api.delete<StandardResponse>(`/prompts/${id}`, {}, StandardResponseSchema);
};

export const getPromptTags = (integrationType?: IntegrationType) => {
  return api.get<PromptLibraryTagsResponse>(
    "/prompts/tags",
    { integration_type: integrationType },
    0,
    PromptLibraryTagsResponseSchema
  );
};

export const getStarterQuestions = ({
  tenantMax,
  publicMax,
  integrationType,
  userView: user_view,
}: {
  tenantMax: number;
  publicMax: number;
  integrationType: IntegrationType;
  userView?: UserView;
}) =>
  api.get<StarterQuestionsResponse>(
    "/prompts/starter",
    {
      tenant_max: tenantMax,
      public_max: publicMax,
      integration_type: integrationType,
      ...user_view,
    },
    0,
    StarterQuestionsResponseSchema
  );

// Playground Session APIs
export const getPlaygroundSessions = (params?: {
  search_text?: string;
  page?: number;
  page_size?: number;
  status?: SessionLifecycleListFilter;
  /** Only chats that run in this workspace. */
  workspace_id?: string;
}) => {
  return api.get<PlaygroundAllSessionResponse>(
    "/studio/sessions",
    params || {},
    0,
    playgroundAllSessionResponseSchema
  );
};

export const getPlaygroundSessionDetails = (
  sessionId: string,
  config?: { preserveErrorStatus: boolean }
) => {
  return api.get<PlaygroundSessionDetailsResponse>(
    `/studio/sessions/${sessionId}`,
    {},
    0,
    playgroundSessionDetailsResponseSchema,
    config
  );
};

export const updatePlaygroundSessionStatus = (
  sessionId: string,
  status: SessionLifecycleStatus
) => {
  return api.update<SessionStatusUpdateResponse>(
    `/studio/sessions/${sessionId}`,
    { status },
    sessionStatusUpdateResponseSchema
  );
};

export const renamePlaygroundSession = (sessionId: string, name: string) => {
  return api.update<SessionRenameResponse>(
    `/studio/sessions/${sessionId}/name`,
    { name },
    sessionRenameResponseSchema
  );
};

export const starPlaygroundSession = (sessionId: string, starred: boolean) => {
  return api.update<SessionStarResponse>(
    `/studio/sessions/${sessionId}/star`,
    { starred },
    sessionStarResponseSchema
  );
};

// ========== Share Links (US-003) ==========
export const createShareLink = (data: ShareLinkCreateRequest) =>
  api.post<ShareLinkResponse>("/playground/share", data, shareLinkResponseSchema);

export const getShareLinks = (sessionId: string) =>
  api.get<ShareLinkListResponse>(
    `/playground/share/session/${sessionId}`,
    {},
    0,
    shareLinkListResponseSchema
  );

export const deleteShareLink = (shareId: string) =>
  api.delete<StandardResponse>(`/playground/share/${shareId}`, {}, StandardResponseSchema);

// Get shared session - auth is optional (backend handles both cases)
export const getSharedSession = (shareId: string) =>
  api.get<SharedSessionResponse>(
    `/playground/share/shared/${shareId}`,
    {},
    0,
    sharedSessionResponseSchema
  );

// Email a Studio session report to a mailing list. AI-6343: surfaced as
// the Email tab in `ShareDialog`. Backend route is the one used by the
// legacy chatbotV2 EmailDialog (`studio/session_to_email_mailing_list`).
export const sendSessionToEmail = (data: {
  session_id: string;
  recipient_type: "specific_emails" | "all_altimate_users" | "team";
  emails?: string[];
  team_ids?: number[];
}) =>
  api.post<SessionEmailResponse>(
    "/studio/session_to_email_mailing_list",
    data,
    SessionEmailResponseSchema
  );

// ========== Studio → User opportunities (enableStudioOpportunities, AI-7498) ==========

// Publish a Studio-drafted opportunity. The agent proposes the finding via the
// `opportunity_draft` stream channel; this is the sole publish path, called on
// Submit from OpportunityDraftForm. A gate failure returns HTTP 422 with
// `{ detail: { errors: [{ code, message }] } }`, which the form maps to inline
// field errors via `parseStudioOpportunitySubmitErrors`.
export const submitStudioOpportunity = (data: {
  session_id: string;
  turn_uuid?: string;
  finding: OpportunityFinding;
  /** True when re-submitting an already-published opportunity (Edit flow). */
  allow_update?: boolean;
}) =>
  api.post<SubmitStudioOpportunityResponse>(
    "/studio/opportunities/submit",
    data,
    submitStudioOpportunityResponseSchema
  );

// Pre-submit smoke test for a Studio-drafted opportunity (AI-7498). Runs the
// deterministic publish gates plus a numbers/description plausibility check
// without any side effects. A 200 unlocks Submit; a failed check returns HTTP
// 422 with the same `{ detail: { errors } }` shape as submit, parsed by
// `parseStudioOpportunitySubmitErrors`.
export const validateStudioOpportunity = (data: {
  session_id: string;
  turn_uuid?: string;
  finding: OpportunityFinding;
}) =>
  api.post<ValidateStudioOpportunityResponse>(
    "/studio/opportunities/validate",
    data,
    validateStudioOpportunityResponseSchema
  );

// Reject an in-flight Studio DRAFT (AI-8316) so a half-finished opportunity
// stops re-rendering on every session load. Never touches a published
// opportunity — that is `DELETE /studio/opportunities`.
export const discardStudioOpportunity = (data: {
  session_id: string;
  turn_uuid?: string;
  resource_name?: string;
}) =>
  api.post<DiscardStudioOpportunityResponse>(
    "/studio/opportunities/discard",
    data,
    discardStudioOpportunityResponseSchema
  );

/**
 * Extract the per-field gate errors from a failed `submitStudioOpportunity`
 * call. Returns the `errors` array on a well-formed 422, otherwise null (so the
 * caller can fall back to a generic error toast).
 *
 * The global response interceptor rejects with `error.response.data.detail`
 * rather than the `AxiosError` itself, so a 422 arrives here as the bare
 * `{ errors: [...] }` detail object. The wrapped body and a raw `AxiosError`
 * are both still accepted, so the parser survives a change to that interceptor.
 */
export const parseStudioOpportunitySubmitErrors = (
  error: unknown
): StudioOpportunityFieldError[] | null => {
  const detail = studioOpportunityErrorDetailSchema.safeParse(error);
  if (detail.success) {
    return detail.data.errors;
  }
  const body = axios.isAxiosError(error) ? error.response?.data : error;
  const wrapped = submitStudioOpportunityErrorSchema.safeParse(body);
  return wrapped.success ? wrapped.data.detail.errors : null;
};

// ========== Slack Webhook Sharing (US-004) ==========

export const shareToSlack = (data: ShareToSlackRequest) =>
  api.post<ShareToSlackResponse>("/playground/share/slack", data, shareToSlackResponseSchema);

// Get all subscriptions for the user
export const getCostAnalysisSubscriptions = (params: { instance_id?: string }) =>
  api.get<CostAnalysisSubscriptionsListResponse>(
    "/summary/v2/cost-analysis/subscriptions",
    params,
    0,
    costAnalysisSubscriptionsListResponseSchema
  );

// Delete a specific subscription by ID
export const deleteCostAnalysisSubscriptionById = (subscriptionId: string) =>
  api.delete<CostAnalysisUnsubscribeResponse>(
    `/summary/v2/cost-analysis/subscriptions/${subscriptionId}`,
    {},
    costAnalysisUnsubscribeResponseSchema
  );

// SSO Configuration API
// The endpoint reports failures as HTTP 200 with {status: "ERROR"} — it never
// raises, so the body's status field is the only failure signal. Callers must
// check it; axios resolves any 2xx.
export const ConfigureSSOResponseSchema = z.object({
  status: z.string(),
  message: z.string().nullable().optional(),
});
export type ConfigureSSOResponse = z.infer<typeof ConfigureSSOResponseSchema>;

export const configureSSO = (
  data: {
    client_id: string;
    client_secret: string;
    oidc_url: string;
  },
  provider: (typeof SSOProvider)[keyof typeof SSOProvider]
) =>
  api.post<ConfigureSSOResponse>(
    `/supertokens/configure/${provider}`,
    data,
    ConfigureSSOResponseSchema
  );

export const getCompanyInfo = () =>
  api.get<CompanyInfoResponse>("/auth/company-info", {}, 0, CompanyInfoResponseSchema);

// Streamlit, notebook workload APIs
export const getStreamlitNotebookJobs = (params: Record<string, string | number | undefined>) => {
  return api.get<Page<StreamlitNotebookJobListResponse>>(
    "/query/v2/workloads",
    params,
    0,
    pageSchema(CommonJobListSchema)
  );
};

export const getStreamlitNotebookJobsGraphData = (
  params: Record<string, string | number | undefined>
) => {
  return api.get<StreamlitNotebookJobsGraphDataResponse>(
    "/query/v2/workloads/graph_data",
    params,
    0,
    StreamlitNotebookJobsGraphDataResponseSchema
  );
};

export const getStreamlitNotebookJobsGraphSummary = (
  params: Record<string, string | number | undefined>
) => {
  return api.get<CommonGraphSummaryResponse>(
    "/query/v2/workloads/graph_summary",
    params,
    0,
    CommonGraphSummarySchema
  );
};

export const getStreamlitNotebookJobRunsList = (
  workload_hash: string,
  params: Record<string, string | number | undefined>
) => {
  return api.get<Page<StreamlitNotebookJobRunsListResponse>>(
    `/query/v2/workloads/jobs/${workload_hash}`,
    params,
    0,
    pageSchema(StreamlitNotebookRunsListSchema)
  );
};

export const getStreamlitNotebookJobRunsSummary = (
  workload_hash: string,
  params: Record<string, string | number | undefined>
) => {
  return api.get<StreamlitNotebookJobRunsSummaryDataResponse>(
    `/query/v2/workloads/summary/${workload_hash}`,
    params,
    0,
    CommonRunsSummarySchema
  );
};

export const getStreamlitNotebookJobChildQueries = (
  session_id: string,
  params: Record<string, string | number | undefined>
) => {
  return api.get<Page<StreamlitNotebookChildQueriesListResponse>>(
    `/query/v2/workloads/child_queries/${session_id}`,
    params,
    0,
    pageSchema(StreamlitNotebookChildQueriesSchema)
  );
};

export const getSpaceDashboard = (dashboardId: number | string) => {
  return api.get<MetabaseDashboard>(
    `/spaces/dashboards/${dashboardId}`,
    {},
    0,
    MetabaseDashboardSchema
  );
};

// Superset embed (AI-7926): mints a guest token scoped to one dashboard the
// tenant owns; the embedded-sdk calls this on mount and before token expiry.
export const getSpacesGuestToken = (embedUuid: string) => {
  return api.post<SpacesGuestTokenResponse>(
    "/spaces/guest-token",
    { embed_uuid: embedUuid },
    SpacesGuestTokenResponseSchema
  );
};

// Spaces APIs (using existing dashboard endpoint)
export const getSpaces = (
  params: SpacesRequest = {
    page: 1,
    page_size: 20,
    sort_by: "updated_at",
    sort_order: "desc",
    archived: false,
  }
) => {
  // Remove undefined/null keys so we don't send `key=undefined`
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null)
  ) as SpacesRequest;
  return api.get<SpacesResponse>("/spaces/dashboards", cleanParams, 0, SpacesResponseSchema);
};

// Syncs only the caller's tenant. The fleet-wide /dashboards/sync-all stays
// a backend ops endpoint and is deliberately not reachable from the UI.
export const syncTenantDashboards = () => {
  return api.post<SyncDashboardsResponse>(
    "/spaces/dashboards/sync",
    {},
    SyncDashboardsResponseSchema
  );
};

// Snowflake Compute Pools APIs
//
// `instance_id` is a comma-joined allow-list of instance ids. Callers pass it
// only when a selection exists, so an unfiltered request is unchanged.
type ComputePoolsParams = { start_date?: string; end_date?: string; instance_id?: string };

export const getComputePoolsSummary = (params?: ComputePoolsParams) =>
  api.get<z.infer<typeof computePoolsSummarySchema>>(
    "/compute-pools/summary",
    params || {},
    0,
    computePoolsSummarySchema
  );

export const getComputePoolDailyCost = (poolName: string, params?: ComputePoolsParams) =>
  api.get<z.infer<typeof computePoolDailyCostItemSchema>[]>(
    `/compute-pools/${encodeURIComponent(poolName)}/daily-cost`,
    params || {},
    0,
    z.array(computePoolDailyCostItemSchema)
  );

export const getComputePoolsGraphData = (
  params: ComputePoolsParams & { start_date: string; end_date: string }
) =>
  api.get<z.infer<typeof computePoolsGraphDataSchema>>(
    `/compute-pools/daily-cost`,
    params,
    0,
    computePoolsGraphDataSchema
  );

export const getComputePoolsMetadata = (
  params?: ComputePoolsParams & { snowflakeTags?: string[] }
) =>
  api.get<z.infer<typeof computePoolMetadataItemSchema>[]>(
    "/compute-pools/metadata",
    params || {},
    0,
    z.array(computePoolMetadataItemSchema)
  );

export const getComputePoolOverview = (poolName: string, params?: ComputePoolsParams) =>
  api.get<z.infer<typeof computePoolOverviewSchema>>(
    `/compute-pools/${encodeURIComponent(poolName)}/overview`,
    params || {},
    0,
    computePoolOverviewSchema
  );

export const getComputePoolEntries = (
  poolName: string,
  params?: ComputePoolsParams & { page?: number; size?: number }
) =>
  api.get<Page<ComputePoolEntry>>(
    `/compute-pools/${encodeURIComponent(poolName)}/entries`,
    params || {},
    0,
    pageSchema(computePoolEntrySchema)
  );

// v2. The v1 `/compute-pools/*` endpoints above stay as they are: they serve
// the dashboard that is already in production, and the reshaped
// AI-Services-style surface lands under /v2 so the two can coexist rather than
// needing a lockstep frontend and backend deploy.
export const getComputePoolUtilization = (
  poolName: string,
  params: {
    start_date: string;
    end_date: string;
    aggregation_level?: "hour" | "day";
    instance_id?: string;
  }
) =>
  api.get<z.infer<typeof computePoolUtilizationSchema>>(
    `/compute-pools/v2/${encodeURIComponent(poolName)}/utilization`,
    params,
    0,
    computePoolUtilizationSchema
  );

// Snowflake Contracts APIs
export const getContractsSummary = () =>
  api.get<z.infer<typeof contractsSummarySchema>>(
    "/contracts/summary",
    {},
    0,
    contractsSummarySchema
  );

export const getActiveContracts = () =>
  api
    .get<{
      contracts: z.infer<typeof contractItemSchema>[];
    }>("/contracts/active", {}, 0, z.object({ contracts: z.array(contractItemSchema) }))
    .then(r => r?.contracts);

// Credits Usage APIs
export const getMyUsage = (params: {
  start_date: string;
  end_date: string;
  page?: number;
  size?: number;
}) => {
  return api.get<Page<Transaction>>(
    "/credits/my-usage/table-data",
    params,
    0,
    pageSchema(transactionSchema)
  );
};

export const getMyUsageTrends = (params: { start_date: string; end_date: string }) => {
  return api.get<MyUsageTrendsResponse>(
    "/credits/my-usage/trends",
    params,
    0,
    myUsageTrendsResponseSchema
  );
};

export const getOrgUsageGraphData = (params: { start_date: string; end_date: string }) => {
  return api.get<OrgUsageTrendsResponse>(
    "/credits/org-usage/trends",
    params,
    0,
    orgUsageTrendsResponseSchema
  );
};

export const getOrgUsageTransactions = (params: {
  start_date: string;
  end_date: string;
  page?: number;
  size?: number;
}) => {
  return api.get<Page<AggregatedTask>>(
    "/credits/org-usage/table-data/aggregated-by-task",
    params,
    0,
    pageSchema(aggregatedTaskSchema)
  );
};

export const getOrgUsageTransactionsGroupedByUser = (params: {
  start_date: string;
  end_date: string;
  page?: number;
  size?: number;
}) => {
  return api.get<Page<TransactionGroupedByUser>>(
    "/credits/org-usage/table-data/by-users",
    params,
    0,
    pageSchema(transactionGroupedByUserSchema)
  );
};

export const getPlans = () => {
  return api.get<PlansResponse>("/credits/plans", {}, 0, PlansResponseSchema);
};

export const downloadMyUsageCSV = (params: { start_date: string; end_date: string }) =>
  downloadCSV("/credits/my-usage/table-data", params);

export const downloadOrgUsageCSV = (params: { start_date: string; end_date: string }) =>
  downloadCSV("/credits/org-usage/table-data", params);

export const downloadOrgUsageByUsersCSV = (params: { start_date: string; end_date: string }) =>
  downloadCSV("/credits/org-usage/table-data/by-users", params);

export const getImpersonationDetails = (body: { email: string }) => {
  return api.post<UserLoginOutResponse>("/impersonate", body, userLoginOutSchema);
};

// Warehouse Cost Summary API
export const getWarehouseCostSummary = (params: GetAllWarehousesParams) =>
  api.get<WarehouseCostSummaryResponse>(
    `/warehouse/total_savings/`,
    params,
    0,
    WarehouseCostSummarySchema
  );

// Warehouse Summary Cost Graph API
export const getWarehouseSummaryCostGraph = (params: GetWarehouseSummaryCostGraphParams) =>
  api.get<WarehouseSummaryCostGraphResponse>(
    `/warehouse/cost/v2/`,
    params,
    0,
    WarehouseSummaryCostGraphSchema
  );

// Warehouse APIs
export const getAllWarehouses = (params: GetAllWarehousesParams = {}) => {
  return api.get<WarehouseListResponse>(`/warehouse/`, params, 0, WarehouseListResponseSchema);
};

export const getWarehouseFilters = (params: GetWarehouseFiltersParams = {}) =>
  api.get<WarehouseFiltersResponse>(
    `/warehouse/filters/`,
    params,
    0,
    WarehouseFiltersResponseSchema
  );

// Get Warehouse Details API
export const getWarehouse = (
  instance_id: string,
  warehouse_rk: string,
  params: GetWarehouseParams = {}
) =>
  api.get<WarehouseDetails>(
    `/warehouse/instance/${instance_id}/warehouse_rk/${warehouse_rk}/`,
    params,
    0,
    WarehouseDetailsSchema
  );

// Get Warehouse Auto Savings API V2
export const getWarehouseAutoSavingsDataV2 = (
  instance_id: string,
  warehouse_rk: string,
  params: GetWarehouseAutoSavingsParams = {}
) =>
  api.get<WarehouseAutoSavingsResponse>(
    `/warehouse/auto_savings/v2/instance/${instance_id}/warehouse_rk/${warehouse_rk}`,
    params,
    0,
    WarehouseAutoSavingsResponseSchema
  );

// Get Warehouse Auto Tune Configuration API
export const getWarehouseAutoTuneConfiguration = (instance_id: string, warehouse_rk: string) =>
  api.get<WarehouseAutoTuneConfiguration>(
    `/warehouse/auto-tune-configuration/instance/${instance_id}/warehouse_rk/${warehouse_rk}`,
    {},
    0,
    WarehouseAutoTuneConfigurationSchema
  );

// Set Warehouse Auto Tune Configuration API
export const setWarehouseAutoTuneConfiguration = (
  instance_id: string,
  warehouse_rk: string,
  body: SetWarehouseAutoTuneConfigurationRequest
) =>
  api.post<WarehouseAutoTuneToggleResponse>(
    `/warehouse/auto-tune/toggle-auto-manage?instance_id=${instance_id}&warehouse_rk=${warehouse_rk}`,
    body,
    WarehouseAutoTuneToggleResponseSchema
  );

// Get Warehouse Decision Data API
export const getWarehouseDecisionData = (
  instance_id: string,
  warehouse_rk: string,
  params?: GetWarehouseDecisionDataParams
) =>
  api.get<WarehouseDecisionDataResponse>(
    `/warehouse/decision-chart/instance/${instance_id}/warehouse_rk/${warehouse_rk}`,
    params || {},
    0,
    WarehouseDecisionDataResponseSchema
  );

// Get Warehouse Query Execution Times API
export const getWarehouseQueryExecutionTimes = (
  instance_id: string,
  warehouse_rk: string,
  params?: GetWarehouseQueryExecutionTimesParams
) =>
  api.get<WarehouseQueryExecutionTimesResponse>(
    `/warehouse/query-execution/instance/${instance_id}/warehouse_rk/${warehouse_rk}`,
    params || {},
    0,
    WarehouseQueryExecutionTimesResponseSchema
  );

// Get warehouse auto savings configuration
export const getWarehouseAutoSavings = (instance_id: string, warehouse_rk: string) =>
  api.get<WarehouseAutoSavingsConfigurationResponse>(
    `/warehouse/auto-savings-config/instance/${instance_id}/warehouse_rk/${warehouse_rk}`,
    {},
    0,
    WarehouseAutoSavingsConfigurationResponseSchema
  );

// Toggle warehouse schedules on/off
export const toggleWarehouseSchedules = (instance_id: string, warehouse_name: string) =>
  api.post<ToggleWarehouseSchedulesResponse>(
    `/warehouse/schedules/toggle-schedules/`,
    {
      instance_id,
      warehouse_name,
    },
    ToggleWarehouseSchedulesResponseSchema
  );

// Get Warehouse Auto Savings Audit Events V2 API
export const getWarehouseAutoSavingsAuditEventsV2 = (
  instance_id: string,
  warehouse_rk: string,
  params?: GetWarehouseAuditEventsParams
) =>
  api.get<WarehouseAuditEventsResponse>(
    `/warehouse/audit-events/v2/instance/${instance_id}/warehouse_rk/${warehouse_rk}`,
    params || {},
    0,
    WarehouseAuditEventsResponseSchema
  );

// Get Warehouse Cost API
export const getWarehouseCost = (
  instance_id: string,
  warehouse_rk: string,
  params?: GetWarehouseCostParams
) =>
  api.get<WarehouseCostResponse>(
    `/warehouse/cost/instance/${instance_id}/warehouse_rk/${warehouse_rk}/`,
    params || {},
    0,
    WarehouseCostResponseSchema
  );

// Get Warehouse Cost V2 API
export const getWarehouseCostV2 = (
  instance_id: string,
  warehouse_rk: string,
  params?: GetWarehouseCostParams
) =>
  api.get<WarehouseCostResponse>(
    `/warehouse/cost/instance/${instance_id}/warehouse_rk/${warehouse_rk}/v2/`,
    params || {},
    0,
    WarehouseCostResponseSchema
  );

// Get Warehouse Cluster Utilization API
export const getWarehouseClusterWarehouseUtilization = (
  instance_id: string,
  warehouse_rk: string,
  params?: GetWarehouseCostParams
) =>
  api.get<WarehouseClusterUtilizationResponse>(
    `/warehouse/cluster_utilization/instance/${instance_id}/warehouse_rk/${warehouse_rk}/`,
    params || {},
    0,
    WarehouseClusterUtilizationResponseSchema
  );

// Get Warehouse Last Event Time API
export const getWarehouseLastEventTime = () =>
  api.get<WarehouseLastEventTimeResponse>(
    "/warehouse/last_event_time",
    {},
    0,
    WarehouseLastEventTimeResponseSchema
  );

// Get Warehouse Metadata API
export const getWarehouseMetadata = () =>
  api.get<WarehouseMetadataResponse>("/warehouse/metadata", {}, 0, WarehouseMetadataResponseSchema);

// AI Feedback APIs
// Get AI Feedback API
export const getAIFeedback = (params: { feedback_id: string; feedback_type: string }) =>
  api.get<GetAIFeedbackResponse>("/feedbacks/ai", params, 0, GetAIFeedbackResponseSchema);

// Post AI Feedback API
export const postAIFeedback = (params: { feedback_id: string; feedback_type: string }) => {
  return (data: {
    feedback_value: string;
    full_form: {
      good_form: Array<{ label: string; type: string; value: string }>;
      bad_form: Array<{ label: string; type: string; value: string }>;
    };
  }) =>
    api.postWithParams<{ is_submitted: boolean }>(
      "/feedbacks/ai",
      params,
      data,
      z.object({
        is_submitted: z.boolean(),
      })
    );
};

// Payment APIs
// Create Checkout Session API
export const createCheckoutSession = (data: CheckoutSessionRequest) =>
  api.post<CreateCheckoutSessionResponse>(
    "/payment/checkout",
    data,
    CreateCheckoutSessionResponseSchema
  );

// Post Checkout Session API
export const postCheckoutSession = (data: PostCheckoutSessionRequest) =>
  api.post<StandardResponse>("/payment/checkout/update", data, StandardResponseSchema);

// Downgrade Plan API
export const downgradePlan = (data: DowngradePlanRequest) =>
  api.post<StandardResponse>("/payment/downgrade", data, StandardResponseSchema);

// Cancel Plan Downgrade API
export const cancelPlanDowngrade = () =>
  api.post<StandardResponse>("/payment/downgrade/cancel", {}, StandardResponseSchema);

// Downgrade Plan Feedback API
export const downgradePlanFeedback = (data: DowngradePlanFeedbackRequest) =>
  api.post<DowngradePlanFeedbackResponse>(
    "/payment/downgrade/feedback",
    data,
    DowngradePlanFeedbackResponseSchema
  );

// Get Credit Usage API
export const getCreditUsage = () =>
  api.get<CreditUsageResponse>("/payment/credits", {}, 0, CreditUsageResponseSchema);

// Get All Plans API
export const getAllPlans = (tenure: string) =>
  api.get<AllPlansResponse>(`/payment/plans/${tenure}`, {}, 0, AllPlansResponseSchema);

// Wallet APIs
export const getWallet = () =>
  api.get<WalletResponse>("/payment/wallet", {}, 0, WalletResponseSchema);

export const getTokenUsage = () =>
  api.get<TokenUsageResponse>("/payment/token-usage", {}, 0, TokenUsageResponseSchema);

export const createWalletDeposit = (data: { amount_cents: number }) =>
  api.post<WalletDepositResponse>("/payment/wallet/deposit", data, WalletDepositResponseSchema);

export const confirmWalletDeposit = (data: { checkout_session_id: string }) =>
  api.post<StandardResponse>("/payment/wallet/deposit/confirm", data, StandardResponseSchema);

export const updateWalletSettings = (data: {
  spend_cap_cents: number | null;
  auto_reload_enabled: boolean;
  auto_reload_amount_cents: number;
  auto_reload_threshold_cents: number;
}) => api.put<StandardResponse>("/payment/wallet/settings", data, StandardResponseSchema);

// Metabase Pulse Preview API
export const getMetabasePulsePreview = (dashboardId: number) =>
  api.get<{
    html: string;
  }>(
    `/metabase/pulse/preview_dashboard/${dashboardId}`,
    {},
    0,
    z.object({
      html: z.string(),
    })
  );

export const fetchDbtInfo = (params: { table_rk: string }) =>
  api.get<DbtInfoResponse>(`/lineage/dbt_info`, params, 0, DbtInfoResponseSchema);

// Lineage APIs
export const upstreamTables = (fqn: string) =>
  api.post<LineageResponse>("/lineage/upstream", { fqn }, lineageResponseSchema);

export const downstreamTables = (fqn: string) =>
  api.post<LineageResponse>("/lineage/downstream", { fqn }, lineageResponseSchema);

export const postColumns = (data: GetColumnsRequest) =>
  api.get<GetColumnsResponse>("/lineage/schema", data, 0, getColumnsResponseSchema);

export const altimateToAmundsen = (params: TerminalNodeRequest) =>
  api.get<TerminalNodeResponse>("/lineage/terminal", params, 0, terminalNodeResponseSchema);

export const postConnectedColumns = (data: ConnectedColumnsRequest) =>
  api.post<ConnectedColumnsResponse>("/lineage/v2/connected", data, connectedColumnsResponseSchema);

// Datasets/Tables APIs
export const getTables = (params: GetTablesParams) =>
  api.get<TablesResponse>("/tables/", params, 0, TablesResponseSchema);

export const getTableFilter = () =>
  api.get<TableFiltersResponse>("/tables/filters", {}, 0, TableFiltersResponseSchema);

export const getDatasetTags = () =>
  api.get<TagsResponse>("/tables/columns/tags/", {}, 0, DatasetTagsResponseSchema);

export const getTablesAccessed = () =>
  api.get<RecentTablesResponse>("/tables/recent", {}, 0, RecentTablesResponseSchema);

export const getTableRk = (tableId: string) =>
  api.get<TableRkResponse>(`/tables/table_rk/${tableId}`, {}, 0, TableRkResponseSchema);

export const updateTableSchemaDesc = (columnId: number, data: UpdateColumnDescriptionRequest) =>
  api.update<StandardResponse>(`/tables/column/${columnId}`, data, StandardResponseSchema);

// Mask Data APIs
// Get mask data configuration
export const getMaskData = () =>
  api.get<GetMaskDataResponse>("/mask/", {}, 0, GetMaskDataResponseSchema);

// Set/update mask data configuration
export const setMaskData = (data: SetMaskDataRequest) =>
  api.post<SetMaskDataResponse>("/mask/", data, SetMaskDataResponseSchema);

// Table Detail and Management APIs
// Get table by ID
export const getTableById = (id: string) =>
  api.get<TableDetailResponse>(`/tables/items/${id}`, {}, 0, TableDetailResponseSchema);

// Get table description by ID
export const getTableDescriptionById = (id: string) =>
  api.get<TableDescriptionResponse>(
    `/tables/table_description/${id}`,
    {},
    0,
    TableDescriptionResponseSchema
  );

// Update table purpose/description
export const updateTablePurpose = (id: string, data: UpdateTablePurposeRequest) =>
  api.update<StandardResponse, UpdateTablePurposeRequest>(
    `/tables/${id}`,
    data,
    StandardResponseSchema
  );

// Generate AI description for table
export const generateDescription = (id: string, data: Record<string, string>) =>
  api.post<StandardResponse>(`/recommendations/description/${id}`, data, StandardResponseSchema);

// Get table insights (storage breakdown)
export const getTableInsightsById = (id: string) =>
  api.get<TableInsightsResponse>(`/tables/insights/${id}`, {}, 0, TableInsightsResponseSchema);

// Get table ID from table resource key
export const getTableId = (data: GetTableIdRequest) =>
  api.post<GetTableIdResponse>(`/tables/table_id`, data, GetTableIdResponseSchema);

// Get table storage details over time
export const getTableStorageOverTimeById = (id: string) =>
  api.get<TableStorageOverTimeResponse>(
    `/tables/storage_details/${id}`,
    {},
    0,
    TableStorageOverTimeResponseSchema
  );

// Column Access History APIs
// Get column access history with pagination
export const getColumnAccessHistory = (params: GetColumnAccessHistoryParams) =>
  api.post<ColumnAccessHistoryResponse>(
    `/tables/access_history/?page=${params.page}&size=${params.size}`,
    {
      table_id: params.table_id,
      column_rk: params.column_rk,
      order_by: params.order_by,
      sort_order: params.sort_order,
    },
    ColumnAccessHistoryResponseSchema
  );

// Get column access history filters
export const getColumnUserAccessHistoryFilters = (tableID: string) =>
  api.get<ColumnUserAccessHistoryFiltersResponse>(
    `/tables/access_history/filters/?table_id=${tableID}`,
    {},
    0,
    ColumnUserAccessHistoryFiltersResponseSchema
  );

// Get user-specific column access history
export const getColumnUserAccessHistory = (data: GetColumnUserAccessHistoryRequest) =>
  api.post<ColumnUserAccessHistoryResponse>(
    `/tables/access_history/column/`,
    data,
    ColumnUserAccessHistoryResponseSchema
  );

// Get dataset/table list with filters
export const getDatasetList = (params: TableDataFilters) =>
  api.get<DatasetListResponse>("/tables/tabledata/", params, 0, DatasetListResponseSchema);

// Name-searchable table inventory read from ClickHouse `table_metadata` — the
// same source the Studio publish gate verifies against, so anything listed here
// can be published (AI-8314). `/tables/tabledata/` is a Snowflake cost rollup
// that lists strictly fewer tables and is empty on new-pipeline tenants.
// `value` is the fully-qualified `database.schema.table` the gate resolves by.
export const getTableInventory = (params: {
  search?: string;
  page?: number;
  size?: number;
  instance_ids?: string[];
}) => api.get<TableRkFilterResponse>("/tables/inventory", params, 0, TableRkFilterResponseSchema);

// Lineage V2 APIs
// Get upstream tables (V2 endpoint)
export const upstreamTablesV2 = (fqn: string) =>
  api.post<UpstreamDownstreamTablesResponse>(
    "/lineage/v2/downstream",
    { fqn },
    upstreamDownstreamTablesResponseSchema
  );

// Get downstream tables (V2 endpoint)
export const downstreamTablesV2 = (fqn: string) =>
  api.post<UpstreamDownstreamTablesResponse>(
    "/lineage/v2/upstream",
    { fqn },
    upstreamDownstreamTablesResponseSchema
  );

// Demo Session API
export const createDemoSession = () =>
  api.post<DemoSessionResponse>("/demo/create-demo-session", {}, demoSessionResponseSchema);

export const authenticateDemo = (sessionId: string) =>
  api.post<User>(`/demo/authenticate?session_id=${sessionId}`, {}, UserSchema);

// Query Filters API with Pagination
export const getQueryFilters = (params: GetQueryFiltersParams = {}) =>
  api.get<QueryFilters>("/query/v2/filters/", params, 0, QueryFiltersSchema);

// Query Groups API
export const getAllQueryGroups = (params: QueryGroupsFilters) =>
  api.get<Page<QueryGroup>>("/query/v2/groups", params, 0, pageSchema(QueryGroupSchema));

// Company APIs
export const getCompanyList = () =>
  api.get<CompanyListResponse>("/companies/", {}, 0, CompanyListResponseSchema);

export const addNewCompany = (company: {
  name: string;
  country: string;
  provider: IntegrationType;
}) => api.post<CompanyCreateResponse>("/companies/", company, CompanyCreateResponseSchema);

// Subscriptions APIs
// Types are imported at the top of the file from the subscription feature module

export const fetchSubscriptionsV2 = (params: SubscriptionListFilters = {}) =>
  api.get<SubscriptionListResponse>(
    "/api/v1/alerts/rules",
    params,
    0,
    SubscriptionListResponseSchema
  );

export const fetchSubscriptionSummary = (params: SubscriptionSummaryFilters = {}) =>
  api.get<SubscriptionV2Summary>(
    "/api/v1/alerts/rules/summary",
    params,
    0,
    SubscriptionV2SummarySchema
  );

export const fetchSubscriptionFilters = () =>
  api.get<SubscriptionFiltersResponse>(
    "/api/v1/alerts/rules/filters",
    {},
    0,
    SubscriptionFiltersResponseSchema
  );

export const deleteSubscription = (id: string) =>
  api.delete<SubscriptionDeleteResponse>(
    `/api/v1/alerts/rules/${id}`,
    {},
    SubscriptionDeleteResponseSchema
  );

export const toggleSubscriptionStatus = (id: string, status: "active" | "inactive") =>
  api.post<SubscriptionResponse>(
    `/api/v1/alerts/rules/${id}`,
    { status },
    SubscriptionResponseSchema
  );

export const fetchSubscriptionById = (id: string) =>
  api.get<SubscriptionResponse>(`/api/v1/alerts/rules/${id}`, {}, 0, SubscriptionResponseSchema);

export const fetchSubscriptionHistory = (
  id: string,
  params: Record<string, string | number | undefined> = {}
) =>
  api.get<SubscriptionHistoryResponse>(
    `/api/v1/alerts/rules/${id}/runs`,
    params,
    0,
    SubscriptionHistoryResponseSchema
  );

export const createSubscriptionV2 = (data: BackendSubscriptionCreateRequest) =>
  api.post<SubscriptionResponse>("/api/v1/alerts/rules", data, SubscriptionResponseSchema);

export const updateSubscriptionV2 = (id: string, data: BackendSubscriptionCreateRequest) =>
  api.post<SubscriptionResponse>(`/api/v1/alerts/rules/${id}`, data, SubscriptionResponseSchema);

/** Creator-only visibility change. The team is derived server-side from the
 *  ``x-user-group-id`` header — ``team_id`` is never accepted in the body. */
export const updateSubscriptionVisibility = (id: string, visibility: RuleVisibility) =>
  api.post<SubscriptionResponse>(
    `/api/v1/alerts/rules/${id}`,
    { visibility },
    SubscriptionResponseSchema
  );

export const cloneSubscription = (id: string) =>
  api.post<SubscriptionResponse>(
    `/api/v1/alerts/rules/${id}/clone`,
    {},
    SubscriptionResponseSchema
  );

export const fetchSubscriptionTemplates = () =>
  api.get<SubscriptionTemplatesListResponse>(
    "/api/v1/alerts/templates",
    {},
    0,
    SubscriptionTemplatesListResponseSchema
  );

export const fetchMetricFilterOptions = (params: {
  category: string;
  metric_name: string;
  filter_key: string;
}) =>
  api.get<MetricFilterValuesResponse>(
    "/subscriptions/metric-filter-options",
    params,
    0,
    MetricFilterValuesResponseSchema
  );

export const fetchSubscriptionMetrics = (params: { platform?: "snowflake" | "databricks" } = {}) =>
  api.get<MetricsCatalogResponse>(
    "/api/v1/alerts/metrics/catalog",
    params,
    0,
    MetricsCatalogResponseSchema
  );

export const fetchSubscriptionPreview = (params?: SubscriptionPreviewParams) =>
  api.post<SubscriptionPreviewResponse>(
    "/api/v1/alerts/preview",
    params ?? {},
    SubscriptionPreviewResponseSchema
  );

// Playground-report rule create/update. Same endpoint as
// createSubscriptionV2 (POST /api/v1/alerts/rules) but the body shape
// is source=playground_report — no metric_config/platform/entity_type.
export const createPlaygroundReport = (data: PlaygroundReportCreateRequest) =>
  api.post<SubscriptionResponse>("/api/v1/alerts/rules", data, SubscriptionResponseSchema);

// ``source`` is immutable after create and is not part of the update
// schema, so the body is the create shape minus that field.
export const updatePlaygroundReport = (id: number | string, data: PlaygroundReportUpdateRequest) =>
  api.post<SubscriptionResponse>(`/api/v1/alerts/rules/${id}`, data, SubscriptionResponseSchema);

// Start a background preview of a playground_report: the alerts worker
// generates it exactly as the schedule will, keeps it on the session and
// emails it to the caller only. Returns 202 at once; 409 while another
// run holds the session.
export const startPlaygroundReportPreview = (params: PlaygroundReportPreviewRequest) =>
  api.post<PlaygroundReportRun>(
    "/api/v1/alerts/preview/playground-report/runs",
    params,
    PlaygroundReportRunSchema
  );

// The session's current or last preview run and its kept report.
export const getPlaygroundReportPreview = (sessionId: string) =>
  api.get<PlaygroundReportRunStatus>(
    `/api/v1/alerts/preview/playground-report/runs/${encodeURIComponent(sessionId)}`,
    {},
    0,
    PlaygroundReportRunStatusSchema
  );

// Send a run's kept preview to destinations exactly as reviewed — no new
// generation. 409 when a newer preview replaced it.
export const sendPlaygroundReportPreview = (
  runId: string,
  data: PlaygroundReportSendPreviewRequest
) =>
  api.post<PlaygroundReportTestResponse>(
    `/api/v1/alerts/preview/playground-report/runs/${encodeURIComponent(runId)}/send`,
    data,
    PlaygroundReportTestResponseSchema
  );

export const testNotification = (data: NotificationTestRequest) =>
  api.post<NotificationTestResponse>(
    "/api/v1/alerts/notifications/test",
    data,
    NotificationTestResponseSchema
  );

export const fetchMetricFilterOptionsById = (filterName: string, metricName?: string) =>
  api.get<MetricFilterOptionsResponse>(
    `/api/v1/alerts/metrics/filters/${filterName}/options`,
    { metric_name: metricName },
    0,
    MetricFilterOptionsResponseSchema
  );

export const searchMetricFilterOptions = (params: {
  filterName: string;
  metricName: string;
  search: string;
  page: number;
  size: number;
}) =>
  api.get<MetricFilterOptionsResponse>(
    `/api/v1/alerts/metrics/filters/${params.filterName}/options`,
    {
      metric_name: params.metricName,
      search: params.search,
      page: params.page,
      size: params.size,
    },
    0,
    MetricFilterOptionsResponseSchema
  );

// Auth APIs

// Warehouse Insights API
export const getWarehouseInsights = (data: WarehouseInsightsRequest) =>
  api.post<WarehouseInsightsResponse>("/warehouse/insights", data, WarehouseInsightsResponseSchema);

// Integration Environments API
export const getIntegrationEnvironments = (integrationId: string) =>
  api.get<IntegrationEnvironmentsResponse>(
    `/dbt/v1/integration_environments/${integrationId}`,
    {},
    0,
    IntegrationEnvironmentsResponseSchema
  );

// DBT Docs Share APIs
export const getDbtDocsShareList = (params: GetDbtDocsShareListParams) =>
  api.get<DbtDocsShareListResponse>(
    "dbt/dbt_docs_share",
    params,
    0,
    DbtDocsShareListResponseSchema
  );

export const getDbtDocsShareListFilters = () =>
  api.get<DbtDocsFilters>("dbt/dbt_docs_share/filters", {}, 0, DbtDocsFiltersSchema);

export const getDbtDocsShareUrl = (shareId: string) =>
  api.get<DbtDocsShareUrlResponse>(
    `dbt/dbt_docs_share/${shareId}`,
    {},
    0,
    DbtDocsShareUrlResponseSchema
  );

export const deleteDbtDocsShare = (shareId: string) =>
  api.delete<DbtDocsStandardApiResponse>(
    `dbt/dbt_docs_share/${shareId}`,
    {},
    DbtDocsStandardApiResponseSchema
  );

// Current User API
export const getCurrentUser = () =>
  api.get<CurrentUserResponse>("/dbt/dbt_docs_share/user/details", {}, 0, BaseUserSchema);

// Experimentation APIs
export const getAllExperiments = (params: GetAllExperimentsParams) =>
  api.get<GetAllExperimentsResponse>(
    "/experimentation",
    params,
    0,
    GetAllExperimentsResponseSchema
  );

export const getExperimentById = (id: string) =>
  api.get<ExperimentDetail>(`/experimentation/${id}`, {}, 0, ExperimentDetailSchema);

export const getExperimentSummary = () =>
  api.get<ExperimentSummaryResponse>(
    "/experimentation/metadata/summary",
    {},
    0,
    ExperimentSummaryResponseSchema
  );

export const createExperiment = (data: CreateExperimentRequest) =>
  api.post<{ ok: boolean }>("/experimentation", data, ExperimentStandardApiResponseSchema);

export const updateExperimentStatus = (id: string, data: UpdateExperimentStatusRequest) =>
  api.update<{ ok: boolean }>(
    `/experimentation/${id}/status`,
    data,
    ExperimentStandardApiResponseSchema
  );

export const getSchemaMetadata = () =>
  api.get<SchemaMetadataResponse>(
    "/experimentation/schema/metadata",
    {},
    0,
    SchemaMetadataResponseSchema
  );

// Query Jobs API
export const getQueryJobs = (params: QueryJobsFilters) =>
  api.get<QueryJobsResponse>("/query/jobs", params, 60000, QueryJobsResponseSchema);

// Get query job executions by ID
export const getQueryJobExecutions = (id: string, params: QueryJobExecutionsFilters) =>
  api.get<QueryJobExecutionsResponse>(
    `/query/jobs/executions/${id}`,
    params,
    0,
    QueryJobExecutionsResponseSchema
  );

// Get job definitions by ID
export const getJobDefinitions = (id: string) =>
  api.get<QueryJobDefinitionsResponse>(
    `/query/jobs/definitions/${id}`,
    {},
    0,
    QueryJobDefinitionsResponseSchema
  );

// Update query job name
export const updateQueryJobName = (id: string, data: UpdateQueryJobNameRequest) =>
  api.post<UpdateQueryJobNameResponse>(
    `/query/job/edit_name/${id}`,
    data,
    UpdateQueryJobNameResponseSchema
  );

// Get query job filters
export const getQueryJobFilters = (params: GetQueryJobFiltersParams) =>
  api.get<QueryJobFiltersResponse>("/query/job/filters/", params, 0, QueryJobFiltersResponseSchema);

// Get query job graph data
export const getQueryJobData = (id: string, params: GetQueryJobDataParams) =>
  api.get<QueryGraphDataResponse>(
    `/query/jobs/graph_data/${id}`,
    params,
    0,
    QueryGraphDataResponseSchema
  );

// Get query group graph data
export const getQueryGroupData = (id: string, params: GetQueryGroupDataParams) =>
  api.get<QueryGraphDataResponse>(
    `/query/groups/graph_data/${id}`,
    params,
    0,
    QueryGraphDataResponseSchema
  );

export const postForgotPassword = (data: ForgotPasswordRequest) =>
  api.post<StandardResponse>("/auth/forgot-password", data, StandardResponseSchema);

// dbt Cloud APIs
const DBT_CLOUD_API_BASE = "/api/v1/dbt/cloud";

// Validate dbt Cloud API token
export const validateDbtCloudToken = (request: DbtCloudValidateTokenRequest) =>
  api.post<DbtCloudValidationResponse>(
    `${DBT_CLOUD_API_BASE}/validate`,
    request,
    DbtCloudValidationResponseSchema
  );

// Test an existing dbt Cloud connection using stored credentials
export const testDbtCloudConnection = (connectionId: number) =>
  api.post<DbtCloudValidationResponse>(
    `${DBT_CLOUD_API_BASE}/connections/${connectionId}/test`,
    {},
    DbtCloudValidationResponseSchema
  );

// Create a new dbt Cloud connection
export const createDbtCloudConnection = (request: DbtCloudConnectionCreateRequest) =>
  api.post<DbtCloudConnectionResponse>(
    `${DBT_CLOUD_API_BASE}/connections`,
    request,
    DbtCloudConnectionResponseSchema
  );

// List all dbt Cloud connections
export const listDbtCloudConnections = () =>
  api.get<DbtCloudConnectionListResponse>(
    `${DBT_CLOUD_API_BASE}/connections`,
    null,
    0,
    DbtCloudConnectionListResponseSchema
  );

// Get a specific dbt Cloud connection
export const getDbtCloudConnection = (connectionId: number) =>
  api.get<DbtCloudConnectionResponse>(
    `${DBT_CLOUD_API_BASE}/connections/${connectionId}`,
    null,
    0,
    DbtCloudConnectionResponseSchema
  );

// Delete a dbt Cloud connection
export const deleteDbtCloudConnection = (connectionId: number) =>
  api.delete(
    `${DBT_CLOUD_API_BASE}/connections/${connectionId}`,
    {},
    DbtCloudConnectionResponseSchema
  );

// Update a dbt Cloud connection
export const updateDbtCloudConnection = (
  connectionId: number,
  request: DbtCloudConnectionUpdateRequest
) =>
  api.update<DbtCloudConnectionResponse>(
    `${DBT_CLOUD_API_BASE}/connections/${connectionId}`,
    request,
    DbtCloudConnectionResponseSchema
  );

// Refresh (re-discover) projects and environments
export const refreshDbtCloudConnection = (connectionId: number) =>
  api.post<DbtCloudDiscoveryResponse>(
    `${DBT_CLOUD_API_BASE}/connections/${connectionId}/refresh`,
    {},
    DbtCloudDiscoveryResponseSchema
  );

// Update a project (enable/disable)
export const updateDbtCloudProject = (projectId: number, request: DbtCloudProjectUpdateRequest) =>
  api.update<DbtCloudProjectResponse>(
    `${DBT_CLOUD_API_BASE}/projects/${projectId}`,
    request,
    DbtCloudProjectResponseSchema
  );

// Update an environment (enable/disable)
export const updateDbtCloudEnvironment = (
  environmentId: number,
  request: DbtCloudEnvironmentUpdateRequest
) =>
  api.update<DbtCloudEnvironmentResponse>(
    `${DBT_CLOUD_API_BASE}/environments/${environmentId}`,
    request,
    DbtCloudEnvironmentResponseSchema
  );

// Trigger a sync
export const triggerDbtCloudSync = (connectionId: number) =>
  api.post<DbtCloudSyncResponse>(
    `${DBT_CLOUD_API_BASE}/connections/${connectionId}/sync`,
    {},
    DbtCloudSyncResponseSchema
  );

// Get sync history params interface
export interface GetDbtCloudSyncHistoryParams {
  page?: number;
  size?: number;
}

// Get sync history
export const getDbtCloudSyncHistory = (
  connectionId: number,
  params?: GetDbtCloudSyncHistoryParams
) =>
  api.get<DbtCloudSyncHistoryResponse>(
    `${DBT_CLOUD_API_BASE}/connections/${connectionId}/sync-history`,
    params,
    0,
    DbtCloudSyncHistoryResponseSchema
  );

// Update schedule
export const updateDbtCloudSchedule = (
  connectionId: number,
  request: DbtCloudScheduleUpdateRequest
) =>
  api.update<DbtCloudScheduleResponse>(
    `${DBT_CLOUD_API_BASE}/connections/${connectionId}/schedule`,
    request,
    DbtCloudScheduleResponseSchema
  );

// =====================================
// Databricks APIs
// =====================================
const DATABRICKS_API_BASE = "/api/v1/databricks";

// SKU Cost APIs
export const getSkuCostBreakdown = (params: SkuBreakdownParams) => {
  const { group_by = "sku", ...restParams } = params;
  return api.get<SkuCostBreakdownResponse>(
    `${DATABRICKS_API_BASE}/cost/breakdown/${group_by}`,
    restParams,
    0,
    SkuCostBreakdownResponseSchema
  );
};

// =====================================
// Workspaces Cost APIs
// =====================================

export const getWorkspaces = () =>
  api.get<WorkspacesResponse>(
    `${DATABRICKS_API_BASE}/cost/workspaces`,
    {},
    0,
    WorkspacesResponseSchema
  );

export const getWorkspacesCostTotal = (params: WorkspacesCostParams) =>
  api.get<CostTotalResponse>(
    `${DATABRICKS_API_BASE}/cost/total`,
    params,
    0,
    CostTotalResponseSchema
  );

export const getWorkspacesCostBreakdown = (params: WorkspacesBreakdownParams) => {
  const { group_by = "workspace", ...restParams } = params;
  return api.get<WorkspacesCostBreakdownResponse>(
    `${DATABRICKS_API_BASE}/cost/breakdown/${group_by}`,
    restParams,
    0,
    WorkspacesCostBreakdownResponseSchema
  );
};

export const getWorkspacesCostTrendsByProduct = (params: WorkspacesTrendsParams) =>
  api.get<ProductTrendsResponse>(
    `${DATABRICKS_API_BASE}/cost/trends/by-product`,
    params,
    0,
    ProductTrendsResponseSchema
  );

export const getWorkspacesEfficiency = (params: WorkspacesCostParams) =>
  api.get<WorkspaceEfficiencyResponse>(
    `${DATABRICKS_API_BASE}/cost/workspace-efficiency`,
    params,
    0,
    WorkspaceEfficiencyResponseSchema
  );

// AI Services APIs
export const getAIServicesSummary = (params: AIServicesSummaryParams) =>
  api.get<AIServicesSummaryResponse>(
    "/ai_services/summary",
    params,
    0,
    aiServicesSummaryResponseSchema
  );

export const getAIServiceGraph = (serviceType: string, params: AIServiceGraphParams) =>
  api.get<AIServiceGraphResponse>(
    `/ai_services/${serviceType}/graph`,
    params,
    0,
    aiServiceGraphResponseSchema
  );

export const getAIServiceUsage = (serviceType: string, params: AIServiceUsageParams) =>
  api.get<AIServiceUsageResponse>(
    `/ai_services/${serviceType}/usage`,
    params,
    0,
    aiServiceUsageResponseSchema
  );

export const getAIServiceBreakdown = (serviceType: string, params: AIServiceBreakdownParams) =>
  api.get<AIServiceBreakdownResponse>(
    `/ai_services/${serviceType}/breakdown`,
    params,
    0,
    aiServiceBreakdownResponseSchema
  );

export const getAIServiceFilterValues = (
  serviceType: string,
  params: AIServiceFilterValuesParams
) =>
  api.get<AIServiceFilterValuesResponse>(
    `/ai_services/${serviceType}/filter_values`,
    params,
    0,
    aiServiceFilterValuesResponseSchema
  );

// AI Conversation Traces APIs
export const getAICacheOverview = (params: CacheOverviewParams) =>
  api.get<CacheOverviewResponse>("/ai_caching/overview", params, 0, cacheOverviewResponseSchema);

export const getAIServiceConversations = (serviceType: string, params: ConversationsParams) =>
  api.get<ConversationsResponse>(
    `/ai_services/${serviceType}/conversations`,
    params,
    0,
    conversationsResponseSchema
  );

export const getAIServiceConversationTrace = (
  serviceType: string,
  conversationId: string,
  params: ConversationTraceParams
) =>
  api.get<TraceResponse>(
    `/ai_services/${serviceType}/conversations/${encodeURIComponent(conversationId)}/trace`,
    params,
    0,
    traceResponseSchema
  );

export const getAIServiceTraceQuery = (serviceType: string, params: TraceQueryParams) =>
  api.get<TraceQueryResponse>(
    `/ai_services/${serviceType}/trace_query`,
    params,
    0,
    traceQueryResponseSchema
  );

export const getClusters = (params: ClustersParams = {}) =>
  api.get<ClustersResponse>(`${DATABRICKS_API_BASE}/clusters`, params, 0, ClustersResponseSchema);

export const downloadClustersCSV = (params: ClustersParams) =>
  downloadCSV(`${DATABRICKS_API_BASE}/clusters`, params as Record<string, unknown>);

export const getClusterDetail = (params: ClusterDetailParams) =>
  api.get<ClusterDetail>(
    `${DATABRICKS_API_BASE}/clusters/${params.cluster_id}`,
    {},
    0,
    ClusterDetailSchema
  );

export const getClusterUtilization = (params: ClusterTimeseriesParams) =>
  api.get<ClusterUtilizationResponse>(
    `${DATABRICKS_API_BASE}/clusters/${params.cluster_id}/utilization`,
    {
      start_date: params.start_date,
      end_date: params.end_date,
      granularity: params.granularity,
    },
    0,
    ClusterUtilizationResponseSchema
  );

export const getClusterCost = (params: ClusterTimeseriesParams) =>
  api.get<ClusterCostResponse>(
    `${DATABRICKS_API_BASE}/clusters/${params.cluster_id}/cost`,
    {
      start_date: params.start_date,
      end_date: params.end_date,
      granularity: params.granularity,
    },
    0,
    ClusterCostResponseSchema
  );

export interface ClusterFilterFieldParams extends EntityFilterParams {
  // Type-filter bucket(s) to scope the cluster-name dropdown; mirrors the
  // /clusters list compute_type param so options stay in sync with the table.
  compute_type?: string[];
}

export const getClustersCostTrends = (params: ClustersCostTrendsParams) =>
  api.get<ClustersCostTrendsResponse>(
    `${DATABRICKS_API_BASE}/clusters/cost-trends`,
    params,
    0,
    ClustersCostTrendsResponseSchema
  );

// =====================================
// SSO Group Sync APIs
// =====================================
export const getSSOTeamSyncConfig = () =>
  api.get<z.infer<typeof SSOTeamSyncConfigResponseSchema>>(
    "/sso-group-mappings/config",
    {},
    0,
    SSOTeamSyncConfigResponseSchema
  );

export const updateSSOTeamSyncConfig = (data: SSOTeamSyncConfigUpdatePayload) =>
  api.put<z.infer<typeof SSOTeamSyncConfigResponseSchema>, SSOTeamSyncConfigUpdatePayload>(
    "/sso-group-mappings/config",
    data,
    SSOTeamSyncConfigResponseSchema
  );

export const getSSOGroupMappings = (params?: { mapping_type?: string }) =>
  api.get<z.infer<typeof SSOGroupMappingListSchema>>(
    "/sso-group-mappings/mappings",
    params ?? {},
    0,
    SSOGroupMappingListSchema
  );

export const createSSOGroupMapping = (data: {
  external_group_identifier: string;
  match_type: string;
  mapping_type: string;
  target_id: number;
}) =>
  api.post<z.infer<typeof SSOGroupMappingResponseSchema>>(
    "/sso-group-mappings/mappings",
    data,
    SSOGroupMappingResponseSchema
  );

export const updateSSOGroupMapping = (mappingId: number, data: SSOGroupMappingUpdatePayload) =>
  api.put<z.infer<typeof SSOGroupMappingResponseSchema>, SSOGroupMappingUpdatePayload>(
    `/sso-group-mappings/mappings/${mappingId}`,
    data,
    SSOGroupMappingResponseSchema
  );

export const deleteSSOGroupMapping = (mappingId: number) =>
  api.delete<z.infer<typeof StandardResponseWithWarningSchema>>(
    `/sso-group-mappings/mappings/${mappingId}`,
    {},
    StandardResponseWithWarningSchema
  );

export const previewSSOGroupMappings = (data: { external_groups: string[] }) =>
  api.post<z.infer<typeof SSOPreviewResponseSchema>>(
    "/sso-group-mappings/preview",
    data,
    SSOPreviewResponseSchema
  );

export const getSSOAuditEvents = (params: {
  user_id?: number;
  event_type?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}) =>
  api.get<z.infer<typeof SSOAuditEventListResponseSchema>>(
    "/sso-group-mappings/audit-events",
    params,
    0,
    SSOAuditEventListResponseSchema
  );

export const getSSOTargets = () =>
  api.get<z.infer<typeof SSOTargetsResponseSchema>>(
    "/sso-group-mappings/targets",
    {},
    0,
    SSOTargetsResponseSchema
  );

export const getSSOExternalGroups = (params?: { search?: string }) =>
  api.get<z.infer<typeof SSOExternalGroupListResponseSchema>>(
    "/sso-group-mappings/external-groups",
    params ?? {},
    0,
    SSOExternalGroupListResponseSchema
  );

export const uploadSSOExternalGroups = (data: FormData) =>
  api.post<z.infer<typeof SSOBulkUploadResponseSchema>>(
    "/sso-group-mappings/external-groups/upload",
    data,
    SSOBulkUploadResponseSchema,
    { headers: { "Content-Type": "multipart/form-data" } }
  );

export const uploadSSOGroupMappings = (data: FormData) =>
  api.post<z.infer<typeof SSOBulkMappingUploadResponseSchema>>(
    "/sso-group-mappings/mappings/upload",
    data,
    SSOBulkMappingUploadResponseSchema,
    { headers: { "Content-Type": "multipart/form-data" } }
  );

// --- Entity Filter APIs ---

export interface EntityFilterParams {
  prefix?: string;
  page?: number;
  size?: number;
  workspace_id?: string;
  start_date?: string;
  end_date?: string;
}

// Databricks access-rule picker: the catalog of accounts / workspaces a team's
// access rules can target. Powers the Teams page dropdowns for Databricks tenants.
export const DatabricksAccessRuleWorkspaceOptionSchema = z.object({
  workspace_id: z.string(),
  workspace_name: z.string(),
  account_id: z.string(),
});

export const DatabricksAccessRuleAccountOptionSchema = z.object({
  account_id: z.string(),
  workspace_count: z.number(),
});

export const DatabricksAccessRuleOptionsResponseSchema = z.object({
  accounts: z.array(DatabricksAccessRuleAccountOptionSchema),
  workspaces: z.array(DatabricksAccessRuleWorkspaceOptionSchema),
});

export type DatabricksAccessRuleOptionsResponse = z.infer<
  typeof DatabricksAccessRuleOptionsResponseSchema
>;

export const getDatabricksAccessRuleOptions = () =>
  api.get<DatabricksAccessRuleOptionsResponse>(
    `${DATABRICKS_API_BASE}/access-rules/options`,
    {},
    0,
    DatabricksAccessRuleOptionsResponseSchema
  );

export const getDatabricksQuerySqlLineage = (statementId: string) =>
  api.get<QuerySqlLineageResponse>(
    `${DATABRICKS_API_BASE}/queries/${statementId}/sql-lineage`,
    {},
    0,
    QuerySqlLineageResponseSchema
  );

export const getDatabricksJobDetail = (
  jobId: string,
  params: { start_date?: string; end_date?: string } = {}
) =>
  api.get<JobDetailResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}`,
    params,
    0,
    JobDetailResponseSchema
  );

export const getDatabricksSingleJobRunTrends = (
  jobId: string,
  params: { start_date?: string; end_date?: string; granularity?: ChartGranularity } = {}
) =>
  api.get<JobRunTrendsResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/trends`,
    params,
    0,
    JobRunTrendsResponseSchema
  );

export const getDatabricksResourceOpportunities = (params: {
  resource_uri: string;
  resource_type: number;
  instance_id?: string;
  workspace_id?: string;
  start_date?: string;
  end_date?: string;
  status_filter?: string;
  sort?: string;
  sort_order?: string;
}) =>
  api.get<GetOpportunitiesResourceResponse>(
    `${DATABRICKS_API_BASE}/opportunities/resource`,
    params,
    0,
    GetOpportunitiesResourceResponseSchema
  );

// Auto-Tune (Databricks): read + toggle
export const getDatabricksJobAutoRecommendations = (
  jobId: string,
  params: { instance_id: number | string; workspace_id?: string; limit?: number }
) =>
  api.get<DbxAutoRecommendationsResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/auto-recommendations`,
    params,
    0,
    DbxAutoRecommendationsResponseSchema
  );

export const toggleDatabricksJobAutoTune = (
  jobId: string,
  params: {
    instance_id: number | string;
    workspace_id?: string;
    task_key?: string;
    force?: boolean;
  },
  body: {
    enabled: boolean;
    scope?: "all_tasks" | "specific_tasks";
    enabled_task_keys?: string[];
  }
) => {
  const qs = new URLSearchParams({ instance_id: String(params.instance_id) });
  if (params.workspace_id) qs.set("workspace_id", params.workspace_id);
  if (params.task_key) qs.set("task_key", params.task_key);
  if (params.force) qs.set("force", "true");
  return api.update<ToggleAutoTuneResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/auto-tune/toggle?${qs.toString()}`,
    body,
    ToggleAutoTuneResponseSchema
  );
};

// Backoff + notification config CRUD for jobs, scoped to
// ``/jobs/{job_id}/auto-tune/config``. The backoff half has a
// job-specific shape (``JobAutoTuneConfigResponse`` /
// ``JobAutoTuneConfigRequest`` — a latency threshold, not the
// cluster/warehouse cold-start rate); the notification half reuses the
// shared config schema. A plain config write, never routed through the
// ``/toggle`` endpoint: never touches task enablement, never runs the
// Databricks preflight.
const jobAutoTuneConfigUrl = (jobId: string, segment: string) =>
  `${DATABRICKS_API_BASE}/jobs/${jobId}/auto-tune/${segment}`;

export const getJobAutoTuneConfig = (
  jobId: string,
  params: { instance_id: number | string; workspace_id?: string }
) =>
  api.get<JobAutoTuneConfigResponse>(
    jobAutoTuneConfigUrl(jobId, "config"),
    params,
    0,
    JobAutoTuneConfigResponseSchema
  );

export const saveJobAutoTuneConfig = (
  jobId: string,
  params: { instance_id: number | string; workspace_id?: string },
  body: JobAutoTuneConfigRequest
) => {
  const qs = new URLSearchParams({ instance_id: String(params.instance_id) });
  if (params.workspace_id) qs.set("workspace_id", params.workspace_id);
  return api.update<JobAutoTuneConfigResponse, JobAutoTuneConfigRequest>(
    `${jobAutoTuneConfigUrl(jobId, "config")}?${qs.toString()}`,
    body,
    JobAutoTuneConfigResponseSchema
  );
};

// Resets ONLY the backoff policy to defaults — never disables Auto Tune,
// never resets task enablement.
export const resetJobAutoTuneBackoff = (
  jobId: string,
  params: { instance_id: number | string; workspace_id?: string }
) => {
  const qs = new URLSearchParams({ instance_id: String(params.instance_id) });
  if (params.workspace_id) qs.set("workspace_id", params.workspace_id);
  return api.post<JobAutoTuneConfigResponse>(
    `${jobAutoTuneConfigUrl(jobId, "config/reset")}?${qs.toString()}`,
    {},
    JobAutoTuneConfigResponseSchema
  );
};

// Paginated jobs Auto Tune history, typed + zod-validated (unlike the raw
// axios call in ``pages/jobs/auto-tune/hooks.ts``) so the CSV export's
// full-history pager (``pages/jobs/auto-tune/historyCsv.ts``) can reuse it
// the same way the cluster export reuses ``getClusterAutoTuneHistory``.
export const getJobAutoTuneHistory = (
  jobId: string,
  params: {
    instance_id: number | string;
    workspace_id?: string;
    event_types?: string;
    page?: number;
    page_size?: number;
  }
) =>
  api.get<AutoTuneHistoryResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/auto-tune/history`,
    params,
    0,
    AutoTuneHistoryResponseSchema
  );

// Auto Tune (Databricks all-purpose clusters): status / recommendations /
// history / savings-trends reads + the toggle. Mirrors the jobs wrappers
// but scoped to ``/clusters/{cluster_id}/auto-tune/*``. The toggle records
// a config + audit change only — no Databricks cluster edit is invoked.
const clusterAutoTuneUrl = (clusterId: string, segment: string) =>
  `${DATABRICKS_API_BASE}/clusters/${clusterId}/auto-tune/${segment}`;

export const getClusterAutoTuneStatus = (
  clusterId: string,
  params: { instance_id: number | string; workspace_id?: string }
) =>
  api.get<AutoTuneStatusResponse>(
    clusterAutoTuneUrl(clusterId, "status"),
    params,
    0,
    AutoTuneStatusResponseSchema
  );

export const getClusterAutoTuneRecommendations = (
  clusterId: string,
  params: { instance_id: number | string; workspace_id?: string }
) =>
  api.get<ClusterAutoTuneRecommendationsResponse>(
    clusterAutoTuneUrl(clusterId, "recommendations"),
    params,
    0,
    ClusterAutoTuneRecommendationsResponseSchema
  );

export const getClusterAutoTuneHistory = (
  clusterId: string,
  params: {
    instance_id: number | string;
    workspace_id?: string;
    event_types?: string;
    period?: number;
    page?: number;
    page_size?: number;
  }
) =>
  api.get<AutoTuneHistoryResponse>(
    clusterAutoTuneUrl(clusterId, "history"),
    params,
    0,
    AutoTuneHistoryResponseSchema
  );

export const getClusterAutoTuneSavingsTrends = (
  clusterId: string,
  params: {
    instance_id: number | string;
    start_date: string;
    end_date: string;
    granularity?: string;
  }
) =>
  api.get<AutoTuneSavingsTrendsResponse>(
    clusterAutoTuneUrl(clusterId, "savings-trends"),
    params,
    0,
    AutoTuneSavingsTrendsResponseSchema
  );

export const toggleClusterAutoTune = (
  clusterId: string,
  params: { instance_id: number | string; workspace_id?: string },
  body: {
    enabled: boolean;
    rec_type?: "resize" | "auto_terminate" | "family_change" | "autoscale" | null;
  }
) => {
  const qs = new URLSearchParams({ instance_id: String(params.instance_id) });
  if (params.workspace_id) qs.set("workspace_id", params.workspace_id);
  return api.update<ClusterAutoTuneToggleResponse>(
    `${clusterAutoTuneUrl(clusterId, "toggle")}?${qs.toString()}`,
    body,
    ClusterAutoTuneToggleResponseSchema
  );
};

// Backoff + notification config CRUD — a plain config write, never routed
// through toggle(): it never touches the axis flags, never runs the
// Databricks preflight, never triggers the pre-touch defaults capture.
export const getClusterAutoTuneConfig = (
  clusterId: string,
  params: { instance_id: number | string; workspace_id?: string }
) =>
  api.get<ClusterAutoTuneConfigResponse>(
    clusterAutoTuneUrl(clusterId, "config"),
    params,
    0,
    ClusterAutoTuneConfigResponseSchema
  );

export const saveClusterAutoTuneConfig = (
  clusterId: string,
  params: { instance_id: number | string; workspace_id?: string },
  body: ClusterAutoTuneConfigRequest
) => {
  const qs = new URLSearchParams({ instance_id: String(params.instance_id) });
  if (params.workspace_id) qs.set("workspace_id", params.workspace_id);
  return api.update<ClusterAutoTuneConfigResponse, ClusterAutoTuneConfigRequest>(
    `${clusterAutoTuneUrl(clusterId, "config")}?${qs.toString()}`,
    body,
    ClusterAutoTuneConfigResponseSchema
  );
};

// Resets ONLY the backoff policy to defaults — never disables Auto Tune,
// never resets the optimization toggles.
export const resetClusterAutoTuneBackoff = (
  clusterId: string,
  params: { instance_id: number | string; workspace_id?: string }
) => {
  const qs = new URLSearchParams({ instance_id: String(params.instance_id) });
  if (params.workspace_id) qs.set("workspace_id", params.workspace_id);
  return api.post<ClusterAutoTuneConfigResponse>(
    `${clusterAutoTuneUrl(clusterId, "config/reset")}?${qs.toString()}`,
    {},
    ClusterAutoTuneConfigResponseSchema
  );
};

// Per-cluster infra cost trend, backing the Cost Savings chart's Infra
// mode — same ``AutoTuneSavingsTrendsResponse`` shape as the DBU-mode
// savings-trends endpoint so the chart consumes an identical shape either
// way.
export const getClusterAutoTuneInfraCostTrends = (
  clusterId: string,
  params: {
    instance_id: number | string;
    workspace_id?: string;
    start_date: string;
    end_date: string;
    granularity?: string;
  }
) =>
  api.get<AutoTuneSavingsTrendsResponse>(
    clusterAutoTuneUrl(clusterId, "infra-cost-trends"),
    params,
    0,
    AutoTuneSavingsTrendsResponseSchema
  );

// Jobs' Infra-mode series, the sibling of the cluster endpoint above and
// sharing its wire shape — read cost through
// ``readInfraCostTrendPointCost``, never the raw ``dbu_savings`` field.
export const getJobAutoTuneInfraCostTrends = (
  jobId: string,
  params: {
    instance_id: number | string;
    workspace_id?: string;
    start_date: string;
    end_date: string;
    granularity?: string;
  }
) =>
  api.get<AutoTuneSavingsTrendsResponse>(
    jobAutoTuneConfigUrl(jobId, "infra-cost-trends"),
    params,
    0,
    AutoTuneSavingsTrendsResponseSchema
  );

// The infra-cost-trends response reuses the savings-trends wire shape
// so the chart consumes an identical shape either way — but
// on THIS endpoint the ``dbu_savings`` slot carries the cluster's cloud
// COST, not a savings figure, and ``infra_savings`` carries the
// infra-saved leg. Read cost through this accessor rather than the raw
// field name, so a caller never mistakes it for savings.
export const readInfraCostTrendPointCost = (point: AutoTuneSavingsTrendPoint): number =>
  point.dbu_savings;

// Cluster-level Auto Tune enabled/disabled audit events for the Cost
// Savings chart's vertical event markers — per-optimization toggles
// are excluded server-side.
export const getClusterAutoTuneToggleEvents = (
  clusterId: string,
  params: {
    instance_id: number | string;
    workspace_id?: string;
    start_date: string;
    end_date: string;
  }
) =>
  api.get<ClusterAutoTuneToggleEventsResponse>(
    clusterAutoTuneUrl(clusterId, "toggle-events"),
    params,
    0,
    ClusterAutoTuneToggleEventsResponseSchema
  );

// Spark UI deep-link for a Databricks job run (or specific task run within
// it). BE always returns 200 with a `state` discriminator; real HTTP errors
// (auth, server crashes) route through the standard global error handler.
export const getDatabricksJobRunSparkUi = (
  jobId: string,
  runId: string,
  params: { task_run_id?: string; task_key?: string; workspace_id?: string } = {}
) =>
  api.get<JobRunSparkUiResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/runs/${runId}/spark-ui`,
    params,
    0,
    JobRunSparkUiResponseSchema
  );

// Static job task list (every task in the workflow DAG, including
// tasks that have never run). Backed by the BE
// /jobs/{id}/tasks-inventory endpoint reading
// ``dbx_job_tasks_inventory``. The Auto Tune surfaces use this as
// the authoritative task list.
export const getDatabricksJobTasksInventory = (
  jobId: string,
  // ``workspace_id`` disambiguates ``job_id`` across workspaces on the BE's
  // per-task cost lookup; omitted when unknown, which the BE degrades from
  // gracefully (matches the pre-ticket behavior).
  params: { instance_id: number | string; workspace_id?: string }
) =>
  api.get<JobTasksInventoryResponse>(
    `${DATABRICKS_API_BASE}/jobs/${jobId}/tasks-inventory`,
    params,
    0,
    JobTasksInventoryResponseSchema
  );

// --- User APIs ---

export const getDatabricksServicePrincipalConfig = () =>
  api.get<ServicePrincipalConfig>(
    `${DATABRICKS_API_BASE}/users/service-principal-config`,
    {},
    0,
    ServicePrincipalConfigSchema
  );

export const upsertDatabricksServicePrincipalConfig = (data: ServicePrincipalConfig) =>
  api.put<ServicePrincipalConfig, ServicePrincipalConfig>(
    `${DATABRICKS_API_BASE}/users/service-principal-config`,
    data,
    ServicePrincipalConfigSchema
  );

// =====================================
// Databricks Summary APIs
// =====================================

export const getDatabricksSummarySavingsOverall = (params: SummarySavingsParams) =>
  api.get<SummarySavingsOverallResponse>(
    `${DATABRICKS_API_BASE}/savings/overall`,
    params,
    0,
    SummarySavingsOverallResponseSchema
  );

export const getDatabricksSummarySavingsOverallEstimate = (params: SummarySavingsParams) =>
  api.get<SummarySavingsOverallEstimateResponse>(
    `${DATABRICKS_API_BASE}/savings/overall/estimate`,
    params,
    0,
    SummarySavingsOverallEstimateResponseSchema
  );

export const getDatabricksSummarySavingsTimeseries = (params: SummaryCostParams) =>
  api.get<SummarySavingsTimeseriesResponse>(
    `${DATABRICKS_API_BASE}/savings/timeseries`,
    params,
    0,
    SummarySavingsTimeseriesResponseSchema
  );

export const getDatabricksSummarySavingsAssisted = (params: SummaryCostParams) =>
  api.get<SummarySavingsAssistedResponse>(
    `${DATABRICKS_API_BASE}/savings/assisted`,
    params,
    0,
    SummarySavingsAssistedResponseSchema
  );
// Databricks Connection APIs
export const getDatabricksConnection = (id: number) =>
  api.get<DatabricksConnectionResponse>(
    `${DATABRICKS_API_BASE}/connections/${id}`,
    {},
    0,
    DatabricksConnectionResponseSchema
  );

export const createDatabricksConnection = (data: DatabricksConnectionCreateRequest) =>
  api.post<DatabricksConnectionResponse>(
    `${DATABRICKS_API_BASE}/connections`,
    data,
    DatabricksConnectionResponseSchema
  );

export const deleteDatabricksConnection = (id: number) =>
  api.delete<StandardResponse>(
    `${DATABRICKS_API_BASE}/connections/${id}`,
    {},
    StandardResponseSchema
  );

export const testDatabricksConnection = (data: DatabricksConnectionTestRequest) =>
  api.post<DatabricksConnectionTestResponse>(
    `${DATABRICKS_API_BASE}/connections/test`,
    data,
    DatabricksConnectionTestResponseSchema
  );

export const validateDatabricksConnection = (id: number) =>
  api.post<DatabricksConnectionTestResponse>(
    `${DATABRICKS_API_BASE}/connections/${id}/validate`,
    {},
    DatabricksConnectionTestResponseSchema
  );

// Workspaces under the connection's Databricks account. Sourced from the
// ClickHouse inventory ingestion populates — a connection doesn't enumerate them.
export const getDatabricksConnectionWorkspaces = (id: number) =>
  api.get<DatabricksWorkspacesResponse>(
    `${DATABRICKS_API_BASE}/connections/${id}/workspaces`,
    {},
    0,
    DatabricksWorkspacesResponseSchema
  );

// Tenant-wide, not scoped to a connection — runs carry no data_store_id.
export const getDatabricksSyncHistory = (params: DatabricksSyncHistoryParams) =>
  api.get<DatabricksSyncHistoryResponse>(
    `${DATABRICKS_API_BASE}/sync-history`,
    params,
    0,
    DatabricksSyncHistoryResponseSchema
  );
// Skills — a standalone library of skill bundles. The list returns metadata
// only; a bundle's SKILL.md body and its other files are fetched per skill.
/**
 * One page of the skills library.
 *
 * `privacy` and `datamate_id` are sent to the server rather than filtered here.
 * Narrowing a page is not narrowing the set: filtering the 50 rows the client
 * was handed leaves the rest on page 2 while the screen reads "no skills".
 *
 * `page` is 1-indexed, matching the `Page[...]` envelope the API returns. The
 * `Paginate` component is 0-indexed, so callers convert at that boundary.
 */
export const getSkills = (params: SkillListParams = {}) =>
  api.get<z.infer<typeof SkillListResponseSchema>>(
    "/skills",
    {
      page: params.page ?? 1,
      size: params.size ?? DEFAULT_SKILL_PAGE_SIZE,
      ...(params.privacy !== undefined ? { privacy: params.privacy } : {}),
      ...(params.datamateId !== undefined ? { datamate_id: params.datamateId } : {}),
      ...(params.imported !== undefined ? { imported: params.imported } : {}),
    },
    0,
    SkillListResponseSchema
  );

export const getSkill = (publicId: string) =>
  api.get<z.infer<typeof SkillResponseSchema>>(`/skills/${publicId}`, {}, 0, SkillResponseSchema);

export const createSkill = (data: CreateSkillPayload) =>
  api.post<z.infer<typeof SkillResponseSchema>>("/skills", data, SkillResponseSchema);

export const updateSkill = (publicId: string, data: UpdateSkillPayload) =>
  api.update<z.infer<typeof SkillResponseSchema>, UpdateSkillPayload>(
    `/skills/${publicId}`,
    data,
    SkillResponseSchema
  );

/**
 * Write one file inside a skill's bundle. PATCH with `files` replaces the whole
 * bundle, which is not what an editor saving one file means.
 *
 * The path is encoded segment by segment rather than interpolated raw: this is
 * the only skill endpoint that takes an arbitrary bundle path, and a `#` or `?`
 * in a filename would otherwise truncate the request silently. Segment-wise,
 * because the separators are part of the route — `encodeURIComponent` on the
 * whole path would escape them and address a file that does not exist.
 */
export const updateSkillFile = (publicId: string, path: string, content: string) =>
  api.put<z.infer<typeof SkillResponseSchema>, { content: string }>(
    `/skills/${publicId}/files/${path.split("/").map(encodeURIComponent).join("/")}`,
    { content },
    SkillResponseSchema
  );

/** Browse the public data-engineering-skills catalog. Cached server-side. */
export const getSkillCatalog = () =>
  api.get<z.infer<typeof SkillCatalogResponseSchema>>(
    "/skills/catalog",
    {},
    0,
    SkillCatalogResponseSchema
  );

/**
 * Import catalog skills into the library and attach them to workspaces.
 *
 * The whole selection travels in one request: importing seven skills as seven
 * calls leaves a window where some are attached and some are not.
 */
export const importCatalogSkills = (data: ImportCatalogSkillsPayload) =>
  api.post<z.infer<typeof SkillsCreatedResponseSchema>>(
    "/skills/catalog/import",
    data,
    SkillsCreatedResponseSchema
  );

/**
 * Replace the whole set of workspaces a skill is attached to.
 *
 * One request, applied atomically: every id is validated before anything is
 * written, so a bad id leaves the previous set untouched.
 *
 * The response carries the attachment set alone, not the whole skill — the
 * skill's own fields are unaffected by an attachment write.
 */
export const setSkillWorkspaces = (publicId: string, datamateIds: number[]) =>
  api.put<z.infer<typeof SkillWorkspacesResponseSchema>, { datamate_ids: number[] }>(
    `/skills/${publicId}/datamates`,
    { datamate_ids: datamateIds },
    SkillWorkspacesResponseSchema
  );

/**
 * Replace the whole set of skills attached to one workspace.
 *
 * The mirror of `setSkillWorkspaces`, and scoped to the workspace: it only
 * touches rows for this `datamateId`, so it cannot disturb where else any of
 * these skills is attached. Doing the same through the skill-scoped endpoint
 * would mean reading each skill's full workspace set first, one request each.
 */
export const setDatamateSkills = (datamateId: number, skillPublicIds: string[]) =>
  api.put<z.infer<typeof DatamateSkillsResponseSchema>, { skill_public_ids: string[] }>(
    `/datamates/${datamateId}/skills`,
    { skill_public_ids: skillPublicIds },
    DatamateSkillsResponseSchema
  );

export const deleteSkill = (publicId: string) =>
  api.delete<StandardResponse>(`/skills/${publicId}`, {}, StandardResponseSchema);

// Workspace rules: what Altimate Code sessions propose and the workspace's
// owner reviews. Every review, pin or retire names the version the caller last
// saw; a mismatch is a 409 whose `detail.code` says why.
const rulesPath = (datamateId: string) => `/datamates/${datamateId}/rules`;

// Both GETs take no params, which skips the client's schema check: validate explicitly
// so backend drift is reported and recovered, not passed through.
export const getRulesSummary = async (datamateId: string) => {
  const url = `${rulesPath(datamateId)}/summary`;
  return validateResponse<RulesSummary>(
    await api.get(url, {}, 0, RulesSummarySchema),
    url,
    RulesSummarySchema
  );
};

export const updateRuleSettings = (datamateId: string, data: RuleSettingsPayload) =>
  api.update<RulesSummary, RuleSettingsPayload>(
    `${rulesPath(datamateId)}/settings`,
    data,
    RulesSummarySchema
  );

export const getRules = (datamateId: string, params: RuleListParams) =>
  api.get<RuleListResponse>(rulesPath(datamateId), params, 0, RuleListResponseSchema);

export const reviewRule = (datamateId: string, publicId: string, data: RuleReviewPayload) =>
  api.post<RuleDetail, RuleReviewPayload>(
    `${rulesPath(datamateId)}/${publicId}/review`,
    data,
    RuleDetailSchema
  );

export const changeRule = (
  datamateId: string,
  publicId: string,
  action: RuleOwnerAction,
  data: RuleActionPayload
) =>
  api.post<RuleDetail, RuleActionPayload>(
    `${rulesPath(datamateId)}/${publicId}/${action}`,
    data,
    RuleDetailSchema
  );

export const getRuleEvents = async (datamateId: string, publicId: string) => {
  const url = `${rulesPath(datamateId)}/${publicId}/events`;
  return validateResponse<RuleEvent[]>(
    await api.get(url, {}, 0, RuleEventsResponseSchema),
    url,
    RuleEventsResponseSchema
  );
};
